import { Component, type ErrorInfo, type FormEvent, type InputHTMLAttributes, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { allCountries } from "country-region-data";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";

import {
  appApiFetch,
  apiFetch,
  clearSession,
  createAnnouncement,
  createTournament,
  deleteAnnouncement,
  deleteTournament,
  getDashboardCounts,
  getCurrentUser,
  getToken,
  getTournamentDashboard,
  listAnnouncements,
  listNotifications,
  listRegistrations,
  listSubmissions,
  listTournaments,
  login,
  markAnnouncementRead,
  registerAccount,
  reviewRegistration,
  reviewSubmission,
  setSession,
  stripHtml,
  updateAnnouncement,
  updatePassword,
  updateSubmission,
  updateTournament,
  updateProfile,
  uploadMedia,
  withdrawSubmission,
  type AnnouncementItem,
  type AuthUser,
  type CreateTournamentPayload,
  type DashboardCounts,
  type NotificationItem,
  type RegistrationItem,
  type SubmissionItem,
  type Tournament,
  type TournamentDashboardData,
} from "./api";
import {
  canUseCreator as userCanUseCreator,
  initialRoleForUser,
  isAuthenticatedAppPath,
  pathForView as pathForAppView,
  primaryActionForRole,
  roleHintFromPathname,
  tournamentIdFromPathname,
  type Role,
  type View,
  viewFromPathname,
} from "./appState";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

type BannerAnnouncement = {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  readAt: string | null;
};

type RegistrationForm = {
  tournamentId: string;
  name: string;
  email: string;
  phone: string;
  teamName: string;
};

type SubmissionForm = {
  tournamentId: string;
  participantName: string;
  participantEmail: string;
  species: string;
  length: string;
  weight: string;
  photoUrl: string;
  videoUrl: string;
  photoFile: File | null;
  videoFile: File | null;
  mediaCaptureMode: string;
  notes: string;
  lat: string;
  lng: string;
  gpsAccuracy: string;
  gpsCapturedAt: string;
  shareExactPoint: boolean;
};

type SubscriptionTier = NonNullable<AuthUser["subscriptionTier"]>;

type CreateTournamentForm = {
  name: string;
  description: string;
  imageUrl: string;
  startAt: string;
  endAt: string;
  country: string;
  region: string;
  rules: string;
  scoringMethod: string;
  visibility: string;
  participantLimit: string;
  species: string;
  tags: string;
  entryFee: string;
  currency: string;
  paymentInstructions: string;
  paymentRequired: boolean;
  joinApprovalRequired: boolean;
  paymentMethod: string;
  bankCountry: string;
  bankAccountHolder: string;
  bankName: string;
  iban: string;
  swiftBic: string;
  accountNumber: string;
  routingNumber: string;
  sortCode: string;
  branchCode: string;
  bankAddress: string;
  recipientAddress: string;
  otherPaymentDetails: string;
  scoringMetric: "LENGTH" | "WEIGHT" | "LENGTH_AND_WEIGHT";
  allowAnySpecies: boolean;
  timestampRequired: boolean;
  photoRequired: boolean;
  videoRequired: boolean;
  mediaSourceRule: "ALLOW_UPLOAD_OR_CAMERA" | "CAMERA_ONLY";
  gpsRequired: boolean;
};

const emptyRegistration: RegistrationForm = {
  tournamentId: "",
  name: "",
  email: "",
  phone: "",
  teamName: "",
};

const emptySubmission: SubmissionForm = {
  tournamentId: "",
  participantName: "",
  participantEmail: "",
  species: "",
  length: "",
  weight: "",
  photoUrl: "",
  videoUrl: "",
  photoFile: null,
  videoFile: null,
  mediaCaptureMode: "",
  notes: "",
  lat: "",
  lng: "",
  gpsAccuracy: "",
  gpsCapturedAt: "",
  shareExactPoint: true,
};

const emptyCreateTournament: CreateTournamentForm = {
  name: "",
  description: "",
  imageUrl: "",
  startAt: "",
  endAt: "",
  country: "",
  region: "",
  rules: "",
  scoringMethod: "LENGTH",
  visibility: "PUBLIC",
  participantLimit: "5",
  species: "",
  tags: "",
  entryFee: "",
  currency: "EUR",
  paymentRequired: false,
  joinApprovalRequired: false,
  paymentMethod: "BANK_TRANSFER",
  paymentInstructions: "",
  bankCountry: "",
  bankAccountHolder: "",
  bankName: "",
  iban: "",
  swiftBic: "",
  accountNumber: "",
  routingNumber: "",
  sortCode: "",
  branchCode: "",
  bankAddress: "",
  recipientAddress: "",
  otherPaymentDetails: "",
  scoringMetric: "LENGTH",
  allowAnySpecies: true,
  timestampRequired: true,
  photoRequired: true,
  videoRequired: false,
  mediaSourceRule: "ALLOW_UPLOAD_OR_CAMERA",
  gpsRequired: false,
};

const CREATE_TOURNAMENT_DRAFT_KEY = "reelmaster:create-tournament-draft";

function tournamentToCreateForm(tournament: Tournament): CreateTournamentForm {
  return {
    name: tournament.name || "",
    description: stripHtml(tournament.description || ""),
    imageUrl: tournament.imageUrl || "",
    startAt: tournament.startAt || "",
    endAt: tournament.endAt || "",
    country: tournament.country || "",
    region: tournament.region || "",
    rules: tournament.rules || "",
    scoringMethod: tournament.scoringMethod || "LENGTH",
    visibility: tournament.visibility || "PUBLIC",
    participantLimit: tournament.participantLimit ? String(tournament.participantLimit) : "",
    species: tournament.species.join(", "),
    tags: tournament.tags.join(", "),
    entryFee: tournament.entryFeeCents ? String(tournament.entryFeeCents / 100) : "",
    currency: tournament.currency || "EUR",
    paymentRequired: tournament.paymentRequired,
    joinApprovalRequired: Boolean(tournament.joinApprovalRequired || tournament.paymentRequired),
    paymentMethod: tournament.paymentMethod || "BANK_TRANSFER",
    paymentInstructions: tournament.paymentInstructions || "",
    bankCountry: tournament.bankCountry || "",
    bankAccountHolder: tournament.bankAccountHolder || "",
    bankName: tournament.bankName || "",
    iban: tournament.iban || "",
    swiftBic: tournament.swiftBic || "",
    accountNumber: tournament.accountNumber || "",
    routingNumber: tournament.routingNumber || "",
    sortCode: tournament.sortCode || "",
    branchCode: tournament.branchCode || "",
    bankAddress: tournament.bankAddress || "",
    recipientAddress: tournament.recipientAddress || "",
    otherPaymentDetails: tournament.otherPaymentDetails || "",
    scoringMetric: tournament.scoringMetric || "LENGTH",
    allowAnySpecies: tournament.allowAnySpecies,
    timestampRequired: true,
    photoRequired: tournament.photoRequired,
    videoRequired: tournament.videoRequired,
    mediaSourceRule: tournament.mediaSourceRule || "ALLOW_UPLOAD_OR_CAMERA",
    gpsRequired: tournament.gpsRequired,
  };
}

const reelmasterMarker = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const highlightedMarker = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [32, 52],
  iconAnchor: [16, 52],
  popupAnchor: [1, -42],
  shadowSize: [52, 52],
});

function formatDate(value: string) {
  if (!value) return "Schedule not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatDateWithOptions(date, dateFormatOptions());
}

function formatRange(startAt: string, endAt: string) {
  if (!startAt && !endAt) return "Schedule pending";
  if (!endAt) return formatDate(startAt);
  return `${formatDate(startAt)} - ${formatDate(endAt)}`;
}

function tournamentName(tournament: Tournament) {
  return tournament.name.trim() || `Tournament #${tournament.id}`;
}

function tournamentLocation(tournament: Tournament) {
  return [tournament.region, tournament.country].filter(Boolean).join(", ") || "Location pending";
}

type TournamentPhase = "DRAFT" | "UPCOMING" | "LIVE" | "ENDED" | "CLOSED";

function getTournamentStatus(tournament: Tournament): TournamentPhase {
  const explicit = (tournament.status || "").toUpperCase();
  const start = tournament.startAt ? new Date(tournament.startAt).getTime() : Number.NaN;
  const end = tournament.endAt ? new Date(tournament.endAt).getTime() : Number.NaN;
  const now = Date.now();

  if (explicit === "DRAFT") return "DRAFT";
  if (explicit === "CLOSED") return "CLOSED";
  if (explicit === "ENDED" || explicit === "FINISHED" || explicit === "ARCHIVED") return "ENDED";
  if (Number.isFinite(end) && end < now) return "ENDED";
  if (Number.isFinite(start) && start > now) return "UPCOMING";
  return "LIVE";
}

function isTournamentLive(tournament: Tournament) {
  return getTournamentStatus(tournament) === "LIVE";
}

function isTournamentEnded(tournament: Tournament) {
  return getTournamentStatus(tournament) === "ENDED" || getTournamentStatus(tournament) === "CLOSED";
}

function canJoinTournament(tournament: Tournament, registration?: RegistrationItem) {
  const status = getTournamentStatus(tournament);
  if (registration) return false;
  if ((tournament.visibility || "").toUpperCase() !== "PUBLIC") return false;
  if (tournament.participantLimit > 0 && tournament.registrationCount >= tournament.participantLimit) return false;
  return status === "UPCOMING" || status === "LIVE";
}

function statusLabel(tournament: Tournament) {
  const status = getTournamentStatus(tournament);
  if (status === "DRAFT") return "Draft";
  if (status === "UPCOMING") return "Upcoming";
  if (status === "LIVE") return "Live";
  if (status === "CLOSED") return "Closed";
  return "Ended";
}

function joinUnavailableLabel(tournament: Tournament) {
  const status = getTournamentStatus(tournament);
  if (status === "DRAFT") return "Draft";
  if (status === "ENDED") return "Tournament ended";
  if (status === "CLOSED") return "Closed";
  if ((tournament.visibility || "").toUpperCase() !== "PUBLIC") return "Invite only";
  if (tournament.participantLimit > 0 && tournament.registrationCount >= tournament.participantLimit) return "Full";
  return "Not open";
}

function scoringLabel(metric: string) {
  if (metric === "WEIGHT") return "Weight";
  if (metric === "LENGTH_AND_WEIGHT") return "Length and weight";
  return "Length";
}

function projectedScore(metric: string, length: string | number, weight: string | number) {
  const lengthValue = Number(length || 0);
  const weightValue = Number(weight || 0);
  if (metric === "WEIGHT") return weightValue;
  if (metric === "LENGTH_AND_WEIGHT") return (lengthValue * 1000) + weightValue;
  return lengthValue;
}

function scoreUnit(metric: string) {
  if (metric === "WEIGHT") return "kg";
  if (metric === "LENGTH_AND_WEIGHT") return "pts";
  return "cm";
}

function timeLeftLabel(value: string) {
  const target = value ? new Date(value).getTime() : Number.NaN;
  if (!Number.isFinite(target)) return "-";
  const diff = Math.max(0, target - Date.now());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const fallbackLakeImages = [
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1511884642898-4c92249e20b6?auto=format&fit=crop&w=1200&q=80",
];

function safeMediaUrl(value?: string | null) {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("data:image/")) return raw;
  if (raw.startsWith("/")) return raw;
  try {
    const url = new URL(raw, window.location.origin);
    const hasResolvableHost = url.hostname === "localhost" || url.hostname.includes(".");
    if (!hasResolvableHost) return "";
    if (url.protocol === "https:") return url.toString();
    if (url.protocol === "http:" && ["frontend.reelmaster.area.lv", "reelmaster.area.lv"].includes(url.hostname)) {
      url.protocol = "https:";
      return url.toString();
    }
  } catch {
    return "";
  }
  return "";
}

const MAX_PHOTO_SIDE = 1600;
const PHOTO_JPEG_QUALITY = 0.68;
const MAX_RELEASE_VIDEO_SECONDS = 30;
const VIDEO_TARGET_WIDTH = 720;
const VIDEO_TARGET_FPS = 24;
const VIDEO_BITRATE = 700000;

type LoadedImageSource = {
  source: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
};

function replaceFileExtension(name: string, extension: string) {
  const cleanName = name.trim() || `catch-media-${Date.now()}`;
  return cleanName.includes(".") ? cleanName.replace(/\.[^.]+$/, `.${extension}`) : `${cleanName}.${extension}`;
}

async function loadImageSource(file: File): Promise<LoadedImageSource> {
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close(),
      };
    } catch {
      // Some mobile browsers cannot decode HEIC/large camera images through createImageBitmap.
    }
  }

  const url = URL.createObjectURL(file);
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Could not read the selected photo."));
    image.src = url;
  });
  return {
    source: image,
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
    cleanup: () => URL.revokeObjectURL(url),
  };
}

async function preparePhotoFileForUpload(file: File) {
  if (!file.type.startsWith("image/")) return file;
  const loaded = await loadImageSource(file);
  try {
    const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(loaded.width, loaded.height));
    const width = Math.max(1, Math.round(loaded.width * scale));
    const height = Math.max(1, Math.round(loaded.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return file;
    context.drawImage(loaded.source, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", PHOTO_JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], replaceFileExtension(file.name, "jpg"), { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    loaded.cleanup();
  }
}

function readVideoDuration(file: File) {
  return new Promise<number>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const cleanup = () => URL.revokeObjectURL(url);
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      cleanup();
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("Could not read the selected release video."));
    };
    video.src = url;
  });
}

function supportedCompressedVideoType() {
  if (typeof MediaRecorder === "undefined") return "";
  return ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

async function compressVideoFile(file: File) {
  const mimeType = supportedCompressedVideoType();
  const canvas = document.createElement("canvas");
  const captureStream = (canvas as HTMLCanvasElement & { captureStream?: (frameRate?: number) => MediaStream }).captureStream;
  if (!mimeType || !captureStream || typeof MediaRecorder === "undefined") return file;

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  let animationFrame = 0;
  let stream: MediaStream | null = null;
  try {
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Could not prepare the selected release video."));
    });

    const scale = Math.min(1, VIDEO_TARGET_WIDTH / Math.max(video.videoWidth, 1));
    canvas.width = Math.max(2, Math.round((video.videoWidth || VIDEO_TARGET_WIDTH) * scale));
    canvas.height = Math.max(2, Math.round((video.videoHeight || Math.round(VIDEO_TARGET_WIDTH * 0.5625)) * scale));
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return file;

    stream = captureStream.call(canvas, VIDEO_TARGET_FPS);
    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: VIDEO_BITRATE });
    const stopped = new Promise<Blob>((resolve) => {
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || mimeType }));
    });

    const drawFrame = () => {
      if (!video.paused && !video.ended) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        animationFrame = window.requestAnimationFrame(drawFrame);
      }
    };

    recorder.start(250);
    await video.play();
    drawFrame();
    await new Promise<void>((resolve, reject) => {
      video.onended = () => resolve();
      video.onerror = () => reject(new Error("Could not compress the selected release video."));
    });
    if (recorder.state !== "inactive") recorder.stop();
    const blob = await stopped;
    if (!blob.size || blob.size >= file.size) return file;
    return new File([blob], replaceFileExtension(file.name, "webm"), { type: blob.type || mimeType, lastModified: Date.now() });
  } finally {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    stream?.getTracks().forEach((track) => track.stop());
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

async function prepareVideoFileForUpload(file: File) {
  if (!file.type.startsWith("video/")) throw new Error("Upload a valid release video file.");
  const duration = await readVideoDuration(file);
  if (duration > MAX_RELEASE_VIDEO_SECONDS + 0.25) {
    throw new Error("Release videos must be 30 seconds or shorter.");
  }
  try {
    return await compressVideoFile(file);
  } catch {
    return file;
  }
}

function isStandaloneApp() {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || Boolean(navigatorWithStandalone.standalone);
}

function installInstructions() {
  const agent = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(agent)) return "On iPhone or iPad, tap Share, then choose Add to Home Screen.";
  if (/Android/i.test(agent)) return "Open the browser menu and choose Install app or Add to Home screen.";
  return "Use your browser menu to install Reelmaster as an app on this device.";
}

function readLocalStorage(key: string, fallback = "") {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeLocalStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Restricted mobile browsers can block storage; the current in-memory flow should continue.
  }
}

function removeLocalStorage(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures from restricted mobile browsers.
  }
}

function readCookie(name: string) {
  try {
    const prefix = `${encodeURIComponent(name)}=`;
    const found = document.cookie.split("; ").find((item) => item.startsWith(prefix));
    return found ? decodeURIComponent(found.slice(prefix.length)) : "";
  } catch {
    return "";
  }
}

function writeCookie(name: string, value: string) {
  try {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
  } catch {
    // Cookie persistence is best-effort; localStorage still carries the preference.
  }
}

function writeThemePreference(value: string) {
  const nextTheme = normalizeThemePreference(value);
  writeLocalStorage("themePreference", nextTheme);
  writeCookie("themePreference", nextTheme);
  return nextTheme;
}

function rememberedLoginEmail() {
  const value = readLocalStorage("rememberedLoginEmail").trim();
  return isCurrentSiteAddress(value) ? "" : value;
}

function setRememberedLoginEmail(email: string, remember: boolean) {
  if (remember) {
    writeLocalStorage("rememberedLoginEmail", email);
  } else {
    removeLocalStorage("rememberedLoginEmail");
  }
}

function normalizedAnnouncementText(value: string) {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function bannerAnnouncementKeys(item: BannerAnnouncement) {
  return [
    item.id,
    `title-body:${normalizedAnnouncementText(item.title)}:${normalizedAnnouncementText(item.body)}`,
    `title-date:${normalizedAnnouncementText(item.title)}:${item.createdAt}`,
  ];
}

function announcementMatchesBanner(announcement: AnnouncementItem, banner: BannerAnnouncement) {
  if (announcement.id === banner.id) return true;
  return normalizedAnnouncementText(announcement.title) === normalizedAnnouncementText(banner.title)
    && normalizedAnnouncementText(stripHtml(announcement.content)) === normalizedAnnouncementText(banner.body);
}

function normalizedAddressValue(value: string) {
  return value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
}

function isCurrentSiteAddress(value: string) {
  const normalizedValue = normalizedAddressValue(value);
  if (!normalizedValue || typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase().replace(/^www\./, "");
  return normalizedValue === host;
}

function tournamentImage(tournament?: Tournament | null, index = 0) {
  return safeMediaUrl(tournament?.imageUrl) || fallbackLakeImages[index % fallbackLakeImages.length];
}

function mediaRuleLabel(rule: string) {
  return rule === "CAMERA_ONLY" ? "Fresh camera photo required" : "Gallery or camera";
}

function speciesList(value: string) {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

const WORLD_COUNTRY_OPTIONS = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Cote d'Ivoire",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czechia",
  "Democratic Republic of the Congo",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];

function countryOptions(extraCountries: string[] = []) {
  const byName = new Map<string, string>();
  WORLD_COUNTRY_OPTIONS.forEach((country) => byName.set(country.toLowerCase(), country));
  extraCountries.map(normalizeCountryName).filter(Boolean).forEach((country) => {
    const key = country.toLowerCase();
    if (!byName.has(key) && !Array.from(byName.keys()).some((option) => option.startsWith(key))) byName.set(key, country);
  });
  return Array.from(byName.values()).sort((a, b) => a.localeCompare(b));
}

const COUNTRY_ALIASES: Record<string, string> = {
  "united states of america": "United States",
  usa: "United States",
  uk: "United Kingdom",
};

const COUNTRY_REGION_DATA_NAME_ALIASES: Record<string, string> = {
  "Brunei": "Brunei Darussalam",
  "Cabo Verde": "Cape Verde",
  "Congo": "Congo, Republic of the (Brazzaville)",
  "Cote d'Ivoire": "Côte d'Ivoire, Republic of",
  "Czechia": "Czech Republic",
  "Democratic Republic of the Congo": "Congo, the Democratic Republic of the (Kinshasa)",
  "Gambia": "Gambia, The",
  "Iran": "Iran, Islamic Republic of",
  "Micronesia": "Micronesia, Federated States of",
  "North Korea": "Korea, Democratic People's Republic of",
  "North Macedonia": "Macedonia, Republic of",
  "Russia": "Russian Federation",
  "South Korea": "Korea, Republic of",
  "Syria": "Syrian Arab Republic",
  "Tanzania": "Tanzania, United Republic of",
  "Vatican City": "Holy See (Vatican City)",
  "Venezuela": "Venezuela, Bolivarian Republic of",
};

function normalizeCountryName(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const alias = COUNTRY_ALIASES[trimmed.toLowerCase()];
  if (alias) return alias;
  return WORLD_COUNTRY_OPTIONS.find((country) => country.toLowerCase() === trimmed.toLowerCase()) ?? trimmed;
}

function countryRegionDataName(country: string) {
  const normalizedCountry = normalizeCountryName(country);
  const alias = COUNTRY_REGION_DATA_NAME_ALIASES[normalizedCountry] ?? normalizedCountry;
  return allCountries.find(([countryName]) => countryName.toLowerCase() === alias.toLowerCase());
}

const PREFERRED_REGION_OPTIONS_BY_COUNTRY: Record<string, string[]> = {
  "United States": [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ],
  Australia: [
    "Australian Capital Territory",
    "New South Wales",
    "Northern Territory",
    "Queensland",
    "South Australia",
    "Tasmania",
    "Victoria",
    "Western Australia",
  ],
  "United Kingdom": ["England", "Northern Ireland", "Scotland", "Wales"],
};

function regionOptions(country: string, extraRegions: string[] = []) {
  const normalizedCountry = normalizeCountryName(country);
  const byName = new Map<string, string>();
  (PREFERRED_REGION_OPTIONS_BY_COUNTRY[normalizedCountry] ?? []).forEach((region) => byName.set(region.toLowerCase(), region));
  countryRegionDataName(normalizedCountry)?.[2]?.forEach(([region]) => {
    if (region && !byName.has(region.toLowerCase())) byName.set(region.toLowerCase(), region);
  });
  extraRegions.map((region) => region.trim()).filter(Boolean).forEach((region) => {
    const key = region.toLowerCase();
    if (!byName.has(key) && !Array.from(byName.keys()).some((option) => option.startsWith(key))) byName.set(key, region);
  });
  return Array.from(byName.values()).sort((a, b) => a.localeCompare(b));
}

function completeOptionValue(value: string, options: string[]) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const exact = options.find((option) => option.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;
  const startsWith = options.filter((option) => option.toLowerCase().startsWith(trimmed.toLowerCase()));
  return startsWith.length === 1 ? startsWith[0] : value;
}

function optionMatches(value: string, options: string[]) {
  const trimmed = value.trim();
  return Boolean(trimmed && options.some((option) => option.toLowerCase() === trimmed.toLowerCase()));
}

function regionPlaceholder(country: string) {
  const normalizedCountry = normalizeCountryName(country);
  if (normalizedCountry === "Latvia") return "e.g. Adazi";
  if (normalizedCountry === "United States") return "e.g. California";
  if (normalizedCountry === "Canada") return "e.g. Ontario";
  if (normalizedCountry === "Australia") return "e.g. Queensland";
  return "Type or choose region";
}

async function detectCountryByIp() {
  const endpoints = [
    { url: "https://ipapi.co/json/", pick: (data: Record<string, unknown>) => data.country_name },
    { url: "https://ipwho.is/", pick: (data: Record<string, unknown>) => data.country },
  ];
  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint.url, { cache: "no-store" });
      if (!response.ok) continue;
      const data = await response.json() as Record<string, unknown>;
      const country = typeof endpoint.pick(data) === "string" ? normalizeCountryName(endpoint.pick(data) as string) : "";
      if (country && countryOptions().some((option) => option.toLowerCase() === country.toLowerCase())) return country;
    } catch {
      // IP location is a convenience; locale and saved preferences still provide a useful fallback.
    }
  }
  return "";
}

function tournamentBadge(tournament: Tournament) {
  return statusLabel(tournament);
}

function canManageTournament(tournament: Tournament, user: AuthUser | null) {
  if (!user || !userCanUseCreator(user)) return false;
  if (user.role === "ADMIN") return true;
  return Boolean(tournament.creatorId) && tournament.creatorId === user.id;
}

function creatorScopedSubmissions(submissions: SubmissionItem[], managedTournamentIds: Set<string>) {
  return submissions.filter((submission) => managedTournamentIds.has(submission.tournamentId));
}

function creatorScopedRegistrations(registrations: RegistrationItem[], managedTournamentIds: Set<string>) {
  return registrations.filter((registration) => managedTournamentIds.has(registration.tournamentId));
}

const localeCountryNames: Record<string, string> = {
  LV: "Latvia",
  LT: "Lithuania",
  EE: "Estonia",
  FI: "Finland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  DE: "Germany",
  PL: "Poland",
  GB: "United Kingdom",
  US: "United States",
  CA: "Canada",
};

function countryFromLocale() {
  const locale = navigator.languages?.[0] || navigator.language || "";
  const region = locale.split("-")[1]?.toUpperCase();
  if (!region) return "";
  try {
    const displayName = new Intl.DisplayNames([locale || "en"], { type: "region" }).of(region);
    if (displayName) return normalizeCountryName(displayName);
  } catch {
    // Older embedded browsers can lack Intl.DisplayNames.
  }
  return localeCountryNames[region] ?? "";
}

function localePreference() {
  const storedCountry = readLocalStorage("countryPreference");
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (storedCountry === "Latvia" || timeZone === "Europe/Riga") return "lv-LV";
  if (storedCountry === "United States") return "en-US";
  if (storedCountry === "United Kingdom") return "en-GB";
  return navigator.languages?.[0] || navigator.language || undefined;
}

function defaultCountryPreference() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (timeZone === "Europe/Riga") return "Latvia";
  return countryFromLocale() || "Latvia";
}

function defaultMeasurementPreference() {
  return defaultCountryPreference() === "United States" ? "IMPERIAL" : "METRIC";
}

function defaultDateFormatPreference() {
  const country = defaultCountryPreference();
  if (country === "Latvia") return "european";
  return "medium";
}

function dateFormatOptions(): Intl.DateTimeFormatOptions {
  const format = readLocalStorage("dateFormat", defaultDateFormatPreference());
  if (format === "short") return { day: "2-digit", month: "2-digit", year: "2-digit" };
  if (format === "iso") return { year: "numeric", month: "2-digit", day: "2-digit" };
  if (format === "european") return { day: "2-digit", month: "2-digit", year: "numeric" };
  return { dateStyle: "medium" };
}

function dateTimeFormatOptions(): Intl.DateTimeFormatOptions {
  const format = readLocalStorage("dateFormat", defaultDateFormatPreference());
  const timeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if (format === "short") return { day: "2-digit", month: "2-digit", year: "2-digit", ...timeOptions };
  if (format === "iso") return { year: "numeric", month: "2-digit", day: "2-digit", ...timeOptions };
  if (format === "european") return { day: "2-digit", month: "2-digit", year: "numeric", ...timeOptions };
  return { dateStyle: "medium", timeStyle: "short" };
}

function fallbackDateLabel(date: Date, includeTime = false) {
  const pad = (value: number) => String(value).padStart(2, "0");
  const dateLabel = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  return includeTime ? `${dateLabel} ${pad(date.getHours())}:${pad(date.getMinutes())}` : dateLabel;
}

function formatDateWithOptions(date: Date, options: Intl.DateTimeFormatOptions, includeTime = false) {
  try {
    return new Intl.DateTimeFormat(localePreference(), options).format(date);
  } catch {
    return fallbackDateLabel(date, includeTime);
  }
}

function shortDateLabel(date: Date) {
  return formatDateWithOptions(date, { month: "short", day: "numeric" });
}

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function lastSevenDays() {
  const today = startOfLocalDay(new Date());
  return Array.from({ length: 7 }, (_, index) => {
    const start = new Date(today);
    start.setDate(today.getDate() - (6 - index));
    const end = new Date(start);
    end.setDate(start.getDate() + 1);
    return { start, end };
  });
}

function defaultTimeZonePreference() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Riga";
}

function normalizeThemePreference(value: string | null | undefined) {
  const normalized = (value || "").toLowerCase();
  if (normalized === "dark" || normalized === "system" || normalized === "light") return normalized;
  return "system";
}

function storedThemePreference() {
  const savedTheme = readLocalStorage("themePreference") || readCookie("themePreference");
  return savedTheme ? normalizeThemePreference(savedTheme) : "light";
}

function resolvedThemePreference(theme: string) {
  const normalized = normalizeThemePreference(theme);
  if (normalized === "system") {
    return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return normalized === "dark" ? "dark" : "light";
}

function subscriptionTierForUser(user: AuthUser | null): SubscriptionTier {
  const override = user ? readLocalStorage(`subscriptionTierOverride:${user.id}`) as AuthUser["subscriptionTier"] | "" : "";
  const tier = override || user?.subscriptionTier || "FREE";
  return tier === "PRO" || tier === "PRO_ARCHIVE" ? tier : "FREE";
}

const subscriptionPlans: Record<SubscriptionTier, { description: string; name: string; price: string; priceDetail: string }> = {
  FREE: {
    description: "Basic angler tools and small tournaments.",
    name: "Free",
    price: "$0",
    priceDetail: "Free forever",
  },
  PRO: {
    description: "Larger events, review tools, and reports.",
    name: "Pro",
    price: "$9",
    priceDetail: "$9 / month",
  },
  PRO_ARCHIVE: {
    description: "Long-term catch archive for clubs.",
    name: "Pro Archive",
    price: "$19",
    priceDetail: "$19 / month",
  },
};

function confirmSubscriptionSwitch(nextTier: SubscriptionTier, currentTier: AuthUser["subscriptionTier"]) {
  const current = currentTier || "FREE";
  if (nextTier === current) return false;
  const nextPlan = subscriptionPlans[nextTier];
  const currentPlan = subscriptionPlans[current];
  return window.confirm(`Switch subscription from ${currentPlan.name} to ${nextPlan.name}?\n\nPrice: ${nextPlan.priceDetail}\n\nThis updates the plan saved to your Reelmaster account.`);
}

function isProUser(user: AuthUser | null) {
  const tier = subscriptionTierForUser(user);
  return tier === "PRO" || tier === "PRO_ARCHIVE" || user?.role === "ADMIN";
}

function escapeReportText(value: string | number | null | undefined) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function numberValue(value: unknown) {
  const next = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : Number.NaN;
  return Number.isFinite(next) ? next : null;
}

function hasCoordinates(submission: SubmissionItem) {
  return numberValue(submission.lat) !== null && numberValue(submission.lng) !== null;
}

function coordinatePair(submission: SubmissionItem): [number, number] | null {
  const lat = numberValue(submission.lat);
  const lng = numberValue(submission.lng);
  return lat === null || lng === null ? null : [lat, lng];
}

function coordinateLabel(submission: SubmissionItem, precision = 4) {
  const pair = coordinatePair(submission);
  return pair ? `${pair[0].toFixed(precision)}, ${pair[1].toFixed(precision)}` : "GPS missing";
}

function safeItems<T>(items: T[] | null | undefined): T[] {
  return Array.isArray(items) ? items.filter((item) => item && typeof item === "object") : [];
}

type ViewErrorBoundaryProps = {
  children: ReactNode;
  onReset: () => void;
  resetKey: string;
};

type ViewErrorBoundaryState = {
  error: Error | null;
  resetKey: string;
};

class ViewErrorBoundary extends Component<ViewErrorBoundaryProps, ViewErrorBoundaryState> {
  state: ViewErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  static getDerivedStateFromProps(props: ViewErrorBoundaryProps, state: ViewErrorBoundaryState) {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Reelmaster view crashed", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="view-crash-card">
        <EmptyCard title="This section could not load" body="One item returned unexpected data. The app stayed open so you can go back and continue." />
        <p className="view-crash-detail">Detail: {this.state.error.message}</p>
        <button className="submit-link compact" type="button" onClick={this.props.onReset}>Back to dashboard</button>
      </section>
    );
  }
}

function openTournamentPdfReport(tournament: Tournament, dashboard: TournamentDashboardData | null, theme: string) {
  const rankings = dashboard?.rankings ?? [];
  const rows = rankings.length
    ? rankings.map((item) => `<tr><td>${item.rank}</td><td>${escapeReportText(item.participantName)}</td><td>${escapeReportText(item.participantEmail)}</td><td>${item.score} ${scoreUnit(tournament.scoringMetric)}</td><td>${item.catches}</td><td>${item.bestLength || item.bestWeight || "-"}</td></tr>`).join("")
    : `<tr><td colspan="6">No approved catches yet.</td></tr>`;
  const dark = theme !== "light";
  const report = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeReportText(tournamentName(tournament))} results</title><style>
    @page { size: A4; margin: 16mm; }
    body { margin: 0; background: ${dark ? "#06191b" : "#f4fbf8"}; color: ${dark ? "#f7fffc" : "#10242a"}; font-family: Inter, Arial, sans-serif; }
    .report { border: 1px solid ${dark ? "#1e6a72" : "#9bd8d0"}; border-radius: 18px; padding: 28px; background: ${dark ? "#082838" : "#ffffff"}; }
    .brand { color: #22e5d1; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
    h1 { margin: 10px 0 8px; font-size: 34px; }
    .meta { color: ${dark ? "#b7d7df" : "#4c6870"}; display: grid; gap: 5px; margin-bottom: 22px; }
    .cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 22px 0; }
    .card { border: 1px solid ${dark ? "#155a67" : "#b7e6df"}; border-radius: 12px; padding: 12px; background: ${dark ? "#0b3448" : "#ecfbf7"}; }
    .card small { display: block; color: ${dark ? "#8ecbd2" : "#52727b"}; text-transform: uppercase; font-size: 11px; font-weight: 800; }
    .card strong { display: block; margin-top: 6px; font-size: 18px; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; overflow: hidden; border-radius: 12px; }
    th, td { border-bottom: 1px solid ${dark ? "#164b5a" : "#d9eeea"}; padding: 11px 9px; text-align: left; }
    th { background: ${dark ? "#0d4054" : "#dff8f2"}; color: ${dark ? "#9ff7e8" : "#07535a"}; font-size: 12px; text-transform: uppercase; }
    .footer { margin-top: 22px; color: ${dark ? "#8eb9c3" : "#5a747a"}; font-size: 12px; }
  </style></head><body><main class="report">
    <div class="brand">Reelmaster Results</div>
    <h1>${escapeReportText(tournamentName(tournament))}</h1>
    <div class="meta"><span>${escapeReportText(tournamentLocation(tournament))}</span><span>${escapeReportText(formatRange(tournament.startAt, tournament.endAt))}</span></div>
    <section class="cards"><div class="card"><small>Status</small><strong>${escapeReportText(statusLabel(tournament))}</strong></div><div class="card"><small>Scoring</small><strong>${escapeReportText(scoringLabel(tournament.scoringMetric))}</strong></div><div class="card"><small>Participants</small><strong>${dashboard?.participants.length ?? tournament.registrationCount}</strong></div></section>
    <h2>Leaderboard</h2>
    <table><thead><tr><th>Rank</th><th>Participant</th><th>Email</th><th>Score</th><th>Catches</th><th>Best fish</th></tr></thead><tbody>${rows}</tbody></table>
    <p class="footer">Generated ${escapeReportText(formatDateTime(new Date().toISOString()))}. Use your browser print dialog to save this report as PDF.</p>
  </main><script>window.addEventListener("load",()=>setTimeout(()=>window.print(),250));</script></body></html>`;
  const popup = window.open("", "_blank");
  if (!popup) return;
  popup.document.open();
  popup.document.write(report);
  popup.document.close();
}

function createTournamentDefaults(user: AuthUser | null): CreateTournamentForm {
  return {
    ...emptyCreateTournament,
    country: readLocalStorage("countryPreference", defaultCountryPreference()),
    participantLimit: isProUser(user) ? "" : "5",
  };
}

const authOnlyViews: View[] = ["catches", "pending", "announcements", "messages", "submit", "create", "account", "settings", "subscription", "tournamentDashboard"];
const ANNOUNCEMENT_REFRESH_MS = 15000;

function pathForView(view: View) {
  const shouldUseAppPath = Boolean(getCurrentUser()) || isAuthenticatedAppPath(window.location.pathname);
  const savedRole = readLocalStorage("dashboardRole") === "creator" ? "creator" : "participant";
  return pathForAppView(view, { authenticated: shouldUseAppPath, role: savedRole });
}

function updateBrowserPath(view: View, mode: "push" | "replace" = "push") {
  const path = pathForView(view);
  if (window.location.pathname === path) return;
  if (mode === "replace") {
    window.history.replaceState({ view }, "", path);
  } else {
    window.history.pushState({ view }, "", path);
  }
}

export function App() {
  const [activeView, setActiveView] = useState<View>(() => viewFromPathname(window.location.pathname));
  const [role, setRole] = useState<Role>(() => initialRoleForUser(getCurrentUser(), readLocalStorage("dashboardRole")));
  const [user, setUser] = useState<AuthUser | null>(() => getCurrentUser());
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts | null>(null);
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [tag, setTag] = useState("");
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [joinedOnly, setJoinedOnly] = useState(false);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [registrations, setRegistrations] = useState<RegistrationItem[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registration, setRegistration] = useState<RegistrationForm>(emptyRegistration);
  const [submission, setSubmission] = useState<SubmissionForm>(emptySubmission);
  const [createForm, setCreateForm] = useState<CreateTournamentForm>(emptyCreateTournament);
  const [editingTournamentId, setEditingTournamentId] = useState("");
  const [selectedTournamentId, setSelectedTournamentId] = useState(() => tournamentIdFromPathname(window.location.pathname));
  const [selectedTournamentTab, setSelectedTournamentTab] = useState<"overview" | "leaderboard" | "announcements" | "questions">("overview");
  const [themePreference, setThemePreference] = useState(() => storedThemePreference());
  const [systemThemeRevision, setSystemThemeRevision] = useState(0);
  const [accountOpen, setAccountOpen] = useState(false);
  const [formNotice, setFormNotice] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const countryFilterMode = useRef<"idle" | "auto" | "manual" | "cleared">("idle");

  const joinedTournamentIds = useMemo(
    () => new Set(registrations.filter((item) => item.status === "APPROVED").map((item) => item.tournamentId)),
    [registrations],
  );
  const managedTournaments = useMemo(
    () => tournaments.filter((tournament) => canManageTournament(tournament, user)),
    [tournaments, user],
  );
  const managedTournamentIds = useMemo(
    () => new Set(managedTournaments.map((tournament) => tournament.id)),
    [managedTournaments],
  );
  const managedSubmissions = useMemo(
    () => creatorScopedSubmissions(submissions, managedTournamentIds),
    [managedTournamentIds, submissions],
  );
  const managedRegistrations = useMemo(
    () => creatorScopedRegistrations(registrations, managedTournamentIds),
    [managedTournamentIds, registrations],
  );
  const roleVisibleTournaments = useMemo(() => {
    const publishedTournaments = tournaments.filter((tournament) => getTournamentStatus(tournament) !== "DRAFT");
    if (role !== "creator") return publishedTournaments;
    const visibleById = new Map(publishedTournaments.map((tournament) => [tournament.id, tournament]));
    managedTournaments.forEach((tournament) => visibleById.set(tournament.id, tournament));
    return Array.from(visibleById.values());
  }, [managedTournaments, role, tournaments]);
  const filteredTournaments = useMemo(() => {
    const queryText = query.trim().toLowerCase();
    const fromTime = startFrom ? new Date(`${startFrom}T00:00:00`).getTime() : Number.NaN;
    const toTime = startTo ? new Date(`${startTo}T23:59:59`).getTime() : Number.NaN;
    return roleVisibleTournaments.filter((tournament) => {
      const searchable = [tournament.name, stripHtml(tournament.description), tournament.country, tournament.region, tournament.tags.join(" "), tournament.species.join(" ")]
        .join(" ")
        .toLowerCase();
      const startTime = tournament.startAt ? new Date(tournament.startAt).getTime() : Number.NaN;
      if (queryText && !searchable.includes(queryText)) return false;
      if (country.trim() && !tournament.country.toLowerCase().includes(country.trim().toLowerCase())) return false;
      if (region.trim() && !tournament.region.toLowerCase().includes(region.trim().toLowerCase())) return false;
      if (tag.trim() && !tournament.tags.join(" ").toLowerCase().includes(tag.trim().toLowerCase())) return false;
      if (Number.isFinite(fromTime) && Number.isFinite(startTime) && startTime < fromTime) return false;
      if (Number.isFinite(toTime) && Number.isFinite(startTime) && startTime > toTime) return false;
      return true;
    });
  }, [country, query, region, roleVisibleTournaments, startFrom, startTo, tag]);
  const approvedTournaments = useMemo(
    () => roleVisibleTournaments.filter((tournament) => joinedTournamentIds.has(tournament.id) && isTournamentLive(tournament)),
    [joinedTournamentIds, roleVisibleTournaments],
  );
  const visibleTournaments = useMemo(
    () => {
      if (!joinedOnly) return filteredTournaments;
      return filteredTournaments.filter((tournament) => (
        role === "creator" ? managedTournamentIds.has(tournament.id) : joinedTournamentIds.has(tournament.id)
      ));
    },
    [filteredTournaments, joinedOnly, joinedTournamentIds, managedTournamentIds, role],
  );
  const defaultTournamentId = roleVisibleTournaments[0]?.id ?? "";
  const defaultSubmissionTournamentId = approvedTournaments[0]?.id ?? "";
  const canUseCreator = userCanUseCreator(user);
  const currentPlan = subscriptionTierForUser(user);
  const currentPlanIsPro = isProUser(user);
  const appTheme = useMemo(() => resolvedThemePreference(themePreference), [themePreference, systemThemeRevision]);
  const primaryAction = primaryActionForRole(role, canUseCreator);
  const bannerAnnouncements = useMemo(() => {
    const byId = new Map<string, BannerAnnouncement>();
    notifications
      .filter((item) => item.kind === "ANNOUNCEMENT")
      .forEach((item) => {
        byId.set(item.id, {
          id: item.id,
          title: item.title,
          body: item.body || "",
          createdAt: item.createdAt,
          readAt: item.readAt,
        });
      });
    announcements.forEach((item) => {
      const existing = byId.get(item.id);
      byId.set(item.id, {
        id: item.id,
        title: item.title,
        body: existing?.body || stripHtml(item.content),
        createdAt: item.createdAt,
        readAt: item.readAt,
      });
    });
    return Array.from(byId.values()).sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt));
  }, [announcements, notifications]);
  const latestAnnouncement = bannerAnnouncements.find((item) => !item.readAt && !bannerAnnouncementKeys(item).some((key) => dismissedAnnouncementIds.has(key))) ?? null;
  const selectedTournament = tournaments.find((tournament) => tournament.id === selectedTournamentId) ?? null;

  function dismissAnnouncementBanner(item: BannerAnnouncement) {
    const dismissedAt = new Date().toISOString();
    const keys = bannerAnnouncementKeys(item);
    setDismissedAnnouncementIds((current) => new Set([...current, ...keys]));
    setAnnouncements((current) => current.map((announcement) => (
      announcementMatchesBanner(announcement, item) ? { ...announcement, readAt: announcement.readAt || dismissedAt } : announcement
    )));
    setNotifications((current) => current.map((notification) => (
      notification.id === item.id || keys.includes(`title-body:${normalizedAnnouncementText(notification.title)}:${normalizedAnnouncementText(notification.body || "")}`)
        ? { ...notification, readAt: notification.readAt || dismissedAt }
        : notification
    )));
    const announcementToMark = announcements.find((announcement) => announcementMatchesBanner(announcement, item));
    void markAnnouncementRead(announcementToMark?.id || item.id).catch(() => {
      // Some announcement banners originate from notification IDs; the local dismiss already handled those.
    });
  }

  useEffect(() => {
    const storedUser = getCurrentUser();
    setUser(storedUser);
    setRole(initialRoleForUser(storedUser, readLocalStorage("dashboardRole")));
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setActiveView(viewFromPathname(window.location.pathname));
      const tournamentId = tournamentIdFromPathname(window.location.pathname);
      if (tournamentId) setSelectedTournamentId(tournamentId);
      setAccountOpen(false);
      setFormNotice(null);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = appTheme;
  }, [appTheme]);

  useEffect(() => {
    writeThemePreference(themePreference);
  }, [themePreference]);

  useEffect(() => {
    const themeQuery = window.matchMedia?.("(prefers-color-scheme: light)");
    if (!themeQuery) return undefined;
    const handleThemeChange = () => setSystemThemeRevision((revision) => revision + 1);
    themeQuery.addEventListener("change", handleThemeChange);
    return () => themeQuery.removeEventListener("change", handleThemeChange);
  }, []);

  useEffect(() => {
    const standaloneQuery = window.matchMedia("(display-mode: standalone)");
    const updateStandalone = () => setIsStandalone(isStandaloneApp());
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
      setFormNotice({ type: "success", text: "Reelmaster is installed on this device." });
    };

    updateStandalone();
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    standaloneQuery.addEventListener("change", updateStandalone);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      standaloneQuery.removeEventListener("change", updateStandalone);
    };
  }, []);

  useEffect(() => {
    if (!canUseCreator && role === "creator") {
      setRole("participant");
      setActiveView("tournaments");
      updateBrowserPath("tournaments", "replace");
    }
  }, [canUseCreator, role]);

  useEffect(() => {
    if (!user && (authOnlyViews.includes(activeView) || isAuthenticatedAppPath(window.location.pathname))) {
      setActiveView("login");
      updateBrowserPath("login", "replace");
      setFormNotice({ type: "error", text: "Please log in to continue." });
      return;
    }
    if ((activeView === "create" || activeView === "pending") && !canUseCreator) {
      setActiveView("dashboard");
      updateBrowserPath("dashboard", "replace");
      setFormNotice({ type: "error", text: "Please log in to use creator tools." });
    }
  }, [activeView, canUseCreator, user]);

  useEffect(() => {
    const hintedRole = roleHintFromPathname(window.location.pathname);
    if (hintedRole === "creator" && canUseCreator && role !== "creator") {
      setRole("creator");
      writeLocalStorage("dashboardRole", "creator");
    }
  }, [activeView, canUseCreator, role]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setAnnouncements([]);
      setDashboardCounts(null);
      setSubmissions([]);
      setDismissedAnnouncementIds(new Set());
      return;
    }
    let cancelled = false;
    async function loadNotificationsAndCounts() {
      try {
        const [notificationData, announcementData, counts] = await Promise.all([
          listNotifications(),
          listAnnouncements(),
          getDashboardCounts(),
        ]);
        if (!cancelled) {
          setNotifications(notificationData.items ?? []);
          setAnnouncements(announcementData.items ?? []);
          setDashboardCounts(counts);
        }
      } catch {
        if (!cancelled) {
          setNotifications([]);
          setAnnouncements([]);
          setDashboardCounts(null);
        }
      }
    }
    void loadNotificationsAndCounts();
    const timer = window.setInterval(loadNotificationsAndCounts, ANNOUNCEMENT_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSubmissions([]);
      setRegistrations([]);
      return;
    }
    let cancelled = false;
    async function loadUserSubmissions() {
      setSubmissionsLoading(true);
      try {
        const [submissionData, registrationData] = await Promise.all([listSubmissions(), listRegistrations()]);
        if (!cancelled) {
          setSubmissions(submissionData.items ?? []);
          setRegistrations(registrationData.items ?? []);
        }
      } catch {
        if (!cancelled) {
          setSubmissions([]);
          setRegistrations([]);
        }
      } finally {
        if (!cancelled) setSubmissionsLoading(false);
      }
    }

    void loadUserSubmissions();
    const timer = window.setInterval(loadUserSubmissions, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user]);

  useEffect(() => {
    if (!defaultTournamentId) return;
    setRegistration((current) => (current.tournamentId ? current : { ...current, tournamentId: defaultTournamentId }));
  }, [defaultTournamentId]);

  useEffect(() => {
    setSubmission((current) => {
      if (!defaultSubmissionTournamentId) return current.tournamentId ? { ...current, tournamentId: "" } : current;
      if (approvedTournaments.some((tournament) => tournament.id === current.tournamentId)) return current;
      return { ...current, tournamentId: defaultSubmissionTournamentId };
    });
  }, [approvedTournaments, defaultSubmissionTournamentId]);

  useEffect(() => {
    if (role !== "participant" || country.trim() || countryFilterMode.current !== "idle") return;
    let cancelled = false;
    async function applyCountryFilter() {
      const fallbackCountry = normalizeCountryName(readLocalStorage("countryPreference") || defaultCountryPreference());
      if (fallbackCountry && !cancelled && countryFilterMode.current === "idle") {
        countryFilterMode.current = "auto";
        setCountry(fallbackCountry);
      }
      const ipCountry = await detectCountryByIp();
      if (ipCountry && !cancelled && (countryFilterMode.current === "idle" || countryFilterMode.current === "auto")) {
        countryFilterMode.current = "auto";
        setCountry(ipCountry);
        writeLocalStorage("countryPreference", ipCountry);
      }
    }
    void applyCountryFilter();
    return () => {
      cancelled = true;
    };
  }, [country, role]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const items = await listTournaments({});
        if (!cancelled) setTournaments(items);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    const timer = window.setTimeout(() => void load(), 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  function clearFilters() {
    countryFilterMode.current = "cleared";
    setQuery("");
    setCountry("");
    setRegion("");
    setTag("");
    setStartFrom("");
    setStartTo("");
    setJoinedOnly(false);
  }

  function updateCountryFilter(value: string) {
    countryFilterMode.current = value.trim() ? "manual" : "cleared";
    setCountry(value);
    setRegion("");
    setTag("");
  }

  function updateRegionFilter(value: string) {
    setRegion(value);
    setTag("");
  }

  function openView(view: View) {
    if (!user && authOnlyViews.includes(view)) {
      setActiveView("login");
      updateBrowserPath("login", "replace");
      setAccountOpen(false);
      setFormNotice({ type: "error", text: "Please log in to continue." });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if ((view === "create" || view === "pending") && !canUseCreator) {
      setActiveView("dashboard");
      updateBrowserPath("dashboard", "replace");
      setAccountOpen(false);
      setFormNotice({ type: "error", text: "Please log in to use creator tools." });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (view === "create") {
      setEditingTournamentId("");
      setCreateForm(createTournamentDefaults(user));
    }
    setActiveView(view);
    updateBrowserPath(view);
    setAccountOpen(false);
    setFormNotice(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openTournamentDashboard(tournamentId: string, tab: "overview" | "leaderboard" | "announcements" | "questions" = "overview") {
    if (!user) {
      setActiveView("login");
      updateBrowserPath("login", "replace");
      setAccountOpen(false);
      setFormNotice({ type: "error", text: "Please log in to continue." });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSelectedTournamentId(tournamentId);
    setSelectedTournamentTab(tab);
    setActiveView("tournamentDashboard");
    window.history.pushState(
      { view: "tournamentDashboard", tournamentId },
      "",
      `${role === "creator" ? "/app/manage" : "/app/tournaments"}/${encodeURIComponent(tournamentId)}`,
    );
    setAccountOpen(false);
    setFormNotice(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function switchRole(nextRole: Role) {
    if (nextRole === "creator" && !canUseCreator) {
      setActiveView("login");
      setFormNotice({ type: "error", text: "Please log in to unlock creator tools." });
      return;
    }
    setRole(nextRole);
    writeLocalStorage("dashboardRole", nextRole);
    const nextView = nextRole === "creator" ? "dashboard" : "tournaments";
    setActiveView(nextView);
    updateBrowserPath(nextView);
    setFormNotice(null);
  }

  function handleLogin(session: { token: string; user: AuthUser }) {
    setSession(session);
    setUser(session.user);
    const nextRole = userCanUseCreator(session.user) ? (readLocalStorage("dashboardRole") === "creator" ? "creator" : "participant") : "participant";
    setRole(nextRole);
    writeLocalStorage("dashboardRole", nextRole);
    setActiveView("dashboard");
    updateBrowserPath("dashboard", "replace");
    setFormNotice({ type: "success", text: "Logged in successfully." });
  }

  function handleLogout() {
    clearSession();
    setUser(null);
    setNotifications([]);
    setRole("participant");
    setActiveView("dashboard");
    setFormNotice({ type: "success", text: "Logged out." });
  }

  async function reloadTournaments() {
    const items = await listTournaments({});
    setTournaments(items);
    return items;
  }

  async function reloadAccountData() {
    if (!user) return;
    const [submissionData, registrationData] = await Promise.all([listSubmissions(), listRegistrations()]);
    setSubmissions(submissionData.items ?? []);
    setRegistrations(registrationData.items ?? []);
  }

  async function reloadAnnouncementsAndCounts() {
    if (!user) return;
    const [notificationData, announcementData, counts] = await Promise.all([
      listNotifications(),
      listAnnouncements(),
      getDashboardCounts(),
    ]);
    setNotifications(notificationData.items ?? []);
    setAnnouncements(announcementData.items ?? []);
    setDashboardCounts(counts);
  }

  async function joinTournament(tournament: Tournament) {
    if (!user) {
      setActiveView("login");
      setFormNotice({ type: "error", text: "Please log in to join a tournament." });
      return;
    }
    if (!canJoinTournament(tournament, registrations.find((item) => item.tournamentId === tournament.id))) {
      setFormNotice({ type: "error", text: "This tournament is not open for new joins." });
      return;
    }
    setFormNotice(null);
    try {
      await appApiFetch("/registrations", {
        method: "POST",
        body: JSON.stringify({
          tournamentId: Number(tournament.id),
          name: user.displayName || user.email,
          email: user.email,
        }),
      });
      await reloadAccountData();
      setFormNotice({
        type: "success",
        text: tournament.paymentRequired
          ? "Join request submitted. Pay using the tournament bank details, then wait for organizer approval."
          : tournament.joinApprovalRequired
            ? "Join request submitted. You can submit catches after the organizer approves you and the event is live."
          : "Tournament joined. You can submit catches when the event is live.",
      });
    } catch (e) {
      setFormNotice({ type: "error", text: (e as Error).message });
    }
  }

  async function submitRegistration(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormNotice(null);
    try {
      await (user ? appApiFetch : apiFetch)("/registrations", {
        method: "POST",
        body: JSON.stringify({
          tournamentId: Number(registration.tournamentId || defaultTournamentId),
          name: registration.name || user?.displayName || user?.email,
          email: registration.email || user?.email,
          phone: registration.phone,
          teamName: registration.teamName,
        }),
      });
      setFormNotice({ type: "success", text: "Registration saved in WordPress for organizer review." });
      setRegistration({ ...emptyRegistration, tournamentId: registration.tournamentId || defaultTournamentId });
      await reloadAccountData();
    } catch (e) {
      setFormNotice({ type: "error", text: (e as Error).message });
    }
  }

  async function submitCatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) {
      openView("login");
      return;
    }
    setFormNotice(null);
    setSubmitting(true);
    try {
      const targetTournamentId = submission.tournamentId || defaultSubmissionTournamentId;
      const selected = tournaments.find((item) => item.id === targetTournamentId);
      if (!targetTournamentId || !selected) {
        throw new Error("You do not have an approved live tournament available for catch submissions.");
      }
      if (!isTournamentLive(selected)) {
        throw new Error("This tournament has not started yet. Catches can be submitted only while the event is live.");
      }
      const participantName = user.displayName || user.email.split("@")[0] || user.email;
      let photoUrl = "";
      let videoUrl = "";
      if (submission.photoFile) {
        const preparedPhoto = await preparePhotoFileForUpload(submission.photoFile);
        const uploaded = await uploadMedia(preparedPhoto, submission.mediaCaptureMode || "gallery");
        photoUrl = safeMediaUrl(uploaded.url);
        if (!photoUrl) throw new Error("Uploaded photo URL could not be verified.");
      }
      if (submission.videoFile) {
        const preparedVideo = await prepareVideoFileForUpload(submission.videoFile);
        const uploaded = await uploadMedia(preparedVideo, "video");
        videoUrl = safeMediaUrl(uploaded.url);
        if (!videoUrl) throw new Error("Uploaded video URL could not be verified.");
      }
      await appApiFetch("/submissions", {
        method: "POST",
        body: JSON.stringify({
          tournamentId: Number(targetTournamentId),
          participantName,
          participantEmail: user.email,
          species: submission.species,
          length: selected?.scoringMetric === "WEIGHT" ? 0 : Number(submission.length || 0),
          weight: Number(submission.weight || 0),
          measurementSystem: "METRIC",
          photoUrl,
          videoUrl,
          notes: submission.notes,
          lat: submission.shareExactPoint && submission.lat ? Number(submission.lat) : undefined,
          lng: submission.shareExactPoint && submission.lng ? Number(submission.lng) : undefined,
          gpsAccuracy: submission.shareExactPoint && submission.gpsAccuracy ? Number(submission.gpsAccuracy) : undefined,
          gpsCapturedAt: submission.shareExactPoint ? submission.gpsCapturedAt : "",
          mediaCaptureMode: submission.mediaCaptureMode,
        }),
      });
      setFormNotice({ type: "success", text: "Catch submitted to WordPress for review." });
      setSubmission({ ...emptySubmission, tournamentId: targetTournamentId });
      const data = await listSubmissions();
      setSubmissions(data.items ?? []);
      setActiveView("catches");
    } catch (e) {
      setFormNotice({ type: "error", text: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }

  async function submitTournament(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) {
      openView("login");
      return;
    }
    if (!canUseCreator) {
      setFormNotice({ type: "error", text: "Please log in to create tournaments." });
      return;
    }
    const countryIsValid = optionMatches(createForm.country, countryOptions());
    const regionIsValid = !createForm.region.trim() || optionMatches(createForm.region, regionOptions(createForm.country));
    if (!createForm.name.trim() || !countryIsValid || !regionIsValid) {
      setFormNotice({ type: "error", text: "Tournament name and a valid country are required. Region is optional, but must be selected from the list when used." });
      return;
    }
    const participantLimit = Number(createForm.participantLimit || 0);
    if (!currentPlanIsPro && (participantLimit < 1 || participantLimit > 5)) {
      setFormNotice({ type: "error", text: "Free plan tournaments can include up to 5 participants. Set max participants from 1 to 5 or upgrade to Pro." });
      return;
    }
    setFormNotice(null);
    setSubmitting(true);
    try {
      const payload: CreateTournamentPayload = {
        name: createForm.name,
        description: createForm.description,
        imageUrl: createForm.imageUrl,
        startAt: createForm.startAt,
        endAt: createForm.endAt,
        country: createForm.country,
        region: createForm.region,
        visibility: createForm.visibility,
        status: "UPCOMING",
        scoringMethod: createForm.scoringMethod,
        rules: createForm.rules,
        participantLimit,
        species: createForm.species,
        tags: createForm.tags,
        entryFeeCents: createForm.paymentRequired ? Math.round(Number(createForm.entryFee || 0) * 100) : 0,
        currency: createForm.currency,
        paymentRequired: createForm.paymentRequired,
        joinApprovalRequired: createForm.paymentRequired || createForm.joinApprovalRequired,
        paymentMethod: createForm.paymentRequired ? createForm.paymentMethod : "NONE",
        paymentInstructions: createForm.paymentInstructions,
        bankCountry: createForm.bankCountry,
        bankAccountHolder: createForm.bankAccountHolder,
        bankName: createForm.bankName,
        iban: createForm.iban,
        swiftBic: createForm.swiftBic,
        accountNumber: createForm.accountNumber,
        routingNumber: createForm.routingNumber,
        sortCode: createForm.sortCode,
        branchCode: createForm.branchCode,
        bankAddress: createForm.bankAddress,
        recipientAddress: createForm.recipientAddress,
        otherPaymentDetails: createForm.otherPaymentDetails,
        scoringMetric: createForm.scoringMetric,
        allowAnySpecies: createForm.allowAnySpecies,
        photoRequired: createForm.photoRequired,
        videoRequired: createForm.videoRequired,
        mediaSourceRule: createForm.mediaSourceRule,
        gpsRequired: createForm.gpsRequired,
      };
      const result = editingTournamentId
        ? await updateTournament(editingTournamentId, payload)
        : await createTournament(payload);
      setCreateForm(emptyCreateTournament);
      setEditingTournamentId("");
      removeLocalStorage(CREATE_TOURNAMENT_DRAFT_KEY);
      await reloadTournaments();
      setSelectedTournamentId(result.tournament.id);
      setActiveView("tournaments");
      setFormNotice({ type: "success", text: editingTournamentId ? "Tournament updated successfully." : "Tournament created successfully." });
    } catch (e) {
      setFormNotice({ type: "error", text: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }

  function editTournament(tournament: Tournament) {
    setCreateForm(tournamentToCreateForm(tournament));
    setEditingTournamentId(tournament.id);
    setActiveView("create");
    setFormNotice(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeTournament(tournament: Tournament) {
    if (!window.confirm(`Delete "${tournamentName(tournament)}"? This cannot be undone.`)) return;
    setSubmitting(true);
    try {
      await deleteTournament(tournament.id);
      await reloadTournaments();
      setSelectedTournamentId("");
      setActiveView("tournaments");
      setFormNotice({ type: "success", text: "Tournament deleted." });
    } catch (e) {
      setFormNotice({ type: "error", text: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  }

  async function installApp() {
    if (isStandalone) {
      setFormNotice({ type: "success", text: "Reelmaster is already installed on this device." });
      return;
    }
    if (!installPrompt) {
      setFormNotice({ type: "success", text: installInstructions() });
      return;
    }

    const promptEvent = installPrompt;
    setInstallPrompt(null);
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setFormNotice({
      type: "success",
      text: choice.outcome === "accepted" ? "Reelmaster is being added to this device." : installInstructions(),
    });
  }

  const accountName = user?.displayName || user?.email || "Account";
  const accountInitial = accountName.trim().slice(0, 1).toUpperCase() || "A";
  const unreadNotifications = notifications.filter((notification) => !notification.readAt).length;

  return (
    <div className={`app-shell theme-${appTheme} ${user ? "has-mobile-tabs" : ""}`}>
      <header className={user ? "topbar app-topbar" : "topbar public-topbar"}>
        <button className="brand brand-button" onClick={() => openView(user ? "tournaments" : "dashboard")} type="button">
          <span className="brand-mark" aria-hidden="true">
            <FishIcon />
          </span>
          <span>
            <strong>Reelmaster</strong>
            <small>Fishing tournament platform</small>
          </span>
        </button>

        {user ? (
          <nav className="primary-nav" aria-label="Primary">
            {canUseCreator ? (
              <div className="role-toggle" aria-label="Dashboard role">
                <button className={role === "participant" ? "selected" : ""} onClick={() => switchRole("participant")} type="button">
                  Angler
                </button>
                <button className={role === "creator" ? "selected" : ""} onClick={() => switchRole("creator")} type="button">
                  Organizer
                </button>
              </div>
            ) : null}
            <NavButton active={activeView === "dashboard"} icon={<HomeIcon />} label="Dashboard" onClick={() => openView("dashboard")} />
            {role === "participant" ? (
              <>
                <NavButton active={activeView === "tournaments"} icon={<TrophyIcon />} label="Tournaments" onClick={() => openView("tournaments")} />
                <NavButton active={activeView === "submit"} icon={<UploadIcon />} label="Submit Catch" onClick={() => openView("submit")} />
                <NavButton active={activeView === "catches"} icon={<FishIcon />} label="My Catches" onClick={() => openView("catches")} />
              </>
            ) : null}
            {role === "creator" ? (
              <>
                <NavButton active={activeView === "tournaments"} icon={<ClipboardIcon />} label="Manage" onClick={() => openView("tournaments")} />
                <NavButton active={activeView === "create"} icon={<PlusIcon />} label="Create" onClick={() => openView("create")} />
                <NavButton active={activeView === "pending"} icon={<FishIcon />} label="Review" onClick={() => openView("pending")} />
              </>
            ) : null}
          </nav>
        ) : null}

        {!user && activeView === "dashboard" ? (
          <nav className="landing-anchor-nav" aria-label="Landing page sections">
            <a href="#overview">Overview</a>
            <a href="#features">Features</a>
            <a href="#tournaments">Tournaments</a>
            <a href="#rules">Rules</a>
            <a href="#how">How it Works</a>
            <a href="#faq">FAQ</a>
          </nav>
        ) : null}

        <div className="account-actions topbar-actions">
          {user ? (
            <button className="quiet-link header-icon-button" aria-label="Notifications" onClick={() => openView("announcements")} type="button">
              <BellIcon />
              {unreadNotifications ? <span className="header-action-dot">{unreadNotifications}</span> : null}
            </button>
          ) : null}
          {!isStandalone ? (
            <button className="quiet-link install-app-button" onClick={installApp} type="button">
              <SmartphoneIcon /> Install app
            </button>
          ) : null}
          {!user && activeView === "dashboard" ? (
            <button
              aria-label="Toggle theme"
              className="quiet-link public-theme-toggle"
              onClick={() => setThemePreference(appTheme === "dark" ? "light" : "dark")}
              type="button"
            >
              <PaletteIcon />
            </button>
          ) : null}
          {user ? (
            <button
              aria-label="Toggle theme"
              className="quiet-link public-theme-toggle"
              onClick={() => setThemePreference(appTheme === "dark" ? "light" : "dark")}
              type="button"
            >
              <PaletteIcon />
            </button>
          ) : null}
          {user ? (
            <div className="account-menu">
              <button className="quiet-link account-trigger" onClick={() => setAccountOpen((open) => !open)} type="button">
                <span className="account-trigger-icon" aria-hidden="true">{accountInitial}</span>
                <span className="account-trigger-label">{accountName}</span>
              </button>
              {accountOpen ? (
                <div className="account-dropdown">
                  <div className="account-dropdown-profile">
                    <strong>{accountName}</strong>
                    <small>{user.email}</small>
                    <span>{subscriptionTierForUser(user).replace("_", " ")} plan</span>
                  </div>
                  <button onClick={() => openView("account")} type="button"><UserIcon /> Profile / Account</button>
                  <button onClick={() => openView("settings")} type="button"><GearIcon /> Settings</button>
                  <button onClick={() => openView("subscription")} type="button"><ShieldIcon /> Subscription</button>
                  <button onClick={handleLogout} type="button">Log out</button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <button className="quiet-link public-login-button" onClick={() => openView("login")} type="button">
                Log in
              </button>
              <button className="signup-link lovable-open-app" onClick={() => openView("signup")} type="button">
                Get started
              </button>
            </>
          )}
        </div>
      </header>

      <main className={`page-shell ${user ? "" : "public-page-shell"} ${latestAnnouncement ? "has-announcement-banner" : ""}`}>
        {latestAnnouncement ? (
          <section className="announcement-banner">
            <div>
              <strong>{latestAnnouncement.title}</strong>
              {latestAnnouncement.body ? <p>{latestAnnouncement.body}</p> : null}
            </div>
            <button type="button" onClick={() => dismissAnnouncementBanner(latestAnnouncement)}>Dismiss</button>
          </section>
        ) : null}
        {error ? <div className="notice error">{error}</div> : null}
        {formNotice ? <div className={`notice ${formNotice.type}`}>{formNotice.text}</div> : null}

        <ViewErrorBoundary resetKey={`${activeView}:${role}:${submissions.length}:${registrations.length}`} onReset={() => openView("dashboard")}>
        {activeView === "dashboard" ? (
          <DashboardView
            announcements={announcements}
            counts={dashboardCounts}
            managedRegistrations={managedRegistrations}
            managedSubmissions={managedSubmissions}
            managedTournaments={managedTournaments}
            onNavigate={openView}
            onOpenLeaderboard={(tournamentId) => openTournamentDashboard(tournamentId, "leaderboard")}
            registrations={registrations}
            role={role}
            submissions={submissions}
            tournaments={tournaments}
            user={user}
          />
        ) : null}
        {activeView === "tournaments" ? (
          <TournamentsView
            clearFilters={clearFilters}
            country={country}
            joinedOnly={joinedOnly}
            loading={loading}
            query={query}
            region={region}
            setCountry={updateCountryFilter}
            setJoinedOnly={setJoinedOnly}
            setQuery={setQuery}
            setRegion={updateRegionFilter}
            setStartFrom={setStartFrom}
            setStartTo={setStartTo}
            setTag={setTag}
            startFrom={startFrom}
            startTo={startTo}
            tag={tag}
            filterTournaments={roleVisibleTournaments}
            tournaments={visibleTournaments}
            registrations={registrations}
            role={role}
            user={user}
            onJoin={joinTournament}
            onSubmitCatch={() => openView("submit")}
            onDashboard={(tournamentId) => openTournamentDashboard(tournamentId)}
          />
        ) : null}
        {activeView === "catches" ? (
          <MyCatchesView
            loading={submissionsLoading}
            onReload={reloadAccountData}
            submissions={submissions}
          />
        ) : null}
        {activeView === "pending" ? (
          <PendingSubmissionsView
            onCatchReviewed={(reviewedSubmission) => {
              setSubmissions((current) => current.map((submission) => (submission.id === reviewedSubmission.id ? reviewedSubmission : submission)));
            }}
            onJoinReviewed={(reviewedRegistration) => {
              setRegistrations((current) => current.map((registration) => (registration.id === reviewedRegistration.id ? reviewedRegistration : registration)));
            }}
            onReload={reloadAccountData}
            registrations={role === "creator" ? managedRegistrations : registrations}
            submissions={role === "creator" ? managedSubmissions : submissions}
            tournaments={role === "creator" ? managedTournaments : tournaments}
          />
        ) : null}
        {activeView === "announcements" ? (
          <AnnouncementsView
            announcements={announcements}
            notifications={notifications}
            onReload={reloadAnnouncementsAndCounts}
            role={role}
            tournaments={tournaments}
          />
        ) : null}
        {activeView === "messages" ? <PlaceholderView title="Messages" body="Your tournament messages will appear here once message threads are enabled." /> : null}
        {activeView === "submit" ? (
          <SubmitCatchView busy={submitting} form={submission} onChange={setSubmission} onSubmit={submitCatch} tournaments={approvedTournaments} user={user} onSettings={() => openView("settings")} />
        ) : null}
        {activeView === "create" ? <CreateTournamentView busy={submitting} editing={Boolean(editingTournamentId)} form={createForm} isPro={currentPlanIsPro} onChange={setCreateForm} onSubmit={submitTournament} /> : null}
        {activeView === "account" ? <SettingsView user={user} onLogout={handleLogout} onThemeChange={setThemePreference} onUserUpdate={setUser} /> : null}
        {activeView === "settings" ? <SettingsView user={user} onLogout={handleLogout} onThemeChange={setThemePreference} onUserUpdate={setUser} /> : null}
        {activeView === "subscription" ? <SubscriptionView currentPlan={currentPlan} onSelectPlan={async (tier) => {
          if (!user) return;
          try {
            const result = await updateProfile({ displayName: user.displayName || user.email.split("@")[0] || user.email, subscriptionTier: tier });
            const nextUser = result.user;
            const token = getToken();
            if (token) setSession({ token, user: nextUser });
            writeLocalStorage(`subscriptionTierOverride:${nextUser.id}`, tier);
            setUser(nextUser);
            setFormNotice({ type: "success", text: tier === "FREE" ? "Free plan selected." : "Subscription plan updated for this account." });
          } catch (e) {
            setFormNotice({ type: "error", text: (e as Error).message });
          }
        }} /> : null}
        {activeView === "terms" ? <LegalView kind="terms" onBack={() => openView("dashboard")} /> : null}
        {activeView === "privacy" ? <LegalView kind="privacy" onBack={() => openView("dashboard")} /> : null}
        {activeView === "cookies" ? <LegalView kind="cookies" onBack={() => openView("dashboard")} /> : null}
        {activeView === "tournamentDashboard" ? <TournamentDashboardView initialTab={selectedTournamentTab} tournament={selectedTournament} registrations={registrations} user={user} onBack={() => openView("tournaments")} onDelete={removeTournament} onEdit={editTournament} onJoin={joinTournament} onSubmitCatch={() => openView("submit")} onAnnouncementsReload={reloadAnnouncementsAndCounts} /> : null}
        {activeView === "login" ? <LoginView onInstallApp={installApp} onLegal={openView} onLogin={handleLogin} onSignup={() => openView("signup")} showInstallPrompt={!isStandalone} /> : null}
        {activeView === "signup" ? (
          <RegistrationView onLegal={openView} onLogin={handleLogin} onLoginClick={() => openView("login")} />
        ) : null}
        </ViewErrorBoundary>
      </main>
      {user ? (
        <MobileBottomNav
          activeView={activeView}
          onNavigate={openView}
          primaryActionView={primaryAction.view}
          role={role}
        />
      ) : null}
    </div>
  );
}

function NavButton({ active, badge, icon, label, onClick }: { active: boolean; badge?: number; icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button className={active ? "active" : ""} onClick={onClick} type="button">
      {icon} {label}
      {badge ? <span className="nav-badge">{badge}</span> : null}
    </button>
  );
}

function MobileBottomNav({
  activeView,
  onNavigate,
  primaryActionView,
  role,
}: {
  activeView: View;
  onNavigate: (view: View) => void;
  primaryActionView: View;
  role: Role;
}) {
  const accountActive = activeView === "account" || activeView === "settings" || activeView === "subscription";
  const items: Array<{ active: boolean; icon: ReactNode; label: string; view: View }> = role === "creator"
    ? [
        { active: activeView === "dashboard", icon: <HomeIcon />, label: "Home", view: "dashboard" },
        { active: activeView === "tournaments", icon: <TrophyIcon />, label: "Events", view: "tournaments" },
        { active: activeView === primaryActionView, icon: <CreateNavIcon />, label: "Create", view: primaryActionView },
        { active: activeView === "pending", icon: <ClipboardIcon />, label: "Review", view: "pending" },
        { active: accountActive, icon: <UserIcon />, label: "Account", view: "account" },
      ]
    : [
        { active: activeView === "dashboard", icon: <HomeIcon />, label: "Home", view: "dashboard" },
        { active: activeView === "tournaments", icon: <TrophyIcon />, label: "Events", view: "tournaments" },
        { active: activeView === primaryActionView, icon: <UploadIcon />, label: "Submit", view: primaryActionView },
        { active: activeView === "catches", icon: <FishIcon />, label: "Catches", view: "catches" },
        { active: accountActive, icon: <UserIcon />, label: "Account", view: "account" },
      ];

  return (
    <nav className="mobile-bottom-nav" aria-label="Mobile primary">
      {items.map((item) => (
        <button className={item.active ? "active" : ""} key={item.label} onClick={() => onNavigate(item.view)} type="button">
          <span>{item.icon}</span>
          <small>{item.label}</small>
        </button>
      ))}
    </nav>
  );
}

function DashboardView({
  announcements,
  counts,
  managedRegistrations,
  managedSubmissions,
  managedTournaments,
  onNavigate,
  onOpenLeaderboard,
  registrations,
  role,
  submissions,
  tournaments,
  user,
}: {
  announcements: AnnouncementItem[];
  counts: DashboardCounts | null;
  managedRegistrations: RegistrationItem[];
  managedSubmissions: SubmissionItem[];
  managedTournaments: Tournament[];
  onNavigate: (view: View) => void;
  onOpenLeaderboard: (tournamentId: string) => void;
  registrations: RegistrationItem[];
  role: Role;
  submissions: SubmissionItem[];
  tournaments: Tournament[];
  user: AuthUser | null;
}) {
  const liveCount = tournaments.filter(isTournamentLive).length;
  const upcomingCount = tournaments.filter((tournament) => !isTournamentEnded(tournament)).length;

  if (!user) {
    return (
      <LandingPage
        liveCount={liveCount}
        onGetStarted={() => onNavigate("signup")}
        onLegal={onNavigate}
        onLogin={() => onNavigate("login")}
        onViewTournaments={() => onNavigate("tournaments")}
        tournaments={tournaments}
        upcomingCount={upcomingCount}
      />
    );
  }

  return role === "creator" ? (
    <CreatorDashboard
      onNavigate={onNavigate}
      registrations={managedRegistrations}
      submissions={managedSubmissions}
      tournaments={managedTournaments}
    />
  ) : (
    <ParticipantDashboard
      announcements={announcements}
      counts={counts}
      onNavigate={onNavigate}
      onOpenLeaderboard={onOpenLeaderboard}
      registrations={registrations}
      submissions={submissions}
      tournaments={tournaments}
    />
  );
}

function LandingPage({
  liveCount,
  onGetStarted,
  onLegal,
  onLogin,
  onViewTournaments,
  tournaments,
  upcomingCount,
}: {
  liveCount: number;
  onGetStarted: () => void;
  onLegal: (view: View) => void;
  onLogin: () => void;
  onViewTournaments: () => void;
  tournaments: Tournament[];
  upcomingCount: number;
}) {
  return (
    <div className="landing-page">
      <LandingHero onGetStarted={onGetStarted} onLogin={onLogin} />
      <LandingStats liveCount={liveCount} tournaments={tournaments} upcomingCount={upcomingCount} />
      <LandingFeatures />
      <LandingTournamentPreview onViewTournaments={onViewTournaments} tournaments={tournaments} />
      <LandingRulesScoring />
      <LandingHow />
      <LandingTestimonials />
      <LandingFaq />
      <LandingPricing onGetStarted={onGetStarted} />
      <LandingCta onGetStarted={onGetStarted} />
      <LandingFooter onGetStarted={onGetStarted} onLegal={onLegal} onViewTournaments={onViewTournaments} />
    </div>
  );
}

function LandingHero({ onGetStarted, onLogin }: { onGetStarted: () => void; onLogin: () => void }) {
  const leaderboardRows = [
    { rank: 1, name: "Jordan Pike", catches: 4, total: "168 cm" },
    { rank: 2, name: "Sam Rivers", catches: 3, total: "152 cm", you: true },
    { rank: 3, name: "Elin Sjo", catches: 3, total: "144 cm" },
    { rank: 4, name: "Mikael Holm", catches: 4, total: "139 cm" },
    { rank: 5, name: "Diego R.", catches: 2, total: "131 cm" },
  ];

  return (
    <section className="landing-hero landing-section" id="overview">
      <div className="landing-hero-copy">
        <span className="landing-kicker"><WaterIcon /> Fishing Tournament Platform</span>
        <h1>Host, join and score fishing tournaments - beautifully.</h1>
        <p>
          Reelmaster is the modern platform for anglers and tournament organizers.
          Run real-time leaderboards, verify catches with photo and GPS proof, and
          build a lifetime archive of every fish you land.
        </p>
        <div className="landing-actions">
          <button className="submit-link hero-button" onClick={onGetStarted} type="button">
            Start a tournament <ArrowRightIcon />
          </button>
          <button className="clear-button hero-button" onClick={onLogin} type="button">
            Log in as angler
          </button>
        </div>
        <div className="landing-proof-row lovable-proof-row" aria-label="Reelmaster benefits">
          <span><CheckIcon /> Free plan available</span>
          <span><CheckIcon /> No credit card to try</span>
          <span><CheckIcon /> Works on mobile</span>
        </div>
      </div>

      <div className="landing-hero-preview" aria-label="Live tournament dashboard preview">
        <article className="lovable-leaderboard-card">
          <div className="lovable-leaderboard-head">
            <div>
              <span className="lovable-live-dot" aria-hidden="true" />
              <strong>Live leaderboard - Clearwater Classic</strong>
            </div>
            <span>Day 2 of 3</span>
          </div>
          <div className="lovable-leaderboard-list">
            {leaderboardRows.map((row) => (
              <div className={row.you ? "lovable-leaderboard-row is-you" : "lovable-leaderboard-row"} key={row.rank}>
                <span className="lovable-leaderboard-rank">{row.rank}</span>
                <div className="lovable-leaderboard-name">
                  <strong>{row.name}</strong>
                  {row.you ? <em>You</em> : null}
                  <small>{row.catches} approved catches</small>
                </div>
                <div className="lovable-leaderboard-score">
                  <strong>{row.total}</strong>
                  <small>total length</small>
                </div>
              </div>
            ))}
          </div>
          <footer>Updates every 30s - 142 anglers competing</footer>
        </article>
      </div>
    </section>
  );
}

function LandingFeatures() {
  const features = [
    { icon: <TrophyIcon />, title: "Host tournaments", body: "Launch single-day or multi-stage tournaments in minutes with custom rules, scoring, and proof requirements." },
    { icon: <FishLineIcon />, title: "Join tournaments", body: "Discover public and invite-only events near you, filter by species, region, and date, and join with a tap." },
    { icon: <ChartIcon />, title: "Live leaderboards", body: "Real-time standings update as catches are approved. Tiebreakers, multi-stage scoring, and category filters built in." },
    { icon: <CameraIcon />, title: "Submit catches", body: "Photo proof, GPS coordinates, species, length and weight submitted in a guided wizard from the water." },
    { icon: <ShieldIcon />, title: "Organizer reviews", body: "Approve or reject each catch with organizer notes. Audit trails keep your tournament fair and disputable." },
    { icon: <BadgeIcon />, title: "Built for anglers", body: "Catch archive, personal stats, and a beautiful catch map that grows with every event you fish." },
  ];

  return (
    <section className="landing-features landing-section" id="features">
      <div className="landing-section-center">
        <span className="landing-kicker">Features</span>
        <h2>Everything a tournament needs</h2>
        <p>From discovery to weigh-in, Reelmaster covers the entire tournament loop for both anglers and organizers.</p>
      </div>
      <div className="landing-feature-grid">
        {features.map((feature) => (
          <article className="landing-feature-card" key={feature.title}>
            <span>{feature.icon}</span>
            <h3>{feature.title}</h3>
            <p>{feature.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingTournamentPreview({ onViewTournaments, tournaments }: { onViewTournaments: () => void; tournaments: Tournament[] }) {
  const publicTournaments = safeItems(tournaments).filter((tournament) => getTournamentStatus(tournament) !== "DRAFT");
  const previewTournaments = publicTournaments
    .slice()
    .sort((a, b) => {
      const aLive = isTournamentLive(a) ? 0 : 1;
      const bLive = isTournamentLive(b) ? 0 : 1;
      if (aLive !== bLive) return aLive - bLive;
      return timestamp(a.startAt) - timestamp(b.startAt);
    })
    .slice(0, 3);

  return (
    <section className="landing-tournament-preview landing-section" id="tournaments">
      <div className="landing-preview-head">
        <div>
          <span className="landing-kicker">Tournaments</span>
          <h2>Live and upcoming</h2>
          <p>A taste of the tournaments running on Reelmaster right now.</p>
        </div>
        <button className="clear-button hero-button" onClick={onViewTournaments} type="button">Browse all <ArrowRightIcon /></button>
      </div>
      <div className="landing-tournament-grid">
        {previewTournaments.length ? previewTournaments.map((tournament, index) => (
          <LandingTournamentCard key={tournament.id} onViewTournaments={onViewTournaments} tournament={tournament} index={index} />
        )) : (
          <article className="landing-tournament-card landing-tournament-empty">
            <div className="landing-tournament-art" style={{ backgroundImage: `linear-gradient(135deg, rgba(4, 36, 43, 0.16), rgba(4, 36, 43, 0.72)), url('${fallbackLakeImages[0]}')` }} />
            <div className="landing-tournament-body">
              <span className="landing-live-pill">Ready</span>
              <h3>Create your first tournament</h3>
              <p>Launch a public or invite-only event with real rules, proof requirements, and leaderboards.</p>
              <button className="clear-button compact" onClick={onViewTournaments} type="button">Browse tournaments</button>
            </div>
          </article>
        )}
      </div>
    </section>
  );
}

function LandingTournamentCard({ index, onViewTournaments, tournament }: { index: number; onViewTournaments: () => void; tournament: Tournament }) {
  const species = tournament.species.slice(0, 2);
  const status = statusLabel(tournament);
  const unit = scoreUnit(tournament.scoringMetric);
  const joinLabel = canJoinTournament(tournament) ? "Open" : joinUnavailableLabel(tournament);

  return (
    <article className="landing-tournament-card">
      <div className="landing-tournament-art" style={{ backgroundImage: `linear-gradient(135deg, rgba(4, 36, 43, 0.08), rgba(4, 36, 43, 0.66)), url('${tournamentImage(tournament, index)}')` }}>
        <span className="landing-live-pill">{status}</span>
        <small>{scoringLabel(tournament.scoringMetric).toLowerCase()} - {unit}</small>
      </div>
      <div className="landing-tournament-body">
        <h3>{tournamentName(tournament)}</h3>
        <p>{stripHtml(tournament.description) || `${tournamentLocation(tournament)} fishing tournament.`}</p>
        <div className="landing-tournament-meta">
          <span><MapPinIcon /> {tournamentLocation(tournament)}</span>
          <span><CalendarIcon /> {formatRange(tournament.startAt, tournament.endAt)}</span>
          <span><TeamIcon /> {tournament.registrationCount}/{tournament.participantLimit || "open"}</span>
          <span><TrophyIcon /> {joinLabel}</span>
        </div>
        <div className="landing-tournament-tags">
          {(species.length ? species : ["open"]).map((item) => <span key={item}>{item}</span>)}
          {tournament.paymentRequired ? <span>{tournament.currency || "EUR"}</span> : <span>free</span>}
        </div>
        <button className="clear-button compact" onClick={onViewTournaments} type="button">View tournament</button>
      </div>
    </article>
  );
}

function LandingRulesScoring() {
  return (
    <section className="landing-rules landing-section" id="rules">
      <div className="landing-section-copy">
        <span className="landing-kicker">Rules & scoring</span>
        <h2>Configurable to your event</h2>
        <p>Whether you score by total length, weight, or a custom point system, Reelmaster gives organizers full control over the ruleset, proof requirements, and dispute flow.</p>
        <ul className="landing-check-list">
          <li><CheckIcon /> Length, weight, or points-based scoring</li>
          <li><CheckIcon /> Photo, video and GPS proof requirements</li>
          <li><CheckIcon /> Minimum size and species whitelists</li>
          <li><CheckIcon /> Per-stage scoring with cumulative totals</li>
          <li><CheckIcon /> Reviewer queue with approve / reject decisions</li>
        </ul>
      </div>
      <article className="landing-rule-sample">
        <header>
          <strong>Sample rule set</strong>
        </header>
        <span><small>Scoring</small><b>Total length (cm)</b></span>
        <span><small>Min. size</small><b>30 cm</b></span>
        <span><small>Proof</small><b>Photo + GPS</b></span>
        <span><small>Per angler cap</small><b>5 catches / day</b></span>
        <span><small>Tiebreaker</small><b>Longest single catch</b></span>
        <span><small>Dispute window</small><b>2 hours after weigh-in</b></span>
      </article>
    </section>
  );
}

function LandingStats({ liveCount, tournaments, upcomingCount }: { liveCount: number; tournaments: Tournament[]; upcomingCount: number }) {
  const publicTournaments = safeItems(tournaments).filter((tournament) => getTournamentStatus(tournament) !== "DRAFT");
  const registeredAnglers = publicTournaments.reduce((sum, tournament) => sum + tournament.registrationCount, 0);
  const countryCount = new Set(publicTournaments.map((tournament) => tournament.country).filter(Boolean)).size;
  const stats = [
    { icon: <UserIcon />, value: registeredAnglers.toLocaleString(), label: "Anglers" },
    { icon: <TrophyIcon />, value: publicTournaments.length.toLocaleString(), label: "Tournaments hosted" },
    { icon: <ChartIcon />, value: liveCount.toLocaleString(), label: "Live now" },
    { icon: <GlobeIcon />, value: Math.max(countryCount, upcomingCount ? 1 : 0).toLocaleString(), label: "Countries" },
  ];

  return (
    <section className="landing-stats" aria-label="Reelmaster platform statistics">
      <div className="landing-stat-grid">
        {stats.map((stat) => (
          <article key={stat.label}>
            <span aria-hidden="true">{stat.icon}</span>
            <div>
              <strong>{stat.value}</strong>
              <small>{stat.label}</small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingHow() {
  const steps = [
    { title: "1. Discover", body: "Browse upcoming tournaments by region, species and rule set." },
    { title: "2. Join", body: "Register, follow any organizer payment instructions, and sync your tournament dashboard." },
    { title: "3. Fish & submit", body: "Log catches with photo, length, weight and GPS from the water." },
    { title: "4. Climb the board", body: "Approved catches update the live leaderboard. Win prizes, earn ranks." },
  ];

  return (
    <section className="landing-how landing-section" id="how">
      <span className="landing-kicker">How it works</span>
      <h2>From sign-up to podium</h2>
      <div className="landing-step-row">
        {steps.map((step, index) => (
          <article key={step.title}>
            <span className="landing-step-number">{index + 1}</span>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingTestimonials() {
  const quotes = [
    { quote: "We replaced a 4-tab spreadsheet with Reelmaster. Our anglers love the live board.", name: "Captain Lewis", role: "Charter operator, FL" },
    { quote: "Submission review is so fast. I cleared 80 catches at the dock in under an hour.", name: "Anya Holm", role: "Pike series organizer, SE" },
    { quote: "The catch map is a journal of every trip I've ever fished. I'm hooked.", name: "Diego R.", role: "Bass angler, MX" },
  ];

  return (
    <section className="landing-testimonials landing-section" aria-label="Community testimonials">
      <span className="landing-kicker">Trusted by anglers</span>
      <h2>What our community says.</h2>
      <div className="landing-testimonial-grid">
        {quotes.map((item) => (
          <article key={item.name}>
            <p>{item.quote}</p>
            <div>
              <span>{item.name.slice(0, 1)}</span>
              <strong>{item.name}<small>{item.role}</small></strong>
              <em>4.9</em>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingFaq() {
  const faqs = [
    ["Is Reelmaster suitable for catch-and-release events?", "Yes. Length-based scoring with photo proof is fully supported, and you can require GPS coordinates for verification."],
    ["Can I run multi-stage tournaments?", "Pro and Pro Archive plans support multi-stage events with stage-level scoring and cumulative leaderboards."],
    ["What proof types are accepted?", "Photos, short videos, and GPS coordinates. Organizers can require any combination per tournament."],
    ["Do anglers need an account to join?", "Yes. Every angler has their own catch archive and leaderboard identity."],
    ["Is there an offline mode?", "Submit catches when connected so photos, GPS proof, and review status reach the tournament backend."],
  ];

  return (
    <section className="landing-faq landing-section" id="faq">
      <div className="landing-section-center">
        <span className="landing-kicker">FAQ</span>
        <h2>Frequently asked questions</h2>
      </div>
      <div className="landing-faq-list">
        {faqs.map(([question, answer], index) => (
          <details key={question} open={index === 0}>
            <summary>{question}<PlusIcon /></summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function LandingPricing({ onGetStarted }: { onGetStarted: () => void }) {
  const plans = [
    {
      name: "Free",
      price: "$0",
      cadence: "forever",
      badge: "",
      items: ["Join up to 3 tournaments / month", "Basic catch submissions", "Community leaderboards"],
    },
    {
      name: "Pro",
      price: "$9",
      cadence: "per month",
      badge: "Most popular",
      items: ["Unlimited tournament entries", "Host up to 5 tournaments", "Advanced filters & GPS history", "Priority support"],
    },
    {
      name: "Pro Archive",
      price: "$19",
      cadence: "per month",
      badge: "",
      items: ["Everything in Pro", "Lifetime catch archive & export", "Unlimited hosting", "Sponsor branding"],
    },
  ];

  return (
    <section className="landing-pricing landing-section" id="landing-pricing">
      <div className="landing-section-center">
        <span className="landing-kicker">Pricing</span>
        <h2>Simple plans for every angler</h2>
        <p>Start free. Upgrade when you're ready to host or archive.</p>
      </div>
      <div className="landing-pricing-grid">
        {plans.map((plan, index) => (
          <article className={index === 1 ? "featured" : ""} key={plan.name}>
            <span className="landing-price-badge">{plan.badge}</span>
            <h3>{plan.name}</h3>
            <strong>{plan.price}<small> {plan.cadence}</small></strong>
            <ul>
              {plan.items.map((item) => <li key={item}><CheckIcon /> {item}</li>)}
            </ul>
            <button className={index === 1 ? "submit-link" : "clear-button"} onClick={onGetStarted} type="button">
              {plan.price === "$0" ? "Get started" : index === 1 ? "Start Pro" : "Go Pro Archive"}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function LandingCta({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="landing-cta landing-section">
      <span className="landing-cta-icon"><WaterIcon /></span>
      <h2>Cast off with Reelmaster</h2>
      <p>Join thousands of anglers running better tournaments. Free to start, no credit card.</p>
      <div className="landing-actions">
        <button className="submit-link hero-button" onClick={onGetStarted} type="button">Create your account</button>
        <button className="clear-button hero-button" onClick={() => document.getElementById("tournaments")?.scrollIntoView({ behavior: "smooth" })} type="button">Browse tournaments</button>
      </div>
    </section>
  );
}

function LandingFooter({
  onGetStarted,
  onLegal,
  onViewTournaments,
}: {
  onGetStarted: () => void;
  onLegal: (view: View) => void;
  onViewTournaments: () => void;
}) {
  return (
    <footer className="landing-footer">
      <div className="landing-footer-brand">
        <div className="brand">
          <span className="brand-mark"><FishIcon /></span>
          <span><strong>Reelmaster</strong><small>Fishing tournament platform</small></span>
        </div>
        <p>The all-in-one platform for fishing tournaments, built by anglers, for anglers.</p>
      </div>
      <nav aria-label="Footer">
        <strong>Platform</strong>
        <a href="#overview">Overview</a>
        <a href="#features">Features</a>
        <a href="#how">How it Works</a>
        <a href="#faq">FAQ</a>
      </nav>
      <nav aria-label="Tournament links">
        <strong>Tournaments</strong>
        <button className="footer-link-button" onClick={onViewTournaments} type="button">Browse Tournaments</button>
        <button className="footer-link-button" onClick={onGetStarted} type="button">Create Tournament</button>
        <a href="#rules">Rules & Scoring</a>
        <a href="#landing-pricing">Pricing</a>
      </nav>
      <nav aria-label="Legal links">
        <strong>Legal</strong>
        <button className="footer-link-button" onClick={() => onLegal("terms")} type="button">Terms of Service</button>
        <button className="footer-link-button" onClick={() => onLegal("privacy")} type="button">Privacy Policy</button>
        <button className="footer-link-button" onClick={() => onLegal("cookies")} type="button">Cookie Policy</button>
      </nav>
    </footer>
  );
}

function LegalView({ kind, onBack }: { kind: "terms" | "privacy" | "cookies"; onBack: () => void }) {
  const pages = {
    terms: {
      eyebrow: "Legal",
      title: "Terms of Service",
      intro: "These terms explain the basic rules for using Reelmaster to browse, join, create, and manage fishing tournaments.",
      sections: [
        ["Using Reelmaster", "You may use Reelmaster to create an account, browse tournaments, request entry, submit catches, and manage events. You are responsible for keeping your login details secure and for the accuracy of information you submit."],
        ["Tournament rules", "Tournament organizers control event rules, scoring methods, proof requirements, participant approvals, manual payment instructions, and review decisions. Participants must follow the rules shown for each tournament."],
        ["Catch submissions", "Catch photos, videos, measurements, notes, timestamps, and GPS details may be reviewed by organizers before appearing on leaderboards. Reelmaster may reject or remove submissions that appear incomplete, fraudulent, unsafe, or outside tournament rules."],
        ["Payments and subscriptions", "The current MVP supports manual tournament payments and subscription plan selection. Paid processor integration is not currently active unless explicitly shown during checkout."],
        ["Availability", "Reelmaster is provided as an MVP and may change, pause, or remove features while the platform is improved. We aim to keep the app reliable, but we cannot guarantee uninterrupted service."],
      ],
    },
    privacy: {
      eyebrow: "Privacy",
      title: "Privacy Policy",
      intro: "This policy describes what information Reelmaster uses to run tournament accounts, submissions, leaderboards, and organizer tools.",
      sections: [
        ["Account information", "We store account details such as email address, display name, role, profile preferences, subscription tier, and settings needed to keep your account working."],
        ["Tournament activity", "We process registrations, catch submissions, measurements, media URLs, review notes, leaderboard results, announcements, and organizer decisions so tournaments can operate."],
        ["Location and media", "When a tournament requires proof, you may choose to submit photos, videos, GPS coordinates, accuracy, and capture timestamps. Browser permissions are requested before location capture."],
        ["Local preferences", "Theme, locale, measurement, date, notification, and account preferences may be stored in your browser using localStorage and cookies so settings persist between visits and logins."],
        ["Data sharing", "Tournament organizers can see relevant participant, registration, and submission details for events they manage. We do not sell personal information."],
      ],
    },
    cookies: {
      eyebrow: "Cookies",
      title: "Cookie Policy",
      intro: "Reelmaster uses small browser storage values to keep the app usable, remember preferences, and support login sessions.",
      sections: [
        ["Essential storage", "Authentication tokens and account state are stored in browser storage so the app can keep you signed in during the MVP session window."],
        ["Preference cookies", "Theme preference is saved to a cookie and localStorage. This lets Reelmaster remember light, dark, or system mode after refreshes and future logins."],
        ["Local app settings", "Country, measurement system, date format, compact mode, notification preferences, and similar settings may be stored locally on your device."],
        ["Managing cookies", "You can clear cookies and site data from your browser settings. Clearing site data may sign you out and reset preferences."],
        ["Third-party services", "The static frontend communicates with the Reelmaster WordPress REST API. Future payment or analytics services will be documented here if added."],
      ],
    },
  } as const;

  const page = pages[kind];

  return (
    <section className="legal-page">
      <button className="clear-button compact legal-back-button" onClick={onBack} type="button"><ArrowLeftIcon /> Back to home</button>
      <div className="legal-hero">
        <span className="landing-kicker">{page.eyebrow}</span>
        <h1>{page.title}</h1>
        <p>{page.intro}</p>
        <small>Last updated: June 9, 2026</small>
      </div>
      <div className="legal-section-list">
        {page.sections.map(([title, body]) => (
          <article key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </article>
        ))}
      </div>
      <article className="legal-note">
        <h2>Contact</h2>
        <p>Questions about these policies can be sent to the Reelmaster site owner through the contact details provided with the tournament or hosting account.</p>
      </article>
    </section>
  );
}

function FishingBoatIllustration() {
  return (
    <svg className="landing-boat-illustration" viewBox="0 0 620 460" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="boatWater" x1="0" x2="1" y1="0" y2="1">
          <stop stopColor="#48F0DF" />
          <stop offset="1" stopColor="#0F5D75" />
        </linearGradient>
        <linearGradient id="boatGlow" x1="0" x2="1" y1="0" y2="0">
          <stop stopColor="#FFFFFF" stopOpacity="0.92" />
          <stop offset="1" stopColor="#8BF6E9" stopOpacity="0.35" />
        </linearGradient>
      </defs>
      <rect x="30" y="54" width="560" height="336" rx="48" fill="url(#boatGlow)" opacity="0.12" />
      <path d="M86 304c62-34 129-34 202 0s150 34 232 0" fill="none" stroke="url(#boatWater)" strokeLinecap="round" strokeWidth="18" />
      <path d="M108 334c55-19 106-18 154 3 63 28 128 27 201-4" fill="none" stroke="#85F5E8" strokeLinecap="round" strokeOpacity="0.36" strokeWidth="10" />
      <path d="M206 258h224l-34 52H238l-32-52Z" fill="#082A3C" stroke="#7EF3E5" strokeWidth="8" />
      <path d="M250 258l54-70 76 70" fill="#0C4157" stroke="#7EF3E5" strokeLinejoin="round" strokeWidth="8" />
      <path d="M362 183c28-44 72-69 130-75" fill="none" stroke="#D9FBF7" strokeLinecap="round" strokeWidth="7" />
      <circle cx="494" cy="108" r="8" fill="#7EF3E5" />
      <path d="M484 143c24-18 55-20 83-5-23 26-53 35-83 5Z" fill="#7EF3E5" />
      <path d="M540 135l27-18-5 36-22-18Z" fill="#7EF3E5" />
      <path d="M134 190c22-17 50-18 75-4-20 23-47 30-75 4Z" fill="#FFFFFF" opacity="0.9" />
      <path d="M185 182l24-16-5 32-19-16Z" fill="#FFFFFF" opacity="0.9" />
      <circle cx="152" cy="187" r="4" fill="#09263A" />
      <path d="M92 246c23-11 48-11 75 0" fill="none" stroke="#8BF6E9" strokeLinecap="round" strokeOpacity="0.42" strokeWidth="7" />
      <path d="M445 246c31-13 65-13 101 0" fill="none" stroke="#8BF6E9" strokeLinecap="round" strokeOpacity="0.42" strokeWidth="7" />
    </svg>
  );
}

function ParticipantDashboard({
  announcements,
  counts,
  onNavigate,
  onOpenLeaderboard,
  registrations,
  submissions,
  tournaments,
}: {
  announcements: AnnouncementItem[];
  counts: DashboardCounts | null;
  onNavigate: (view: View) => void;
  onOpenLeaderboard: (tournamentId: string) => void;
  registrations: RegistrationItem[];
  submissions: SubmissionItem[];
  tournaments: Tournament[];
}) {
  const joinedIds = new Set(registrations.filter((item) => item.status === "APPROVED").map((item) => item.tournamentId));
  const joined = tournaments.filter((tournament) => joinedIds.has(tournament.id));
  const activeJoined = joined.filter((tournament) => isTournamentLive(tournament));
  const upcomingJoined = joined.filter((tournament) => getTournamentStatus(tournament) === "UPCOMING");
  const pendingReviews = submissions.filter((submission) => submission.status === "PENDING");
  const approvedSubmissions = submissions.filter((submission) => submission.status === "APPROVED");
  const unreadAnnouncements = announcements.filter((item) => !item.readAt);
  const bestLength = Math.max(0, ...approvedSubmissions.map((submission) => submission.length || 0));
  const featured = activeJoined[0] ?? joined[0] ?? tournaments.find(isTournamentLive) ?? tournaments[0] ?? null;
  const featuredSubmissions = featured ? submissions.filter((submission) => submission.tournamentId === featured.id) : [];

  return (
    <section className="dashboard-screen participant-screen">
      <header className="dashboard-page-heading">
        <div>
          <span className="greeting-line">Good evening, angler</span>
          <h1>Welcome back, angler.</h1>
          <p>{activeJoined.length ? `You're fishing ${activeJoined.length} live tournament right now.` : "Find a tournament and submit your first catch."}</p>
        </div>
      </header>

      <ParticipantFeatureCard
        onLeaderboard={() => featured ? onOpenLeaderboard(featured.id) : onNavigate("tournaments")}
        onSubmit={() => onNavigate("submit")}
        submissions={featuredSubmissions}
        tournament={featured}
      />

      <section className="design-stat-grid">
        <DesignStatCard icon={<TrophyIcon />} label="Active Tournaments" value={String(activeJoined.length || joined.length)} hint="Live now" />
        <DesignStatCard icon={<FishLineIcon />} label="Catches Submitted" value={String(submissions.length)} hint="This season" />
        <DesignStatCard icon={<BellIcon />} label="Pending Reviews" value={String(pendingReviews.length)} hint="Awaiting results" tone="warning" />
        <DesignStatCard icon={<FishIcon />} label="Best Score" value={bestLength ? `${bestLength} cm` : "-"} hint="This season" tone="success" />
      </section>

      <div className="participant-dashboard-grid">
        <section className="design-panel">
          <PanelTitle title="Your Active Tournaments" action="View all" onAction={() => onNavigate("tournaments")} />
          <div className="participant-list-stack">
            {(activeJoined.length ? activeJoined : joined).slice(0, 2).map((tournament, index) => (
              <ParticipantTournamentRow key={tournament.id} index={index} submissions={submissions.filter((submission) => submission.tournamentId === tournament.id)} tournament={tournament} />
            ))}
            {!joined.length ? <p className="design-empty">Approved registrations will appear here.</p> : null}
          </div>
        </section>

        <section className="design-panel">
          <PanelTitle title="Recent Submissions" action="View all" onAction={() => onNavigate("catches")} />
          <div className="participant-list-stack">
            {submissions.slice(0, 3).map((submission, index) => <RecentSubmissionRow key={submission.id} index={index} submission={submission} />)}
            {!submissions.length ? <p className="design-empty">Submitted catches will appear here.</p> : null}
          </div>
        </section>

        <section className="design-panel">
          <PanelTitle title="Registered & Upcoming" action="View all" onAction={() => onNavigate("tournaments")} />
          <div className="participant-list-stack">
            {upcomingJoined.slice(0, 2).map((tournament) => <UpcomingParticipantRow key={tournament.id} tournament={tournament} />)}
            {!upcomingJoined.length ? <p className="design-empty">No upcoming registered events.</p> : null}
          </div>
        </section>

        <section className="design-panel">
          <PanelTitle title="Announcements" action="View all" onAction={() => onNavigate("announcements")} />
          <div className="participant-list-stack">
            {unreadAnnouncements.slice(0, 4).map((announcement) => (
              <article className="announcement-row" key={announcement.id}>
                <span className="announcement-icon"><BellIcon /></span>
                <div>
                  <strong>{announcement.title}</strong>
                  <small>{announcement.tournamentName || "Tournament update"}</small>
                </div>
                <time>{formatDate(announcement.createdAt)}</time>
              </article>
            ))}
            {!unreadAnnouncements.length ? <p className="design-empty">No unread announcements.</p> : null}
          </div>
        </section>
      </div>
    </section>
  );
}

function TournamentAnnouncementsPanel({
  announcements,
  canManage,
  onGlobalReload,
  onReload,
  showList = true,
  tournament,
}: {
  announcements: AnnouncementItem[];
  canManage: boolean;
  onGlobalReload: () => Promise<void>;
  onReload: () => Promise<void>;
  showList?: boolean;
  tournament: Tournament;
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState(false);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await updateAnnouncement(editingId, { title, content });
      } else {
        await createAnnouncement({ tournamentId: tournament.id, title, content });
      }
      setTitle("");
      setContent("");
      setEditingId("");
      await Promise.all([onReload(), onGlobalReload()]);
    } finally {
      setBusy(false);
    }
  }

  async function markRead(id: string) {
    await markAnnouncementRead(id);
    await Promise.all([onReload(), onGlobalReload()]);
  }

  return (
    <section className="announcement-list">
      {canManage ? (
        <form className="form-card compact-form" onSubmit={save}>
          <h2>{editingId ? "Edit announcement" : "Create announcement"}</h2>
          <label className="field">
            <span>Title</span>
            <input required value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Rules reminder" />
          </label>
          <RichTextEditor value={content} onChange={setContent} />
          <div className="card-actions">
            <button className="submit-link compact" disabled={busy || !title.trim() || !content.trim()} type="submit">{busy ? "Saving..." : "Save announcement"}</button>
            {editingId ? <button className="clear-button compact" type="button" onClick={() => { setEditingId(""); setTitle(""); setContent(""); }}>Cancel</button> : null}
          </div>
        </form>
      ) : null}
      {showList && announcements.length ? announcements.map((announcement) => (
        <article className={announcement.readAt ? "announcement-card read" : "announcement-card"} key={announcement.id}>
          <div>
            <h2>{announcement.title}</h2>
            <div className="rich-content" dangerouslySetInnerHTML={{ __html: announcement.content }} />
          </div>
          <div className="card-actions">
            {canManage ? (
              <>
                <button className="clear-button compact" type="button" onClick={() => { setEditingId(announcement.id); setTitle(announcement.title); setContent(announcement.content); }}>Edit</button>
                <button className="danger-button compact" type="button" onClick={async () => { await deleteAnnouncement(announcement.id); await Promise.all([onReload(), onGlobalReload()]); }}>Delete</button>
              </>
            ) : !announcement.readAt ? (
              <button className="clear-button compact" type="button" onClick={() => void markRead(announcement.id)}>Mark read</button>
            ) : null}
          </div>
        </article>
      )) : null}
      {showList && !announcements.length ? <EmptyCard title="No announcements yet" body="Tournament announcements will appear here." /> : null}
    </section>
  );
}

function CreatorDashboard({
  onNavigate,
  registrations,
  submissions,
  tournaments,
}: {
  onNavigate: (view: View) => void;
  registrations: RegistrationItem[];
  submissions: SubmissionItem[];
  tournaments: Tournament[];
}) {
  const liveTournaments = tournaments.filter(isTournamentLive);
  const activeTournaments = tournaments.filter((item) => !isTournamentEnded(item) && getTournamentStatus(item) !== "DRAFT");
  const upcomingTournaments = tournaments.filter((item) => getTournamentStatus(item) === "UPCOMING");
  const draftTournaments = tournaments.filter((item) => getTournamentStatus(item) === "DRAFT");
  const pendingCatches = submissions.filter((submission) => submission.status === "PENDING");
  const reviewedCatches = submissions.filter((submission) => submission.status === "APPROVED" || submission.status === "REJECTED");
  const todaySubmissions = submissions.filter((submission) => isToday(submission.createdAt));
  const totalAnglers = tournaments.reduce((sum, tournament) => sum + tournament.registrationCount, 0);
  const totalPending = pendingCatches.length;
  const reportDays = lastSevenDays();
  const firstReportDay = reportDays[0]?.start.getTime() ?? 0;
  const lastReportDay = reportDays[reportDays.length - 1]?.end.getTime() ?? Date.now();
  const weeklySubmissions = submissions.filter((submission) => {
    const createdAt = timestamp(submission.createdAt);
    return createdAt >= firstReportDay && createdAt < lastReportDay;
  });
  const weeklyRegistrations = registrations.filter((registration) => {
    const createdAt = timestamp(registration.createdAt);
    return createdAt >= firstReportDay && createdAt < lastReportDay;
  });
  const weeklyRange = reportDays.length ? `${shortDateLabel(reportDays[0].start)} - ${shortDateLabel(reportDays[reportDays.length - 1].start)}` : "Last 7 days";
  const insightSeries = reportDays.map((day) => submissions.filter((submission) => {
    const createdAt = validDate(submission.createdAt);
    return createdAt ? createdAt >= day.start && createdAt < day.end : false;
  }).length);

  return (
    <section className="dashboard-screen creator-screen">
      <header className="creator-top-grid">
        <div>
          <span className="greeting-line">Creator</span>
          <h1>Manage your tournaments.</h1>
          <p>Create, run, and grow fishing tournaments all in one place.</p>
        </div>
        <article className="priority-banner">
          <span className="priority-icon"><ClipboardIcon /></span>
          <div>
            <small>Priority</small>
            <strong>{totalPending} submissions awaiting review</strong>
            <p>Timely reviews keep events fair and anglers happy.</p>
          </div>
          <button className="priority-review-action" type="button" onClick={() => onNavigate("pending")}>
            Review submissions <ArrowRightIcon />
          </button>
        </article>
      </header>

      <section className="design-stat-grid creator-stats">
        <CreatorStatCard icon={<FishLineIcon />} label="My Live Events" value={String(liveTournaments.length)} hint="Currently running" onAction={() => onNavigate("tournaments")} />
        <CreatorStatCard icon={<FishIcon />} label="My Tournament Anglers" value={String(totalAnglers)} hint="Across my events" onAction={() => onNavigate("tournaments")} />
        <CreatorStatCard icon={<ClipboardIcon />} label="Submissions Today" value={String(todaySubmissions.length)} hint="From your events" onAction={() => onNavigate("pending")} />
        <CreatorStatCard icon={<BellIcon />} label="Pending Review" value={String(totalPending)} hint="Requires your attention" onAction={() => onNavigate("pending")} />
      </section>

      <div className="creator-dashboard-grid">
        <section className="design-panel live-tournaments-panel">
          <PanelTitle title="Live Tournaments" action="View all" onAction={() => onNavigate("tournaments")} />
          {activeTournaments.length ? (
            <div className="creator-table-list">
              {(liveTournaments.length ? liveTournaments : activeTournaments).slice(0, 4).map((tournament, index) => (
                <CreatorTournamentRow
                  index={index}
                  key={tournament.id}
                  onManage={() => onNavigate("tournaments")}
                  pending={pendingCatches.filter((submission) => submission.tournamentId === tournament.id).length}
                  submissions={submissions.filter((submission) => submission.tournamentId === tournament.id).length}
                  tournament={tournament}
                />
              ))}
            </div>
          ) : <p className="design-empty live-empty-copy">Create or publish a tournament to start the operations board.</p>}
        </section>

        <section className="design-panel upcoming-panel">
          <PanelTitle title="Upcoming Tournaments" action="View all" onAction={() => onNavigate("tournaments")} />
          <div className="creator-compact-list">
            {upcomingTournaments.slice(0, 3).map((tournament) => <CreatorUpcomingRow key={tournament.id} tournament={tournament} />)}
            {!upcomingTournaments.length ? <p className="design-empty">No upcoming tournaments.</p> : null}
          </div>
        </section>

        <section className="design-panel drafts-panel">
          <PanelTitle title="Drafts" action="View all" onAction={() => onNavigate("tournaments")} />
          <div className="creator-compact-list">
            {draftTournaments.slice(0, 3).map((tournament) => <DraftRow key={tournament.id} tournament={tournament} />)}
            {!draftTournaments.length ? <p className="design-empty">No drafts.</p> : null}
          </div>
        </section>

        <section className="design-panel weekly-panel">
          <PanelTitle title="Weekly Operations" action={weeklyRange} />
          <div className="ops-grid">
            <OpsMetric icon={<TrophyIcon />} value={String(weeklyRegistrations.length)} label="New registrations" note="this week" />
            <OpsMetric icon={<ClipboardIcon />} value={String(weeklySubmissions.length)} label="Submissions" note="this week" />
            <OpsMetric icon={<BellIcon />} value={String(reviewedCatches.length)} label="Reviewed" note="all time" />
            <OpsMetric icon={<UploadIcon />} value={String(totalPending)} label="Pending issues" note="now" negative />
          </div>
        </section>

        <section className="design-panel insights-panel">
          <PanelTitle title="Insights" />
          <InsightsChart labels={reportDays.map((day) => shortDateLabel(day.start))} values={insightSeries} />
          <div className="insights-report">
            <span><small>Managed tournaments</small><strong>{tournaments.length}</strong></span>
            <span><small>Registrations this week</small><strong>{weeklyRegistrations.length}</strong></span>
            <span><small>Submissions this week</small><strong>{weeklySubmissions.length}</strong></span>
            <span><small>Pending reviews</small><strong>{totalPending}</strong></span>
          </div>
        </section>
      </div>
    </section>
  );
}

function PanelTitle({ action, onAction, title }: { action?: string; onAction?: () => void; title: string }) {
  return (
    <div className="panel-title-row">
      <h2>{title}</h2>
      {action && onAction ? <button type="button" onClick={onAction}>{action}</button> : null}
      {action && !onAction ? <span className="panel-title-action">{action}</span> : null}
    </div>
  );
}

function ParticipantFeatureCard({
  onLeaderboard,
  onSubmit,
  submissions,
  tournament,
}: {
  onLeaderboard: () => void;
  onSubmit: () => void;
  submissions: SubmissionItem[];
  tournament: Tournament | null;
}) {
  const approved = submissions.filter((submission) => submission.status === "APPROVED");
  const best = Math.max(0, ...approved.map((submission) => submission.length || submission.weight || 0));
  return (
    <article className="participant-feature-card" style={{ backgroundImage: `linear-gradient(90deg, rgba(6, 28, 43, 0.96) 0%, rgba(6, 28, 43, 0.78) 43%, rgba(6, 28, 43, 0.25) 100%), url('${tournamentImage(tournament)}')` }}>
      <div className="feature-card-copy">
        <div className="feature-status-row">
          <span className="feature-live-pill">Live</span>
          <small>Ends in</small>
          <strong>{tournament ? timeLeftLabel(tournament.endAt) : "-"}</strong>
        </div>
        <h2>{tournament ? tournamentName(tournament) : "Join your first tournament"}</h2>
        <p><MapPinIcon /> {tournament ? tournamentLocation(tournament) : "Find open events near you"}</p>
        <span className="scoring-chip"><FishIcon /> Live Scoring</span>
        <div className="feature-metric-card">
          <MiniMetric label="Your rank" value={approved.length ? "1" : "-"} />
          <MiniMetric label="Best score" value={best ? `${best} ${tournament ? scoreUnit(tournament.scoringMetric) : "cm"}` : "-"} />
          <MiniMetric label="Status" value={approved.length ? "On the board" : "Awaiting catch"} />
        </div>
      </div>
      <div className="feature-actions">
        <button className="clear-button hero-button" type="button" onClick={onLeaderboard}><TrophyIcon /> View leaderboard</button>
        <button className="submit-link hero-button" type="button" onClick={onSubmit}><UploadIcon /> Submit Catch</button>
      </div>
    </article>
  );
}

function DesignStatCard({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: string; hint: string; tone?: "warning" | "success" }) {
  return (
    <article className="design-stat-card">
      <span className={tone ? `design-stat-icon ${tone}` : "design-stat-icon"}>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{hint}</p>
      </div>
    </article>
  );
}

function ParticipantTournamentRow({ index, submissions, tournament }: { index: number; submissions: SubmissionItem[]; tournament: Tournament }) {
  const approved = submissions.filter((submission) => submission.status === "APPROVED");
  const best = Math.max(0, ...approved.map((submission) => submission.length || submission.weight || 0));
  return (
    <article className="participant-tournament-row">
      <img src={tournamentImage(tournament, index)} alt="" />
      <div>
        <strong>{tournamentName(tournament)}</strong>
        <small><MapPinIcon /> {tournamentLocation(tournament)}</small>
        <small><FishLineIcon /> Ends {formatDate(tournament.endAt)}</small>
      </div>
      <span className="feature-live-pill">Live</span>
      <div className="row-rank">
        <small>Rank</small>
        <strong>{approved.length ? `1/${Math.max(tournament.registrationCount, 1)}` : `-/${Math.max(tournament.registrationCount, 1)}`}</strong>
        <small>Best Score</small>
        <strong>{best ? String(best) : "-"}</strong>
      </div>
    </article>
  );
}

function RecentSubmissionRow({ index, submission }: { index: number; submission: SubmissionItem }) {
  const photoUrl = safeMediaUrl(submission.photoUrl);
  return (
    <article className="recent-submission-row">
      {photoUrl ? <img src={photoUrl} alt="" /> : <img src={fallbackLakeImages[index % fallbackLakeImages.length]} alt="" />}
      <div>
        <strong>{submission.species || "Catch"}</strong>
        <small>{submission.length || "-"} cm - {submission.tournamentName || "Tournament"}</small>
      </div>
      <span className={`submission-state ${submissionStatusClass(submission.status)}`}>{submissionStatusLabel(submission.status)}</span>
      <time>{formatDate(submission.createdAt)}</time>
    </article>
  );
}

function UpcomingParticipantRow({ tournament }: { tournament: Tournament }) {
  const date = tournament.startAt ? new Date(tournament.startAt) : null;
  return (
    <article className="upcoming-participant-row">
      <span className="date-tile"><small>{date ? new Intl.DateTimeFormat(undefined, { month: "short" }).format(date) : "TBD"}</small><strong>{date ? date.getDate() : "-"}</strong></span>
      <div>
        <strong>{tournamentName(tournament)}</strong>
        <small><MapPinIcon /> {tournamentLocation(tournament)}</small>
        <small><FishLineIcon /> Starts {formatRange(tournament.startAt, tournament.endAt)}</small>
      </div>
      <span className="registered-pill">Registered</span>
    </article>
  );
}

function CreatorStatCard({ icon, label, onAction, value, hint }: { icon: ReactNode; label: string; onAction: () => void; value: string; hint: string }) {
  return (
    <article className="creator-stat-card">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{hint}</p>
      </div>
      <button type="button" onClick={onAction}>View all</button>
    </article>
  );
}

function CreatorTournamentRow({
  index,
  onManage,
  pending,
  submissions,
  tournament,
}: {
  index: number;
  onManage: () => void;
  pending: number;
  submissions: number;
  tournament: Tournament;
}) {
  return (
    <article className="creator-tournament-row">
      <img src={tournamentImage(tournament, index)} alt="" />
      <div className="creator-row-main">
        <strong>{tournamentName(tournament)}</strong>
        <small><MapPinIcon /> {tournamentLocation(tournament)}</small>
        <small><FishLineIcon /> {formatRange(tournament.startAt, tournament.endAt)} - {isTournamentLive(tournament) ? `Ends in ${timeLeftLabel(tournament.endAt)}` : `Starts in ${timeLeftLabel(tournament.startAt)}`}</small>
        <div className="creator-row-badges">
          <span className="feature-live-pill">{isTournamentLive(tournament) ? "Live" : statusLabel(tournament)}</span>
          {pending ? <span className="pending-count">{pending} pending</span> : null}
        </div>
      </div>
      <MiniMetric label="Anglers" value={String(tournament.registrationCount)} />
      <MiniMetric label="Submissions" value={String(submissions)} />
      <button className="clear-button compact" type="button" onClick={onManage}>View Board</button>
      <button className="submit-link compact" type="button" onClick={onManage}>Manage</button>
      <span className="row-menu">...</span>
    </article>
  );
}

function CreatorUpcomingRow({ tournament }: { tournament: Tournament }) {
  const date = tournament.startAt ? new Date(tournament.startAt) : null;
  return (
    <article className="creator-upcoming-row">
      <span className="date-tile"><small>{date ? new Intl.DateTimeFormat(undefined, { month: "short" }).format(date) : "TBD"}</small><strong>{date ? date.getDate() : "-"}</strong></span>
      <div>
        <strong>{tournamentName(tournament)}</strong>
        <small><MapPinIcon /> {tournamentLocation(tournament)}</small>
      </div>
      <p>Starts in {timeLeftLabel(tournament.startAt)}<br />{tournament.registrationCount} registered</p>
    </article>
  );
}

function DraftRow({ tournament }: { tournament: Tournament }) {
  return (
    <article className="draft-row">
      <span><ClipboardIcon /></span>
      <div>
        <strong>{tournamentName(tournament)}</strong>
        <small>Last edited {formatDate(tournament.startAt || tournament.endAt)}</small>
      </div>
      <i>...</i>
    </article>
  );
}

function OpsMetric({ icon, label, negative = false, note, value }: { icon: ReactNode; label: string; negative?: boolean; note: string; value: string }) {
  return (
    <article className="ops-metric">
      <span>{icon}</span>
      <strong>{value}</strong>
      <p>{label}</p>
      <small className={negative ? "negative" : ""}>{note}</small>
    </article>
  );
}

function InsightsChart({ labels, values }: { labels: string[]; values: number[] }) {
  const safeValues = values.length ? values : [0, 0, 0, 0, 0, 0, 0];
  const safeLabels = labels.length ? labels : ["-", "-", "-", "-", "-", "-", "-"];
  const maxValue = Math.max(1, ...safeValues);
  const width = 520;
  const left = 30;
  const right = 490;
  const top = 42;
  const bottom = 170;
  const step = safeValues.length > 1 ? (right - left) / (safeValues.length - 1) : 0;
  const points = safeValues.map((value, index) => {
    const x = left + (step * index);
    const y = bottom - ((value / maxValue) * (bottom - top));
    return { x, y, value };
  });
  const linePath = points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" ");
  const fillPath = `${linePath} L${right} ${bottom} L${left} ${bottom} Z`;
  const total = safeValues.reduce((sum, value) => sum + value, 0);

  return (
    <div className="insights-chart">
      <div className="chart-heading">
        <small>Submissions over time</small>
        <span>{total} total</span>
      </div>
      <svg viewBox={`0 0 ${width} 190`} role="img" aria-label="Submissions trend">
        <defs>
          <linearGradient id="dashboardChartFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#23d4d7" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#23d4d7" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#dashboardChartFill)" />
        <path d={linePath} fill="none" stroke="#23d4d7" strokeWidth="4" />
        {points.map((point, index) => <circle cx={point.x} cy={point.y} fill="#082536" key={`${point.x}-${index}`} r="6" stroke="#23d4d7" strokeWidth="4" />)}
        {[40, 80, 120, 160].map((y) => <line key={y} x1="24" x2="496" y1={y} y2={y} stroke="rgba(255,255,255,.08)" />)}
      </svg>
      <div className="chart-labels">{safeLabels.map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}</div>
      {!total ? <p className="design-empty">No submissions in this period.</p> : null}
    </div>
  );
}

function DashboardStatCard({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: string; hint?: string; tone?: "primary" | "warning" }) {
  return (
    <article className="dashboard-stat-card">
      <div className="stat-card-top">
        <span>{label}</span>
        <i className={tone === "warning" ? "warning" : ""}>{icon}</i>
      </div>
      <strong className={tone === "primary" ? "primary-value" : ""}>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </article>
  );
}

function DashboardSectionHeader({ action, onAction, subtitle, title }: { action?: string; onAction?: () => void; subtitle?: string; title: string }) {
  return (
    <div className="dashboard-section-heading">
      <div>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {action && onAction ? <button className="text-action" type="button" onClick={onAction}>{action}</button> : null}
    </div>
  );
}

function ParticipantEventCard({ submissions, tournament }: { submissions: SubmissionItem[]; tournament: Tournament }) {
  const approved = submissions.filter((submission) => submission.status === "APPROVED");
  const best = Math.max(0, ...approved.map((submission) => projectedScore(tournament.scoringMetric, submission.length, submission.weight)));
  const live = isTournamentLive(tournament);
  return (
    <article className="active-event-card">
      <div className="event-card-head">
        <div>
          <div className="event-meta-row">
            <span className={live ? "live-badge" : "status-pill"}>{live ? "Live" : statusLabel(tournament)}</span>
            <small><MapPinIcon />{tournamentLocation(tournament)}</small>
          </div>
          <h3>{tournamentName(tournament)}</h3>
        </div>
        <div className="rank-block">
          <span>Your rank</span>
          <strong>{approved.length ? "#1" : "-"}</strong>
          <small>{tournament.registrationCount ? `of ${tournament.registrationCount}` : "pending"}</small>
        </div>
      </div>
      <div className="event-mini-grid">
        <MiniMetric label="Score" value={best ? `${best} ${scoreUnit(tournament.scoringMetric)}` : "-"} />
        <MiniMetric label={live ? "Ends" : "Starts"} value={formatDate(live ? tournament.endAt : tournament.startAt)} />
        <MiniMetric label="Status" value={approved.length ? "On the board" : "Awaiting catch"} tone="primary" />
      </div>
    </article>
  );
}

function CreatorEventCard({ onManage, onReview, pending, tournament }: { onManage: () => void; onReview: () => void; pending: number; tournament: Tournament }) {
  const capacity = tournament.participantLimit || Math.max(tournament.registrationCount, 1);
  const fill = Math.min(100, Math.round((tournament.registrationCount / capacity) * 100));
  return (
    <article className="active-event-card">
      <div className="event-card-head">
        <div>
          <div className="event-meta-row">
            <span className="live-badge">{isTournamentLive(tournament) ? "Live" : statusLabel(tournament)}</span>
            {pending ? <span className="pending-badge">{pending} pending</span> : null}
            <small><MapPinIcon />{tournamentLocation(tournament)}</small>
          </div>
          <h3>{tournamentName(tournament)}</h3>
        </div>
        <div className="event-actions">
          <button className="clear-button compact" type="button" onClick={onManage}>Board</button>
          <button className="submit-link compact" type="button" onClick={onReview}>Review</button>
        </div>
      </div>
      <div className="event-mini-grid creator-mini-grid">
        <MiniMetric label="Anglers" value={`${tournament.registrationCount}/${tournament.participantLimit || "open"}`} />
        <MiniMetric label="Submissions" value="Live API" />
        <MiniMetric label="Pending" value={String(pending)} tone={pending ? "warning" : "primary"} />
        <MiniMetric label={isTournamentLive(tournament) ? "Ends" : "Starts"} value={formatDate(isTournamentLive(tournament) ? tournament.endAt : tournament.startAt)} />
      </div>
      <div className="capacity-track">
        <span>Capacity</span>
        <span>{fill}%</span>
        <i><b style={{ width: `${fill}%` }} /></i>
      </div>
    </article>
  );
}

function SubmissionMiniCard({ submission }: { submission: SubmissionItem }) {
  return (
    <article className="queue-card">
      <div className="queue-card-top">
        <strong>{submission.species || "Catch"}</strong>
        <span className={`mini-status ${submissionStatusClass(submission.status)}`}>{submissionStatusLabel(submission.status)}</span>
      </div>
      <p>{submission.tournamentName || "Tournament"}</p>
      <div className="queue-meta">
        <span>{submission.length || "-"} cm - {submission.weight || "-"} kg</span>
        <span>{formatDate(submission.createdAt)}</span>
      </div>
    </article>
  );
}

function ReviewQueueCard({ onReview, submission }: { onReview: () => void; submission: SubmissionItem }) {
  const initials = (submission.participantName || submission.participantEmail || "A").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return (
    <article className="review-card">
      <div className="review-row">
        <span className="review-avatar">{initials}</span>
        <div>
          <strong>{submission.participantName || submission.participantEmail}</strong>
          <p>{submission.species || "Catch"} - {submission.length || "-"} cm</p>
          <small>{submission.tournamentName}</small>
        </div>
      </div>
      <button className="submit-link compact" type="button" onClick={onReview}>Review</button>
    </article>
  );
}

function MiniMetric({ label, tone, value }: { label: string; tone?: "primary" | "warning"; value: string }) {
  return (
    <span className="mini-metric">
      <small>{label}</small>
      <strong className={tone === "primary" ? "primary-value" : tone === "warning" ? "warning-value" : ""}>{value}</strong>
    </span>
  );
}

function DashboardLeaderboardPreview({ tournament }: { tournament?: Tournament }) {
  const [dashboard, setDashboard] = useState<TournamentDashboardData | null>(null);
  const isLive = tournament ? isTournamentLive(tournament) : false;

  useEffect(() => {
    if (!tournament || !isLive) {
      setDashboard(null);
      return;
    }
    const tournamentId = tournament.id;
    let cancelled = false;
    async function load() {
      try {
        const data = await getTournamentDashboard(tournamentId);
        if (!cancelled) setDashboard(data);
      } catch {
        if (!cancelled) setDashboard(null);
      }
    }
    void load();
    const timer = window.setInterval(load, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isLive, tournament]);

  if (!tournament || !isLive) {
    return <p className="helper-text">No tournaments are live right now.</p>;
  }
  if (!dashboard?.rankings.length) {
    return <p className="helper-text">No approved catches on the live leaderboard yet.</p>;
  }
  return (
    <>
      {dashboard.rankings.slice(0, 3).map((item) => (
        <div className="leader-row" key={`${item.rank}-${item.participantEmail}`}>
          <span>{item.rank}</span>
          <b>{item.participantName}</b>
          <em>{item.score} {tournament.scoringMetric === "WEIGHT" ? "kg" : "cm"}</em>
        </div>
      ))}
    </>
  );
}

function TournamentsView({
  clearFilters,
  country,
  filterTournaments,
  joinedOnly,
  loading,
  query,
  region,
  setCountry,
  setJoinedOnly,
  setQuery,
  setRegion,
  setStartFrom,
  setStartTo,
  setTag,
  startFrom,
  startTo,
  tag,
  tournaments,
  registrations,
  role,
  user,
  onJoin,
  onSubmitCatch,
  onDashboard,
}: {
  clearFilters: () => void;
  country: string;
  filterTournaments: Tournament[];
  joinedOnly: boolean;
  loading: boolean;
  query: string;
  region: string;
  setCountry: (value: string) => void;
  setJoinedOnly: (value: boolean) => void;
  setQuery: (value: string) => void;
  setRegion: (value: string) => void;
  setStartFrom: (value: string) => void;
  setStartTo: (value: string) => void;
  setTag: (value: string) => void;
  startFrom: string;
  startTo: string;
  tag: string;
  tournaments: Tournament[];
  registrations: RegistrationItem[];
  role: Role;
  user: AuthUser | null;
  onJoin: (tournament: Tournament) => void;
  onSubmitCatch: () => void;
  onDashboard: (tournamentId: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<"ALL" | TournamentPhase>("ALL");
  const [sortOrder, setSortOrder] = useState("newest");
  const [visibleCount, setVisibleCount] = useState(10);
  const registrationByTournament = new Map(registrations.map((item) => [item.tournamentId, item]));
  const countries = countryOptions(filterTournaments.map((item) => item.country));
  const regionSource = filterTournaments.filter((item) => !country.trim() || item.country.toLowerCase().includes(country.trim().toLowerCase()));
  const regions = regionOptions(completeOptionValue(country, countries), regionSource.map((item) => item.region));
  const tagSource = filterTournaments.filter((item) => {
    if (country.trim() && !item.country.toLowerCase().includes(country.trim().toLowerCase())) return false;
    if (region.trim() && !item.region.toLowerCase().includes(region.trim().toLowerCase())) return false;
    return true;
  });
  const tags = Array.from(new Set(tagSource.flatMap((item) => item.tags).filter(Boolean))).sort();
  const statusCounts = {
    ALL: tournaments.length,
    LIVE: tournaments.filter((item) => getTournamentStatus(item) === "LIVE").length,
    UPCOMING: tournaments.filter((item) => getTournamentStatus(item) === "UPCOMING").length,
    DRAFT: tournaments.filter((item) => getTournamentStatus(item) === "DRAFT").length,
    ENDED: tournaments.filter(isTournamentEnded).length,
  };
  const filteredByStatus = tournaments.filter((tournament) => {
    if (statusFilter === "ALL") return true;
    if (statusFilter === "ENDED") return isTournamentEnded(tournament);
    return getTournamentStatus(tournament) === statusFilter;
  });
  const sortedTournaments = [...filteredByStatus].sort((a, b) => {
    const aStart = a.startAt ? new Date(a.startAt).getTime() : 0;
    const bStart = b.startAt ? new Date(b.startAt).getTime() : 0;
    if (sortOrder === "oldest") return aStart - bStart;
    if (sortOrder === "ending") return (a.endAt ? new Date(a.endAt).getTime() : 0) - (b.endAt ? new Date(b.endAt).getTime() : 0);
    if (sortOrder === "name") return tournamentName(a).localeCompare(tournamentName(b));
    return bStart - aStart;
  });
  const displayedTournaments = sortedTournaments.slice(0, visibleCount);
  const hasMoreTournaments = visibleCount < sortedTournaments.length;
  const featured = tournaments.find(isTournamentLive) ?? tournaments[0];
  const isCreatorPage = role === "creator";
  useEffect(() => {
    if (!isCreatorPage && statusFilter === "DRAFT") setStatusFilter("ALL");
  }, [isCreatorPage, statusFilter]);
  useEffect(() => {
    setVisibleCount(10);
  }, [country, joinedOnly, query, region, role, sortOrder, startFrom, startTo, statusFilter, tag]);
  const creatorCopy = isCreatorPage
    ? { eyebrow: "Organizer", title: "Manage your tournaments.", body: "Create, run, review, and grow fishing tournaments with real-time participant data." }
    : { eyebrow: "Tournaments", title: "Find your next tournament.", body: "Browse live and upcoming fishing events, view the details, and join when you are ready." };
  const hasActiveFilters = Boolean(query || country || region || tag || startFrom || startTo || joinedOnly);

  function handleClearFilters() {
    if (hasActiveFilters && !window.confirm("Clear all tournament filters?")) return;
    setStatusFilter("ALL");
    clearFilters();
  }

  function showAllTournaments() {
    setJoinedOnly(false);
    setStatusFilter("ALL");
  }

  function showScopedTournaments() {
    setJoinedOnly(true);
    setStatusFilter("ALL");
  }

  return (
    <section className={isCreatorPage ? "tournaments-page creator-tournaments-page" : "tournaments-page participant-tournaments-page"} id="tournaments">
      <section className="tournaments-hero">
        <div className="tournaments-hero-copy">
          <span className="eyebrow">{creatorCopy.eyebrow}</span>
          <h1>{creatorCopy.title}</h1>
          <p>{creatorCopy.body}</p>
        </div>
        {featured ? (
          <article className="tournament-feature-strip">
            <img src={tournamentImage(featured)} alt="" />
            <div className="feature-title-block">
              <span className={`tournament-status-badge ${getTournamentStatus(featured).toLowerCase()}`}>{isTournamentLive(featured) ? "Live now" : statusLabel(featured)}</span>
              <h2>{tournamentName(featured)}</h2>
              <small><MapPinIcon /> {tournamentLocation(featured)}</small>
            </div>
            <div className="feature-info-block">
              <strong>{formatRange(featured.startAt, featured.endAt)}</strong>
              <small>Tournament window</small>
            </div>
            <div className="feature-info-block">
              <strong>{featured.registrationCount}</strong>
              <small>{isCreatorPage ? "Teams enrolled" : "Anglers joined"}</small>
            </div>
            <div className="feature-strip-actions">
              <button className="clear-button compact" type="button" onClick={() => onDashboard(featured.id)}>View details</button>
              {isCreatorPage && canManageTournament(featured, user) ? (
                <button className="submit-link compact" type="button" onClick={() => onDashboard(featured.id)}>Manage</button>
              ) : (
                <TournamentJoinActions
                  onJoin={() => onJoin(featured)}
                  onSubmitCatch={onSubmitCatch}
                  registration={registrationByTournament.get(featured.id)}
                  tournament={featured}
                  user={user}
                />
              )}
            </div>
          </article>
        ) : null}
      </section>

      <section className="tournament-filter-panel" aria-label="Find and filter tournaments">
        <h2>Find tournaments</h2>

        <div className="tournament-filter-grid">
          <label className="field search-field">
            <span>Search</span>
            <input aria-label="Search tournaments" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tournaments, species, tags..." />
          </label>
          <OptionInputField dataListId="tournament-country-options" emptyOptionLabel="All countries" label="Country" options={countries} placeholder="All countries" value={country} onChange={setCountry} />
          <OptionInputField dataListId="tournament-region-options" emptyOptionLabel="All regions" label="Region" options={regions} placeholder="All regions" value={region} onChange={setRegion} />
          <label className="field">
            <span>Tag</span>
            <select aria-label="Filter by tag" value={tag} onChange={(event) => setTag(event.target.value)}>
              <option value="">All tags</option>
              {tags.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label className="field date-range-field">
            <span>Date range</span>
            <div className="date-range-inputs">
              <DateFilterField label="Start date" value={startFrom} onChange={setStartFrom} />
              <span aria-hidden="true">{"->"}</span>
              <DateFilterField label="End date" value={startTo} onChange={setStartTo} />
            </div>
          </label>
          <button className="clear-button tournament-clear-button" onClick={handleClearFilters} type="button"><SearchIcon /> Reset filters</button>
        </div>
      </section>

      <section className="tournament-results-bar">
        <strong>{loading ? "Loading tournaments" : `${sortedTournaments.length} tournaments`}</strong>
        <div className="tournament-results-controls">
          <div className="joined-filter-row">
            <button aria-pressed={!joinedOnly} className={!joinedOnly ? "selected" : ""} onClick={showAllTournaments} type="button">All tournaments</button>
            <button aria-pressed={joinedOnly} className={joinedOnly ? "selected" : ""} onClick={showScopedTournaments} type="button">{isCreatorPage ? "My tournaments" : "Joined only"}</button>
          </div>
          <div className="status-tabs tournament-status-tabs" aria-label="Tournament status">
            <button className={statusFilter === "LIVE" ? "selected" : ""} onClick={() => setStatusFilter("LIVE")} type="button">Live <span>{statusCounts.LIVE}</span></button>
            <button className={statusFilter === "UPCOMING" ? "selected" : ""} onClick={() => setStatusFilter("UPCOMING")} type="button">Upcoming <span>{statusCounts.UPCOMING}</span></button>
            {isCreatorPage ? <button className={statusFilter === "DRAFT" ? "selected" : ""} onClick={() => setStatusFilter("DRAFT")} type="button">Draft <span>{statusCounts.DRAFT}</span></button> : null}
            <button className={statusFilter === "ENDED" ? "selected" : ""} onClick={() => setStatusFilter("ENDED")} type="button">Ended <span>{statusCounts.ENDED}</span></button>
          </div>
        </div>
        <label className="sort-field">
          <span>Sort by</span>
          <select aria-label="Sort tournaments" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="ending">Ending soon</option>
            <option value="name">Name</option>
          </select>
        </label>
      </section>

      <section className="tournament-list tournament-grid" aria-live="polite">
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : joinedOnly && sortedTournaments.length === 0 ? (
          <EmptyCard
            title={isCreatorPage ? "No owned tournaments found" : "No joined tournaments yet"}
            body={isCreatorPage ? "Create a tournament or switch back to all tournaments." : "Join an open tournament and approved entries will appear here."}
          />
        ) : sortedTournaments.length === 0 ? (
          <EmptyCard title="No tournaments found" body={isCreatorPage ? "Create a tournament or adjust the filters to see your events here." : "Adjust the filters or check back when organizers publish new events."} />
        ) : (
          displayedTournaments.map((tournament, index) => {
            const manageable = isCreatorPage && canManageTournament(tournament, user);
            return (
            <article className="tournament-card tournament-design-card" key={tournament.id}>
              <div className="tournament-card-body">
                <div className="card-topline">
                  <span className={`tournament-status-badge ${getTournamentStatus(tournament).toLowerCase()}`}>{tournamentBadge(tournament)}</span>
                  <button className="card-menu-button" aria-label={`View details for ${tournamentName(tournament)}`} onClick={() => onDashboard(tournament.id)} type="button"><ArrowRightIcon /></button>
                </div>
                <div className="tournament-card-main">
                  <img src={tournamentImage(tournament, index)} alt="" />
                  <div>
                    <h2>
                      <button className="title-link" onClick={() => onDashboard(tournament.id)} type="button">
                        {tournamentName(tournament)}
                      </button>
                    </h2>
                    <div className="tag-row">
                      {(tournament.species.length ? tournament.species : tournament.tags).slice(0, 3).map((item) => <span key={item}>{item}</span>)}
                      {!tournament.tags.length && !tournament.species.length ? <span>Any species</span> : null}
                    </div>
                    <p>{stripHtml(tournament.description) || "No description has been added yet."}</p>
                  </div>
                </div>
                <div className="card-meta-grid">
                  <span><small>Window</small>{formatRange(tournament.startAt, tournament.endAt)}</span>
                  <span><small>Location</small>{tournamentLocation(tournament)}</span>
                </div>
                {manageable ? (
                  <div className="card-actions tournament-card-actions">
                    <button className="submit-link compact" type="button" onClick={() => onDashboard(tournament.id)}>Manage</button>
                    <button className="clear-button compact" type="button" onClick={() => onDashboard(tournament.id)}>View</button>
                  </div>
                ) : (
                  <div className="card-actions tournament-card-actions">
                    <button className="clear-button compact" type="button" onClick={() => onDashboard(tournament.id)}>View</button>
                    <TournamentJoinActions
                      onJoin={() => onJoin(tournament)}
                      onSubmitCatch={onSubmitCatch}
                      registration={registrationByTournament.get(tournament.id)}
                      tournament={tournament}
                      user={user}
                    />
                  </div>
                )}
              </div>
            </article>
          );
          })
        )}
      </section>
      {!loading && sortedTournaments.length > 10 ? (
        <div className="tournament-show-more-row">
          <span>Showing {Math.min(visibleCount, sortedTournaments.length)} of {sortedTournaments.length}</span>
          {hasMoreTournaments ? (
            <button className="clear-button compact" type="button" onClick={() => setVisibleCount((count) => Math.min(count + 10, sortedTournaments.length))}>Show more</button>
          ) : (
            <button className="clear-button compact" type="button" onClick={() => setVisibleCount(10)}>Show less</button>
          )}
        </div>
      ) : null}
    </section>
  );
}

function TournamentJoinActions({
  onJoin,
  onSubmitCatch,
  registration,
  tournament,
  user,
}: {
  onJoin: () => void;
  onSubmitCatch: () => void;
  registration?: RegistrationItem;
  tournament: Tournament;
  user: AuthUser | null;
}) {
  const ended = isTournamentEnded(tournament);
  const joinable = canJoinTournament(tournament, registration);
  if (registration?.status === "APPROVED") {
    return (
      <div className="card-actions">
        <span className="status-pill live">Joined</span>
        {isTournamentLive(tournament) ? <button className="submit-link compact" onClick={onSubmitCatch} type="button">Submit catch</button> : <span className="status-pill">{ended ? "Tournament ended" : "Waiting for start"}</span>}
      </div>
    );
  }
  if (registration) {
    return (
      <div className="card-actions">
        <span className="status-pill">{registration.status === "PENDING" ? "Pending approval" : registration.status}</span>
        {registration.paymentStatus === "REQUIRES_PAYMENT" ? <span className="status-pill">Payment unpaid</span> : null}
      </div>
    );
  }
  return (
    <div className="card-actions">
      {tournament.paymentRequired ? <span className="status-pill">Payment required</span> : null}
      {joinable ? (
        <button className="submit-link compact" onClick={onJoin} type="button">
          {user ? "Join tournament" : "Sign in to join"}
        </button>
      ) : (
        <span className="status-pill">{joinUnavailableLabel(tournament)}</span>
      )}
    </div>
  );
}

function AnnouncementsView({
  announcements,
  notifications,
  onReload,
  role,
  tournaments,
}: {
  announcements: AnnouncementItem[];
  notifications: NotificationItem[];
  onReload: () => Promise<void>;
  role: Role;
  tournaments: Tournament[];
}) {
  const [form, setForm] = useState({ tournamentId: tournaments[0]?.id ?? "", title: "", content: "" });
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const visibleAnnouncements: Array<AnnouncementItem | NotificationItem> = announcements.length
    ? announcements
    : notifications.filter((item) => item.kind === "ANNOUNCEMENT");

  useEffect(() => {
    setForm((current) => current.tournamentId ? current : { ...current, tournamentId: tournaments[0]?.id ?? "" });
  }, [tournaments]);

  function announcementContent(announcement: AnnouncementItem | NotificationItem) {
    return "content" in announcement ? announcement.content : announcement.body || "";
  }

  function announcementTournamentId(announcement: AnnouncementItem | NotificationItem) {
    return "tournamentId" in announcement ? announcement.tournamentId || "" : "";
  }

  async function saveAnnouncement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      if (editingId) {
        await updateAnnouncement(editingId, { title: form.title, content: form.content });
      } else {
        await createAnnouncement(form);
      }
      setForm({ tournamentId: form.tournamentId, title: "", content: "" });
      setEditingId("");
      await onReload();
      setNotice(editingId ? "Announcement updated." : "Announcement published.");
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeAnnouncement(id: string) {
    if (!window.confirm("Delete this announcement?")) return;
    setBusy(true);
    try {
      await deleteAnnouncement(id);
      await onReload();
    } finally {
      setBusy(false);
    }
  }

  async function readAnnouncement(id: string) {
    await markAnnouncementRead(id);
    await onReload();
  }

  return (
    <>
      <section className="page-heading">
        <h1>Announcements</h1>
        <p>{role === "creator" ? "Choose a tournament and prepare participant announcements." : "Tournament updates and in-app notifications will appear here."}</p>
      </section>
      {role === "creator" ? (
        <form className="form-card" onSubmit={saveAnnouncement}>
          <h2>New announcement</h2>
          {notice ? <div className="notice success">{notice}</div> : null}
          <TournamentSelect formTournamentId={form.tournamentId} onChange={(tournamentId) => setForm((current) => ({ ...current, tournamentId }))} tournaments={tournaments} />
          <div className="form-grid">
            <label className="field">
              <span>Title</span>
              <input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Weather update, rules reminder..." />
            </label>
          </div>
          <RichTextEditor value={form.content} onChange={(content) => setForm((current) => ({ ...current, content }))} />
          <div className="card-actions">
            <button className="submit-link form-submit" disabled={busy || !form.tournamentId || !form.title.trim() || !form.content.trim()} type="submit">
              {busy ? "Saving..." : editingId ? "Save announcement" : "Publish announcement"}
            </button>
            {editingId ? <button className="clear-button compact" type="button" onClick={() => { setEditingId(""); setForm({ tournamentId: form.tournamentId, title: "", content: "" }); }}>Cancel edit</button> : null}
          </div>
        </form>
      ) : null}
      {visibleAnnouncements.length === 0 ? (
        <EmptyCard title="No announcements yet" body="Organizer announcements for joined tournaments will appear here after login." />
      ) : (
        <section className="announcement-list">
          {visibleAnnouncements.map((announcement) => (
            <article className={announcement.readAt ? "announcement-card read" : "announcement-card"} key={announcement.id}>
              <div>
                <h2>{announcement.title}</h2>
                {announcement.tournamentName ? <span className="status-pill">{announcement.tournamentName}</span> : null}
                <div className="rich-content" dangerouslySetInnerHTML={{ __html: announcementContent(announcement) }} />
              </div>
              <time dateTime={announcement.createdAt}>{new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(announcement.createdAt))}</time>
              <div className="card-actions">
                {role === "creator" ? (
                  <>
                    <button className="clear-button compact" disabled={busy} type="button" onClick={() => {
                      setEditingId(announcement.id);
                      setForm({ tournamentId: announcementTournamentId(announcement), title: announcement.title, content: announcementContent(announcement) });
                    }}>Edit</button>
                    <button className="danger-button compact" disabled={busy} type="button" onClick={() => void removeAnnouncement(announcement.id)}>Delete</button>
                  </>
                ) : !announcement.readAt ? (
                  <button className="clear-button compact" type="button" onClick={() => void readAnnouncement(announcement.id)}>Mark read</button>
                ) : null}
              </div>
            </article>
          ))}
        </section>
      )}
    </>
  );
}

function RichTextEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.innerHTML === value) return;
    editor.innerHTML = value;
  }, [value]);

  function apply(command: "bold" | "italic" | "insertUnorderedList") {
    editorRef.current?.focus();
    document.execCommand(command);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }

  return (
    <div className="field rich-editor-field">
      <span>Message</span>
      <div className="rich-toolbar" aria-label="Announcement formatting">
        <button aria-label="Bold" className="clear-button compact icon-button" onMouseDown={(event) => event.preventDefault()} onClick={() => apply("bold")} type="button">B</button>
        <button aria-label="Italic" className="clear-button compact icon-button" onMouseDown={(event) => event.preventDefault()} onClick={() => apply("italic")} type="button">I</button>
        <button aria-label="Bulleted list" className="clear-button compact icon-button" onMouseDown={(event) => event.preventDefault()} onClick={() => apply("insertUnorderedList")} type="button">List</button>
      </div>
      <div
        aria-label="Announcement message"
        className="rich-editor"
        contentEditable
        ref={editorRef}
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
        role="textbox"
        suppressContentEditableWarning
        tabIndex={0}
      />
    </div>
  );
}

function MyCatchesView({
  loading,
  onReload,
  submissions,
}: {
  loading: boolean;
  onReload: () => Promise<void>;
  submissions: SubmissionItem[];
}) {
  const safeSubmissions = safeItems(submissions);
  const mapped = safeSubmissions.filter(hasCoordinates);
  const [selectedCatchId, setSelectedCatchId] = useState(mapped[0]?.id ?? "");
  const [selectedDetailId, setSelectedDetailId] = useState("");
  const [editing, setEditing] = useState<SubmissionItem | null>(null);
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [tournamentFilter, setTournamentFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("newest");
  const mapPanelRef = useRef<HTMLElement | null>(null);
  const detailPanelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!selectedCatchId && mapped[0]) setSelectedCatchId(mapped[0].id);
  }, [mapped, selectedCatchId]);

  useEffect(() => {
    if (selectedCatchId && !mapped.some((submission) => submission.id === selectedCatchId)) {
      setSelectedCatchId(mapped[0]?.id ?? "");
    }
  }, [mapped, selectedCatchId]);

  useEffect(() => {
    if (selectedDetailId && !safeSubmissions.some((submission) => submission.id === selectedDetailId)) {
      setSelectedDetailId("");
    }
  }, [safeSubmissions, selectedDetailId]);

  const tournaments = Array.from(new Set(safeSubmissions.map((submission) => submission.tournamentName).filter(Boolean))).sort();
  const filtered = safeSubmissions
    .filter((submission) => {
      const text = [submission.species, submission.tournamentName, submission.notes, submission.status].join(" ").toLowerCase();
      if (search.trim() && !text.includes(search.trim().toLowerCase())) return false;
      if (status !== "ALL" && submission.status !== status) return false;
      if (tournamentFilter !== "ALL" && submission.tournamentName !== tournamentFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === "oldest") return timestamp(a.createdAt) - timestamp(b.createdAt);
      if (sortOrder === "best") return (b.length || b.weight || 0) - (a.length || a.weight || 0);
      return timestamp(b.createdAt) - timestamp(a.createdAt);
    });
  const approved = safeSubmissions.filter((submission) => submission.status === "APPROVED");
  const pending = safeSubmissions.filter((submission) => submission.status === "PENDING");
  const draft = safeSubmissions.filter((submission) => submission.status === "DRAFT");
  const best = [...approved].sort((a, b) => (b.length || b.weight || 0) - (a.length || a.weight || 0))[0];
  const recent = [...safeSubmissions].sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt)).slice(0, 5);
  const spotCounts = Array.from(safeSubmissions.reduce((map, submission) => {
    const key = hasCoordinates(submission) ? coordinateLabel(submission, 2) : submission.tournamentName || "Unmapped";
    map.set(key, (map.get(key) ?? 0) + 1);
    return map;
  }, new Map<string, number>())).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const selectedDetail = selectedDetailId ? safeSubmissions.find((submission) => submission.id === selectedDetailId) ?? null : null;

  function clearCatchFilters() {
    setSearch("");
    setStatus("ALL");
    setTournamentFilter("ALL");
    setSortOrder("newest");
  }

  function viewCatchDetails(submission: SubmissionItem) {
    setSelectedDetailId(submission.id);
    if (hasCoordinates(submission)) setSelectedCatchId(submission.id);
    window.requestAnimationFrame(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function showCatchOnMap(submission: SubmissionItem) {
    if (!hasCoordinates(submission)) return;
    setSelectedCatchId(submission.id);
    window.requestAnimationFrame(() => {
      mapPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  async function saveCatchEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editing) return;
    setBusyId(editing.id);
    try {
      await updateSubmission(editing.id, editing);
      setEditing(null);
      await onReload();
    } finally {
      setBusyId("");
    }
  }

  async function withdrawCatch(submission: SubmissionItem) {
    if (!window.confirm(`Withdraw pending ${submission.species || "catch"}?`)) return;
    setBusyId(submission.id);
    try {
      await withdrawSubmission(submission.id);
      await onReload();
    } finally {
      setBusyId("");
    }
  }

  return (
    <section className="catches-workspace">
      <section className="page-heading catches-heading">
        <h1>My Catches</h1>
        <p>Track your submissions, explore your fishing spots, and view review status.</p>
      </section>
      <section className="catch-stat-grid">
        <CatchStatCard icon={<ClipboardIcon />} label="Total Catches" value={String(safeSubmissions.length)} hint="All time" />
        <CatchStatCard icon={<BellIcon />} label="Pending Review" value={String(pending.length)} hint="Awaiting approval" tone="warning" />
        <CatchStatCard icon={<TrophyIcon />} label="Approved" value={String(approved.length)} hint="Verified catches" tone="success" />
        <CatchStatCard icon={<TrophyIcon />} label="Best Catch" value={best ? `${best.length || "-"} cm / ${best.weight || "-"} kg` : "-"} hint={best ? `${best.species || "Catch"} - ${formatDate(best.createdAt)}` : "No approved catches"} tone="blue" />
      </section>
      <div className="catch-layout-grid">
        <section className="catch-main-column">
          <section className="catch-map-panel" ref={mapPanelRef}>
            <div className="panel-title-row">
              <h2>Catch Activity Map</h2>
              <small>{mapped.length} mapped catches</small>
            </div>
            {mapped.length ? (
              <CatchesMap selectedId={selectedCatchId} submissions={mapped} />
            ) : (
              <EmptyCard title="No GPS locations saved yet" body="Submit a catch with location enabled to see it on the map." />
            )}
          </section>
          <section className="catch-filter-bar">
            <label>
              <span>Search</span>
              <input aria-label="Search catches" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search catches..." />
            </label>
            <label>
              <span>Status</span>
              <select aria-label="Filter catches by status" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="ALL">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="DRAFT">Draft</option>
              </select>
            </label>
            <label>
              <span>Tournament</span>
              <select aria-label="Filter catches by tournament" value={tournamentFilter} onChange={(event) => setTournamentFilter(event.target.value)}>
                <option value="ALL">All</option>
                {tournaments.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
            <label>
              <span>Date</span>
              <select aria-label="Sort catches" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="best">Best catch</option>
              </select>
            </label>
            <button className="clear-button compact catch-clear-button" type="button" onClick={clearCatchFilters}>Clear filters</button>
          </section>
          {selectedDetail ? (
            <section className="catch-detail-panel" ref={detailPanelRef} aria-live="polite">
              <div className="catch-detail-head">
                <div>
                  <span className={`review-status-badge ${submissionStatusClass(selectedDetail.status)}`}>{submissionStatusLabel(selectedDetail.status)}</span>
                  <h2>{selectedDetail.species || "Catch details"}</h2>
                  <p>{selectedDetail.tournamentName || "Tournament"}</p>
                </div>
                <button className="clear-button compact" type="button" onClick={() => setSelectedDetailId("")}>Close</button>
              </div>
              <div className="catch-detail-grid">
                <span><small>Length</small><strong>{selectedDetail.length || "-"} cm</strong></span>
                <span><small>Weight</small><strong>{selectedDetail.weight || "-"} kg</strong></span>
                <span><small>Submitted</small><strong>{formatDateTime(selectedDetail.createdAt)}</strong></span>
                <span><small>Location</small><strong>{coordinateLabel(selectedDetail)}</strong></span>
              </div>
              {selectedDetail.notes ? <p className="catch-detail-note">{selectedDetail.notes}</p> : null}
              {selectedDetail.reviewNotes ? <p className="catch-detail-note"><strong>Organizer note:</strong> {selectedDetail.reviewNotes}</p> : null}
              <div className="catch-detail-actions">
                {safeMediaUrl(selectedDetail.photoUrl) ? <a className="clear-button compact" href={safeMediaUrl(selectedDetail.photoUrl)} target="_blank" rel="noreferrer"><CameraIcon /> View photo</a> : null}
                {safeMediaUrl(selectedDetail.videoUrl) ? <a className="clear-button compact" href={safeMediaUrl(selectedDetail.videoUrl)} target="_blank" rel="noreferrer"><CameraIcon /> Watch video</a> : null}
                {hasCoordinates(selectedDetail) ? <button className="submit-link compact" type="button" onClick={() => showCatchOnMap(selectedDetail)}><MapPinIcon /> Show on map</button> : null}
              </div>
            </section>
          ) : null}
          <section className="catch-table-list">
            {loading ? <Skeleton /> : null}
            {!loading && safeSubmissions.length === 0 ? <EmptyCard title="No catches submitted yet" body="Your submitted catches will appear here after you send the first one." /> : null}
            {filtered.map((submission, index) => (
              <ParticipantCatchRow
                busy={busyId === submission.id}
                key={submission.id || `catch-${index}`}
                onEdit={() => setEditing(submission)}
                onShowMap={() => showCatchOnMap(submission)}
                onViewDetails={() => viewCatchDetails(submission)}
                onWithdraw={() => void withdrawCatch(submission)}
                submission={submission}
              />
            ))}
          </section>
        </section>
        <aside className="catch-side-column">
          <section className="catch-side-panel">
            <h2>Catches by Status</h2>
            <StatusDonut items={[
              { label: "Approved", value: approved.length, color: "#34d399" },
              { label: "Pending", value: pending.length, color: "#fbbf24" },
              { label: "Draft", value: draft.length, color: "#94a3b8" },
            ]} total={safeSubmissions.length} />
          </section>
          <section className="catch-side-panel">
            <PanelTitle title="Recent Activity" />
            <div className="activity-list">
              {recent.map((submission, index) => <ActivityItem key={submission.id || `activity-${index}`} submission={submission} />)}
              {!recent.length ? <p className="design-empty">No activity yet.</p> : null}
            </div>
          </section>
          <section className="catch-side-panel">
            <PanelTitle title="Top Fishing Spots" />
            <div className="spot-list">
              {spotCounts.map(([spot, count]) => <span key={spot}><MapPinIcon /> {spot}<strong>{count} catches</strong></span>)}
              {!spotCounts.length ? <p className="design-empty">No mapped locations yet.</p> : null}
            </div>
          </section>
        </aside>
      </div>
      {editing ? (
        <form className="form-card compact-form" onSubmit={saveCatchEdit}>
          <h2>Edit pending catch</h2>
          <div className="form-grid">
            <label className="field">
              <span>Species</span>
              <input required value={editing.species} onChange={(event) => setEditing({ ...editing, species: event.target.value })} />
            </label>
            <label className="field">
              <span>Length (cm)</span>
              <input inputMode="decimal" value={editing.length || ""} onChange={(event) => setEditing({ ...editing, length: Number(event.target.value || 0) })} />
            </label>
            <label className="field">
              <span>Weight (kg)</span>
              <input inputMode="decimal" value={editing.weight || ""} onChange={(event) => setEditing({ ...editing, weight: Number(event.target.value || 0) })} />
            </label>
            <label className="field">
              <span>Photo URL</span>
              <input value={editing.photoUrl || ""} onChange={(event) => setEditing({ ...editing, photoUrl: event.target.value })} />
            </label>
          </div>
          <label className="field">
            <span>Notes</span>
            <textarea rows={3} value={editing.notes || ""} onChange={(event) => setEditing({ ...editing, notes: event.target.value })} />
          </label>
          <div className="card-actions">
            <button className="submit-link compact" disabled={busyId === editing.id} type="submit">Save catch</button>
            <button className="clear-button compact" type="button" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function PendingSubmissionsView({
  onCatchReviewed,
  onJoinReviewed,
  onReload,
  registrations,
  submissions,
  tournaments,
}: {
  onCatchReviewed: (submission: SubmissionItem) => void;
  onJoinReviewed: (registration: RegistrationItem) => void;
  onReload: () => Promise<void>;
  registrations: RegistrationItem[];
  submissions: SubmissionItem[];
  tournaments: Tournament[];
}) {
  const safeSubmissions = safeItems(submissions);
  const safeRegistrations = safeItems(registrations);
  const safeTournaments = safeItems(tournaments);
  const [filter, setFilter] = useState("");
  const [queue, setQueue] = useState<"catches" | "joins">("catches");
  const [busyId, setBusyId] = useState("");
  const [reviewNotice, setReviewNotice] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [speciesFilter, setSpeciesFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("newest");
  const pending = safeSubmissions.filter((submission) => submission.status === "PENDING");
  const reviewedToday = safeSubmissions.filter((submission) => submission.status === "APPROVED" && isToday(submission.createdAt));
  const needsAttention = pending.filter((submission) => {
    const tournament = safeTournaments.find((item) => item.id === submission.tournamentId);
    const metric = tournament?.scoringMetric || "LENGTH";
    return ((metric === "LENGTH" || metric === "LENGTH_AND_WEIGHT") && !submission.length) || ((metric === "WEIGHT" || metric === "LENGTH_AND_WEIGHT") && !submission.weight);
  });
  const species = Array.from(new Set(safeSubmissions.map((submission) => submission.species).filter(Boolean))).sort();
  const filtered = safeSubmissions
    .filter((submission) => {
      if (filter && submission.tournamentId !== filter) return false;
      if (statusFilter !== "ALL" && submission.status !== statusFilter) return false;
      if (speciesFilter !== "ALL" && submission.species !== speciesFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === "oldest") return timestamp(a.createdAt) - timestamp(b.createdAt);
      if (sortOrder === "species") return (a.species || "").localeCompare(b.species || "");
      return timestamp(b.createdAt) - timestamp(a.createdAt);
    });
  const pendingRegistrations = safeRegistrations.filter((registration) => registration.status === "PENDING");
  const filteredRegistrations = filter ? pendingRegistrations.filter((registration) => registration.tournamentId === filter) : pendingRegistrations;
  const recentDecisions = safeSubmissions.filter((submission) => submission.status !== "PENDING").slice(0, 4);
  const reviewFiltersChanged = Boolean(filter || statusFilter !== "PENDING" || speciesFilter !== "ALL");

  async function reviewCatch(id: string, status: "APPROVED" | "REJECTED") {
    const action = status === "APPROVED" ? "approve this catch" : "decline this catch";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    const reviewNotes = window.prompt("Add an optional organizer comment for the participant:", status === "APPROVED" ? "Approved by organizer." : "Declined by organizer.") ?? "";
    setBusyId(id);
    setReviewNotice(null);
    try {
      const result = await reviewSubmission(id, { status, reviewNotes });
      await onReload();
      onCatchReviewed(result.submission);
      setReviewNotice({ type: "success", text: status === "APPROVED" ? "Catch submission approved." : "Catch submission declined." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not review this catch.";
      setReviewNotice({ type: "error", text: message });
      window.alert(message);
    } finally {
      setBusyId("");
    }
  }

  async function reviewJoin(id: string, status: "PENDING" | "APPROVED" | "DECLINED", paymentStatus?: string) {
    const action = paymentStatus === "PAID" ? "mark this registration paid" : status === "APPROVED" ? "approve this join request" : "decline this join request";
    if (!window.confirm(`Are you sure you want to ${action}?`)) return;
    setBusyId(id);
    setReviewNotice(null);
    try {
      const result = await reviewRegistration(id, { status, paymentStatus });
      await onReload();
      onJoinReviewed(result.registration);
      setReviewNotice({
        type: "success",
        text: paymentStatus === "PAID" ? "Join request marked as paid." : status === "APPROVED" ? "Join request approved." : "Join request declined.",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not review this join request.";
      setReviewNotice({ type: "error", text: message });
      window.alert(message);
    } finally {
      setBusyId("");
    }
  }

  function clearReviewFilters() {
    if (reviewFiltersChanged && !window.confirm("Clear all review filters?")) return;
    setFilter("");
    setStatusFilter("PENDING");
    setSpeciesFilter("ALL");
  }

  return (
    <section className="review-workspace">
      <section className="page-heading catches-heading">
        <h1>Review Submissions</h1>
        <p>Approve catch submissions and manage participant join requests.</p>
      </section>
      {reviewNotice ? <div className={`notice ${reviewNotice.type}`}>{reviewNotice.text}</div> : null}
      <section className="catch-stat-grid review-stat-grid">
        <CatchStatCard icon={<ClipboardIcon />} label="Pending Catch Reviews" value={String(pending.length)} hint="Review queue" />
        <CatchStatCard icon={<UploadIcon />} label="Needs Attention" value={String(needsAttention.length)} hint="Missing info or flagged" tone="warning" />
        <CatchStatCard icon={<TrophyIcon />} label="Approved Today" value={String(reviewedToday.length)} hint="Total catches approved" tone="success" />
        <CatchStatCard icon={<PlusIcon />} label="Join Requests" value={String(pendingRegistrations.length)} hint="Waiting for approval" tone="blue" />
      </section>
      <div className="review-layout-grid">
        <section className="review-main-panel">
          <div className="review-tabs" aria-label="Review queue type">
            <button className={queue === "catches" ? "selected" : ""} onClick={() => setQueue("catches")} type="button">
              <span className="review-tab-icon"><FishIcon /></span>
              <span className="review-tab-copy">
                <strong>Catch submissions</strong>
                <small><span>View and manage</span><span>catch submissions</span></small>
              </span>
              <span className="review-tab-chevron" aria-hidden="true"><ArrowRightIcon /></span>
            </button>
            <button className={queue === "joins" ? "selected" : ""} onClick={() => setQueue("joins")} type="button">
              <span className="review-tab-icon"><PlusIcon /></span>
              <span className="review-tab-copy">
                <strong>Join requests</strong>
                <small><span>View and manage</span><span>join requests</span></small>
              </span>
              <span className="review-tab-chevron" aria-hidden="true"><ArrowRightIcon /></span>
            </button>
          </div>
          <div className="review-filter-grid">
            <label className="field review-select-field">
              <span>Tournament</span>
              <div className="review-select-control">
                <span className="review-select-icon" aria-hidden="true"><TrophyIcon /></span>
                <select disabled={safeTournaments.length === 0} value={filter} onChange={(event) => setFilter(event.target.value)}>
                  <option value="">All tournaments</option>
                  {safeTournaments.length === 0 ? <option value="">No tournaments available</option> : null}
                  {safeTournaments.map((tournament) => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournamentName(tournament)}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            <label className="field review-select-field">
              <span>Status</span>
              <div className="review-select-control">
                <span className="review-select-icon" aria-hidden="true"><ClockIcon /></span>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="ALL">All</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </label>
            <label className="field review-select-field">
              <span>Species</span>
              <div className="review-select-control">
                <span className="review-select-icon" aria-hidden="true"><FishLineIcon /></span>
                <select value={speciesFilter} onChange={(event) => setSpeciesFilter(event.target.value)}>
                  <option value="ALL">All species</option>
                  {species.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
            </label>
            <label className="field review-select-field">
              <span>Sort by</span>
              <div className="review-select-control">
                <span className="review-select-icon" aria-hidden="true"><SortIcon /></span>
                <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="species">Species</option>
                </select>
              </div>
            </label>
          </div>
          <div className="queue-toolbar">
            <strong>{queue === "catches" ? `${filtered.length} catch submissions` : `${filteredRegistrations.length} join requests`}</strong>
            <button className="text-action clear-review-filters" type="button" onClick={clearReviewFilters}>Clear all filters</button>
          </div>
          <section className="review-table-list">
            {queue === "catches" && filtered.length === 0 ? <EmptyCard title="No matching catches" body="New catch submissions will appear here after approved participants submit catches." /> : null}
            {queue === "catches" ? filtered.map((submission, index) => (
              <ReviewSubmissionRow
                busy={busyId === submission.id}
                key={submission.id || `submission-${index}`}
                onApprove={() => void reviewCatch(submission.id, "APPROVED")}
                onDecline={() => void reviewCatch(submission.id, "REJECTED")}
                submission={submission}
                tournaments={safeTournaments}
              />
            )) : null}
            {queue === "joins" && filteredRegistrations.length === 0 ? <EmptyCard title="No pending join requests" body="Paid registrations and approval-required joins will appear here." /> : null}
            {queue === "joins" ? filteredRegistrations.map((registration, index) => (
              <article className="review-submission-row join-row" key={registration.id || `registration-${index}`}>
                <div className="catch-thumb"><TrophyIcon /></div>
                <div className="review-submission-title">
                  <strong>{registration.name || registration.email}</strong>
                  <small>{registration.tournamentName || "Tournament"}</small>
                  <small>{registration.email}</small>
                </div>
                <MiniMetric label="Payment" value={(registration.paymentStatus || "PENDING").replace("_", " ")} />
                <span className="review-status-badge pending">{registration.status}</span>
                <div className="review-actions">
                  {registration.paymentStatus === "REQUIRES_PAYMENT" ? (
                    <button className="clear-button compact" disabled={busyId === registration.id} onClick={() => void reviewJoin(registration.id, "PENDING", "PAID")} type="button">Mark paid</button>
                  ) : null}
                  <button className="submit-link compact" disabled={busyId === registration.id} onClick={() => void reviewJoin(registration.id, "APPROVED")} type="button">Approve</button>
                  <button className="clear-button compact" disabled={busyId === registration.id} onClick={() => void reviewJoin(registration.id, "DECLINED")} type="button">Decline</button>
                </div>
              </article>
            )) : null}
          </section>
        </section>
        <aside className="catch-side-column">
          <section className="catch-side-panel">
            <h2>Review Queue Summary</h2>
            <StatusDonut items={[
              { label: "Pending", value: pending.length, color: "#22d3ee" },
              { label: "Needs Info", value: needsAttention.length, color: "#ef4444" },
              { label: "Older than 48h", value: pending.filter((item) => {
                const createdAt = timestamp(item.createdAt);
                return createdAt > 0 && Date.now() - createdAt > 172800000;
              }).length, color: "#f59e0b" },
              { label: "Join Requests", value: pendingRegistrations.length, color: "#22c55e" },
            ]} total={pending.length + pendingRegistrations.length} />
          </section>
          <section className="catch-side-panel guidelines-panel">
            <h2>Review Guidelines</h2>
            <p>Ensure each submission meets all tournament rules.</p>
            <span>Photo is clear and unedited</span>
            <span>Length and weight are provided</span>
            <span>Catch location matches tournament rules</span>
            <span>Species is valid for this tournament</span>
          </section>
          <section className="catch-side-panel">
            <PanelTitle title="Recent Decisions" />
            <div className="activity-list">
              {recentDecisions.map((submission, index) => <ActivityItem key={submission.id || `decision-${index}`} submission={submission} />)}
              {!recentDecisions.length ? <p className="design-empty">No decisions yet.</p> : null}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function CatchStatCard({ hint, icon, label, tone, value }: { hint: string; icon: ReactNode; label: string; tone?: "warning" | "success" | "blue"; value: string }) {
  return (
    <article className="catch-stat-card">
      <span className={tone ? `catch-stat-icon ${tone}` : "catch-stat-icon"}>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{hint}</p>
      </div>
    </article>
  );
}

function ParticipantCatchRow({
  busy,
  onEdit,
  onShowMap,
  onViewDetails,
  onWithdraw,
  submission,
}: {
  busy: boolean;
  onEdit: () => void;
  onShowMap: () => void;
  onViewDetails: () => void;
  onWithdraw: () => void;
  submission: SubmissionItem;
}) {
  const photoUrl = safeMediaUrl(submission.photoUrl);
  return (
    <article className="participant-catch-row">
      {photoUrl ? <img alt="" src={photoUrl} /> : <div className="catch-thumb"><FishIcon /></div>}
      <div className="catch-row-title">
        <strong>{submission.species || "Catch"}</strong>
        <small>{submission.tournamentName || "Tournament"}</small>
        <small><MapPinIcon /> {coordinateLabel(submission)}</small>
      </div>
      <MiniMetric label="Length" value={`${submission.length || "-"} cm`} />
      <MiniMetric label="Weight" value={`${submission.weight || "-"} kg`} />
      <MiniMetric label="Submitted" value={formatDateTime(submission.createdAt)} />
      <span className={`review-status-badge ${submissionStatusClass(submission.status)}`}>{submissionStatusLabel(submission.status)}</span>
      <div className="catch-row-actions">
        {submission.status === "PENDING" ? (
          <>
            <button className="clear-button compact" disabled={busy} type="button" onClick={onEdit}>Edit</button>
            <button className="danger-button compact" disabled={busy} type="button" onClick={onWithdraw}>Withdraw</button>
          </>
        ) : (
          <button className="clear-button compact" type="button" onClick={onViewDetails}>View details</button>
        )}
        {hasCoordinates(submission) ? <button className="text-action" type="button" onClick={onShowMap}><MapPinIcon /> Show on map</button> : null}
      </div>
    </article>
  );
}

function ReviewSubmissionRow({
  busy,
  onApprove,
  onDecline,
  submission,
  tournaments,
}: {
  busy: boolean;
  onApprove: () => void;
  onDecline: () => void;
  submission: SubmissionItem;
  tournaments: Tournament[];
}) {
  const status = submissionStatusLabel(submission.status);
  const tournament = tournaments.find((item) => item.id === submission.tournamentId);
  const metric = tournament?.scoringMetric || "LENGTH";
  const missingLength = (metric === "LENGTH" || metric === "LENGTH_AND_WEIGHT") && !submission.length;
  const missingWeight = (metric === "WEIGHT" || metric === "LENGTH_AND_WEIGHT") && !submission.weight;
  const hasMissingMeasurements = missingLength || missingWeight;
  const statusClass = hasMissingMeasurements ? "needs-info" : submissionStatusClass(submission.status);
  const photoUrl = safeMediaUrl(submission.photoUrl);
  const videoUrl = safeMediaUrl(submission.videoUrl);
  return (
    <article className="review-submission-row">
      {photoUrl ? <img alt="" src={photoUrl} /> : <div className="catch-thumb"><FishIcon /></div>}
      <div className="review-submission-title">
        <strong>{submission.species || "Catch"}</strong>
        <small>{submission.tournamentName || "Tournament"}</small>
        <small><MapPinIcon /> {coordinateLabel(submission)}</small>
      </div>
      <MiniMetric label="Participant" value={submission.participantName || "Unknown"} />
      <MiniMetric label="Submitted" value={formatDateTime(submission.createdAt)} />
      <MiniMetric label="Length" value={`${submission.length || "-"} cm`} />
      <MiniMetric label="Weight" value={`${submission.weight || "-"} kg`} />
      <div>
        <span className={`review-status-badge ${statusClass}`}>{hasMissingMeasurements ? "Needs info" : status}</span>
        {hasMissingMeasurements ? <p className="inline-warning">Missing {missingLength && missingWeight ? "length and weight" : missingLength ? "length" : "weight"}.</p> : null}
      </div>
      <div className="review-actions">
        <button className="submit-link compact" disabled={busy || hasMissingMeasurements || status !== "PENDING"} onClick={onApprove} type="button">Accept</button>
        <button className="clear-button compact" disabled={busy || status !== "PENDING"} onClick={onDecline} type="button">Decline</button>
        {photoUrl ? <a className="text-action" href={photoUrl} target="_blank" rel="noreferrer">View photo</a> : null}
        {videoUrl ? <a className="text-action" href={videoUrl} target="_blank" rel="noreferrer">Watch video</a> : null}
      </div>
    </article>
  );
}

function StatusDonut({ items, total }: { items: Array<{ color: string; label: string; value: number }>; total: number }) {
  const safeTotal = Math.max(total, 1);
  let cursor = 0;
  const gradient = items.map((item) => {
    const start = cursor;
    cursor += (item.value / safeTotal) * 100;
    return `${item.color} ${start}% ${cursor}%`;
  }).join(", ");
  return (
    <div className="status-donut-layout">
      <span className="status-donut" style={{ background: `conic-gradient(${gradient || "#1f3c4c 0% 100%"})` }} />
      <div className="donut-legend">
        {items.map((item) => (
          <span key={item.label}><i style={{ background: item.color }} />{item.label}<strong>{item.value} ({Math.round((item.value / safeTotal) * 100)}%)</strong></span>
        ))}
      </div>
    </div>
  );
}

function submissionStatusLabel(status: string | undefined) {
  return status || "PENDING";
}

function submissionStatusClass(status: string | undefined) {
  return submissionStatusLabel(status).toLowerCase();
}

function ActivityItem({ submission }: { submission: SubmissionItem }) {
  const status = submissionStatusClass(submission.status);
  return (
    <article className="activity-item">
      <span className={`activity-dot ${status}`}>{status === "approved" ? <TrophyIcon /> : status === "pending" ? <BellIcon /> : <ClipboardIcon />}</span>
      <div>
        <strong>{submission.species || "Catch"} {submission.status === "APPROVED" ? "approved" : submission.status === "PENDING" ? "submission" : submissionStatusClass(submission.status)}</strong>
        <small>{submission.tournamentName || "Tournament"}</small>
      </div>
      <time>{relativeTime(submission.createdAt)}</time>
    </article>
  );
}

function validDate(value: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timestamp(value: string) {
  return validDate(value)?.getTime() ?? 0;
}

function formatDateTime(value: string) {
  const date = validDate(value);
  if (!date) return "-";
  return formatDateWithOptions(date, dateTimeFormatOptions(), true);
}

function isToday(value: string) {
  const date = validDate(value);
  if (!date) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
}

function relativeTime(value: string) {
  const date = validDate(value);
  if (!date) return "";
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(1, Math.round(diff / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function CatchMapPreview({
  lat,
  lng,
  onMove,
}: {
  lat: number | null;
  lng: number | null;
  onMove: (lat: number, lng: number) => void;
}) {
  const position: [number, number] = lat && lng ? [lat, lng] : [56.9496, 24.1052];

  return (
    <div className="map-shell">
      <MapContainer center={position} zoom={lat && lng ? 14 : 6} scrollWheelZoom className="leaflet-map">
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {lat && lng ? (
          <Marker
            draggable
            eventHandlers={{ dragend: (event) => {
              const marker = event.target as L.Marker;
              const next = marker.getLatLng();
              onMove(next.lat, next.lng);
            } }}
            icon={reelmasterMarker}
            position={position}
          >
            <Popup>Catch location. Drag marker to adjust.</Popup>
          </Marker>
        ) : null}
        <MapClickHandler onMove={onMove} />
      </MapContainer>
      {!lat || !lng ? <div className="map-empty">Capture GPS to place a marker, or click the map to set it manually.</div> : null}
    </div>
  );
}

function CatchesMap({ selectedId, submissions }: { selectedId: string; submissions: SubmissionItem[] }) {
  const mappedSubmissions = submissions.filter(hasCoordinates);
  const first = mappedSubmissions[0];
  const selected = mappedSubmissions.find((submission) => submission.id === selectedId) ?? first;
  const center = first ? coordinatePair(first) : null;
  const [layer, setLayer] = useState<"street" | "satellite" | "terrain">("street");
  const tileUrl = layer === "satellite"
    ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    : layer === "terrain"
      ? "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attribution = layer === "satellite" ? "Imagery &copy; Esri" : layer === "terrain" ? "Terrain &copy; OpenTopoMap" : "&copy; OpenStreetMap";

  if (!first || !selected || !center) {
    return <EmptyCard title="Map unavailable" body="Saved catches do not have valid GPS coordinates yet." />;
  }

  const selectedPosition = coordinatePair(selected) ?? center;

  return (
    <div className="map-shell">
      <div className="map-layer-toggle" aria-label="Map layer">
        <button className={layer === "street" ? "selected" : ""} onClick={() => setLayer("street")} type="button">Street</button>
        <button className={layer === "satellite" ? "selected" : ""} onClick={() => setLayer("satellite")} type="button">Satellite</button>
        <button className={layer === "terrain" ? "selected" : ""} onClick={() => setLayer("terrain")} type="button">Terrain</button>
      </div>
      <MapContainer center={center} zoom={10} scrollWheelZoom className="leaflet-map">
        <TileLayer attribution={attribution} url={tileUrl} />
        <MapFocus lat={selectedPosition[0]} lng={selectedPosition[1]} />
        {mappedSubmissions.map((submission, index) => {
          const photoUrl = safeMediaUrl(submission.photoUrl);
          const position = coordinatePair(submission);
          if (!position) return null;
          return (
            <Marker icon={submission.id === selected.id ? highlightedMarker : reelmasterMarker} key={submission.id || `marker-${index}`} position={position}>
              <Popup>
                <strong>{submission.species || "Catch"}</strong>
                <br />
                {submission.length || "-"} cm / {submission.weight || "-"} kg
                <br />
                {submission.tournamentName}
                <br />
                {formatDateTime(submission.createdAt)}
                {photoUrl ? <img alt="" className="popup-thumb" src={photoUrl} /> : null}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function MapFocus({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], Math.max(map.getZoom(), 14), { animate: true });
  }, [lat, lng, map]);
  return null;
}

function MapClickHandler({ onMove }: { onMove: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(event) {
      onMove(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function AccountView({ user }: { user: AuthUser | null }) {
  return (
    <>
      <section className="page-heading">
        <h1>Account</h1>
        <p>Your current Reelmaster session.</p>
      </section>
      <article className="empty-card">
        <h2>{user?.displayName || user?.email || "Account"}</h2>
        <p>{user ? `${user.email} - ${user.role}` : "No active session."}</p>
      </article>
    </>
  );
}

function SubscriptionView({ currentPlan, onSelectPlan }: { currentPlan: AuthUser["subscriptionTier"]; onSelectPlan: (tier: SubscriptionTier) => void }) {
  const plans: Array<{
    badge?: string;
    description: string;
    features: string[];
    icon: ReactNode;
    name: string;
    tier: SubscriptionTier;
  }> = [
    {
      description: "For anglers and small local events.",
      features: ["Browse and join tournaments", "Submit catches", "Create tournaments up to 5 participants", "Basic leaderboard access"],
      icon: <FishIcon />,
      name: "Free",
      tier: "FREE",
    },
    {
      badge: "Most popular",
      description: "For organizers running regular tournaments.",
      features: ["Create larger tournaments", "Manage registrations and reviews", "Announcements and Q&A tools", "Results report export", "Priority support"],
      icon: <TrophyIcon />,
      name: "Pro",
      tier: "PRO",
    },
    {
      description: "For clubs that need long-term catch history.",
      features: ["Everything in Pro", "Store past catches after events end", "Archive reports and historical leaderboards", "Club-ready record keeping"],
      icon: <ShieldIcon />,
      name: "Pro Archive",
      tier: "PRO_ARCHIVE",
    },
  ];

  return (
    <section className="subscription-page">
      <div className="subscription-heading">
        <span className="eyebrow">Subscription options</span>
        <h1>Start free. Upgrade when your events grow.</h1>
        <p>Pick the plan that matches how you fish, host, and preserve tournament history.</p>
      </div>
      <div className="subscription-grid">
        {plans.map((plan) => {
          const selected = currentPlan === plan.tier || (!currentPlan && plan.tier === "FREE");
          const planMeta = subscriptionPlans[plan.tier];
          return (
            <article className={`subscription-card ${selected ? "selected" : ""}`} key={plan.tier}>
              {plan.badge ? <span className="subscription-badge"><CheckIcon /> {plan.badge}</span> : null}
              <span className="subscription-icon">{plan.icon}</span>
              <h2>{plan.name}</h2>
              <strong>{planMeta.price}<small>{plan.tier === "FREE" ? " forever" : " / month"}</small></strong>
              <p>{plan.description}</p>
              <ul>
                {plan.features.map((feature) => <li key={feature}><CheckIcon /> {feature}</li>)}
              </ul>
              <button className={selected ? "outline-action" : "submit-link"} type="button" onClick={() => {
                if (selected) return;
                if (confirmSubscriptionSwitch(plan.tier, currentPlan)) onSelectPlan(plan.tier);
              }}>
                {selected ? "Current plan" : plan.tier === "FREE" ? "Switch to Free" : "Upgrade plan"} <ArrowRightIcon />
              </button>
            </article>
          );
        })}
      </div>
      <div className="subscription-assurance">
        <span><ArrowRightIcon /> Cancel anytime</span>
        <span><CalendarIcon /> Billed monthly</span>
        <span><ShieldIcon /> Plan saved to your account</span>
      </div>
    </section>
  );
}

function SettingsView({ user, onLogout, onThemeChange, onUserUpdate }: { user: AuthUser | null; onLogout: () => void; onThemeChange: (theme: string) => void; onUserUpdate: (user: AuthUser) => void }) {
  type SettingsDraft = {
    displayName: string;
    phone: string;
    teamName: string;
    measurement: string;
    countryPreference: string;
    dateFormat: string;
    catchGpsPreference: string;
    emailAlerts: boolean;
    submissionAlerts: boolean;
    reviewReminders: boolean;
    gpsDefault: boolean;
    theme: string;
    language: string;
    timeZone: string;
    twoFactor: boolean;
  };

  function readBoolean(key: string, fallback: boolean) {
    const value = readLocalStorage(key);
    return value === "" ? fallback : value === "true";
  }

  function readDraft(currentUser: AuthUser | null): SettingsDraft {
    return {
      displayName: currentUser?.displayName || currentUser?.email?.split("@")[0] || "",
      phone: readLocalStorage("profilePhone"),
      teamName: readLocalStorage("profileTeamName"),
      measurement: readLocalStorage("measurementSystem", defaultMeasurementPreference()),
      countryPreference: readLocalStorage("countryPreference", defaultCountryPreference()),
      dateFormat: readLocalStorage("dateFormat", defaultDateFormatPreference()),
      catchGpsPreference: readLocalStorage("catchGpsPreference", "ask"),
      emailAlerts: readBoolean("emailAlerts", true),
      submissionAlerts: readBoolean("submissionAlerts", true),
      reviewReminders: readBoolean("reviewReminders", true),
      gpsDefault: readBoolean("gpsDefault", true),
      theme: storedThemePreference(),
      language: readLocalStorage("languagePreference", "English"),
      timeZone: readLocalStorage("timeZonePreference", defaultTimeZonePreference()),
      twoFactor: readBoolean("twoFactorEnabled", false),
    };
  }

  const [draft, setDraft] = useState<SettingsDraft>(() => readDraft(user));
  const [savedDraft, setSavedDraft] = useState<SettingsDraft>(() => readDraft(user));
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [subscriptionSaving, setSubscriptionSaving] = useState<NonNullable<AuthUser["subscriptionTier"]> | null>(null);
  const [memberSince] = useState(() => {
    const stored = readLocalStorage("memberSince");
    if (stored) return stored;
    const created = new Date().toISOString();
    writeLocalStorage("memberSince", created);
    return created;
  });

  useEffect(() => {
    const nextDraft = readDraft(user);
    setDraft(nextDraft);
    setSavedDraft(nextDraft);
  }, [user?.id, user?.displayName, user?.email]);

  const hasChanges = useMemo(() => JSON.stringify(draft) !== JSON.stringify(savedDraft), [draft, savedDraft]);
  const initials = (draft.displayName || user?.email || "A").trim().slice(0, 1).toUpperCase();
  const roleLabel = user?.role === "ADMIN" ? "Admin" : user?.role === "ORGANIZER" ? "Organizer" : user?.role === "ANGLER" ? "Participant" : "Guest";
  const currentPlan = subscriptionTierForUser(user);
  const planLabel = subscriptionPlans[currentPlan].name;
  const settingsPlanOptions: Array<{ icon: ReactNode; tier: SubscriptionTier }> = [
    { icon: <FishIcon />, tier: "FREE" },
    { icon: <TrophyIcon />, tier: "PRO" },
    { icon: <ShieldIcon />, tier: "PRO_ARCHIVE" },
  ];

  function updateDraft<Key extends keyof SettingsDraft>(key: Key, value: SettingsDraft[Key]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function updatePasswordDraft(key: keyof typeof passwordDraft, value: string) {
    setPasswordDraft((current) => ({ ...current, [key]: value }));
  }

  function applyThemePreference(value: string) {
    const nextTheme = writeThemePreference(value);
    setDraft((current) => ({ ...current, theme: nextTheme }));
    setSavedDraft((current) => ({ ...current, theme: nextTheme }));
    onThemeChange(nextTheme);
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    setNotice(null);
    const nextTheme = normalizeThemePreference(draft.theme);
    try {
      const result = await updateProfile({ displayName: draft.displayName, phone: draft.phone, teamName: draft.teamName });
      const token = getToken();
      if (token) setSession({ token, user: result.user });
      onUserUpdate(result.user);
      writeLocalStorage("profilePhone", draft.phone);
      writeLocalStorage("profileTeamName", draft.teamName);
      writeLocalStorage("measurementSystem", draft.measurement);
      writeLocalStorage("countryPreference", draft.countryPreference);
      writeLocalStorage("dateFormat", draft.dateFormat);
      writeLocalStorage("catchGpsPreference", draft.catchGpsPreference);
      writeLocalStorage("emailAlerts", String(draft.emailAlerts));
      writeLocalStorage("submissionAlerts", String(draft.submissionAlerts));
      writeLocalStorage("reviewReminders", String(draft.reviewReminders));
      writeLocalStorage("gpsDefault", String(draft.gpsDefault));
      writeThemePreference(nextTheme);
      onThemeChange(nextTheme);
      writeLocalStorage("languagePreference", draft.language);
      writeLocalStorage("timeZonePreference", draft.timeZone);
      writeLocalStorage("twoFactorEnabled", String(draft.twoFactor));
      setSavedDraft({ ...draft, theme: nextTheme });
      setDraft((current) => ({ ...current, theme: nextTheme }));
      setNotice("Changes saved securely.");
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordUpdate() {
    if (!user) return;
    setPasswordNotice(null);
    if (!passwordDraft.currentPassword || !passwordDraft.newPassword) {
      setPasswordNotice("Enter your current password and a new password.");
      return;
    }
    if (passwordDraft.newPassword.length < 8) {
      setPasswordNotice("New password must be at least 8 characters.");
      return;
    }
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setPasswordNotice("New passwords do not match.");
      return;
    }
    setPasswordSaving(true);
    try {
      const result = await updatePassword({ currentPassword: passwordDraft.currentPassword, newPassword: passwordDraft.newPassword });
      const token = getToken();
      if (token) setSession({ token, user: result.user });
      onUserUpdate(result.user);
      setPasswordDraft({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordNotice("Password updated successfully.");
      setNotice("Password updated successfully.");
    } catch (error) {
      setPasswordNotice((error as Error).message || "Could not update password.");
    } finally {
      setPasswordSaving(false);
    }
  }

  async function updateSubscriptionPlan(tier: SubscriptionTier) {
    if (!user || subscriptionSaving) return;
    if (!confirmSubscriptionSwitch(tier, currentPlan)) return;
    setSubscriptionSaving(tier);
    setNotice(null);
    try {
      const displayName = draft.displayName || user.displayName || user.email.split("@")[0] || user.email;
      const result = await updateProfile({ displayName, subscriptionTier: tier });
      const token = getToken();
      if (token) setSession({ token, user: result.user });
      writeLocalStorage(`subscriptionTierOverride:${result.user.id}`, tier);
      onUserUpdate(result.user);
      setNotice(`${subscriptionPlans[tier].name} plan selected.`);
    } catch (error) {
      setNotice((error as Error).message || "Could not update subscription.");
    } finally {
      setSubscriptionSaving(null);
    }
  }

  return (
    <form className="account-settings-page" onSubmit={saveSettings}>
      <section className="account-settings-heading">
        <span className="account-settings-icon"><GearIcon /></span>
        <div>
          <h1>Account settings</h1>
          <p>Manage your profile, security, preferences, and notification settings.</p>
        </div>
      </section>

      <section className="account-settings-grid">
        <article className="settings-profile-card">
          <div className="settings-card-head">
            <span className="settings-card-icon"><UserIcon /></span>
            <div>
              <h2>Profile</h2>
              <p>Update your personal information and how you appear on the platform.</p>
            </div>
            <button className="outline-action compact" type="button" onClick={() => document.getElementById("settings-display-name")?.focus()}>
              <EditIcon /> Edit profile
            </button>
          </div>
          {notice ? <p className="settings-notice">{notice}</p> : null}
          <div className="profile-settings-body">
            <div className="profile-avatar">
              <span>{initials}</span>
              <button aria-label="Change profile photo" type="button"><CameraIcon /></button>
            </div>
            <div className="profile-field-grid">
              <label className="profile-edit-field">
                <span><UserIcon /> Display name</span>
                <input id="settings-display-name" disabled={!user} value={draft.displayName} onChange={(event) => updateDraft("displayName", event.target.value)} />
              </label>
              <label className="profile-edit-field">
                <span><MailIcon /> Email address</span>
                <input disabled value={user?.email || "No active session"} />
              </label>
              <label className="profile-edit-field">
                <span><PhoneIcon /> Phone number</span>
                <input disabled={!user} value={draft.phone} onChange={(event) => updateDraft("phone", event.target.value)} placeholder="-" />
              </label>
              <label className="profile-edit-field">
                <span><TeamIcon /> Club / team</span>
                <input disabled={!user} value={draft.teamName} onChange={(event) => updateDraft("teamName", event.target.value)} placeholder="-" />
              </label>
            </div>
          </div>
        </article>

        <article className="settings-section-card account-summary-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><UserIcon /></span>
            <h2>Account summary</h2>
          </div>
          <div className="settings-row-list">
            <SettingsRow icon={<TeamIcon />} label="Role" value={roleLabel} />
            <SettingsRow icon={<BadgeIcon />} label="Plan" value={planLabel} />
            <SettingsRow icon={<ShieldIcon />} label="Account status" value={<span className="status-pill active">Active</span>} />
            <SettingsRow icon={<CalendarIcon />} label="Member since" value={formatDate(memberSince)} />
          </div>
        </article>

        <article className="settings-section-card subscription-settings-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><BadgeIcon /></span>
            <div>
              <h2>Subscription</h2>
              <p>Manage the plan saved to your Reelmaster account.</p>
            </div>
          </div>
          <div className="settings-plan-summary">
            <span>Current plan</span>
            <strong>{planLabel}</strong>
          </div>
          <div className="settings-plan-options">
            {settingsPlanOptions.map((plan) => {
              const selected = currentPlan === plan.tier || (!currentPlan && plan.tier === "FREE");
              const planMeta = subscriptionPlans[plan.tier];
              return (
                <button
                  aria-pressed={selected}
                  className={selected ? "selected" : ""}
                  disabled={!user || subscriptionSaving !== null}
                  key={plan.tier}
                  onClick={() => void updateSubscriptionPlan(plan.tier)}
                  type="button"
                >
                  <span>{plan.icon}</span>
                  <b>{planMeta.name}</b>
                  <strong className="settings-plan-price">{planMeta.price}<small>{plan.tier === "FREE" ? " forever" : " / month"}</small></strong>
                  <small>{subscriptionSaving === plan.tier ? "Saving..." : selected ? "Current plan" : planMeta.description}</small>
                </button>
              );
            })}
          </div>
          <p className="settings-inline-hint"><InfoIcon /> Plan changes ask for confirmation before updating your account.</p>
        </article>

        <article className="settings-section-card security-card">
          <div className="settings-card-head">
            <span className="settings-card-icon"><ShieldIcon /></span>
            <div>
              <h2>Security</h2>
              <p>Keep your account secure with these authentication settings.</p>
            </div>
            <button aria-expanded={securityOpen} className="outline-action compact" disabled={!user} type="button" onClick={() => setSecurityOpen((open) => !open)}>
              {securityOpen ? "Hide password" : "Change password"} <ArrowRightIcon />
            </button>
          </div>
          <div className="settings-row-list">
            <SettingsRow icon={<LockIcon />} label="Password" value={<span className="muted-row-value">Change available</span>} />
            <label className="settings-toggle-row">
              <span><ShieldIcon /><b>Two-factor authentication</b><small>Add an extra layer of security to your account.</small></span>
              <input aria-label="Two-factor authentication" checked={draft.twoFactor} onChange={(event) => updateDraft("twoFactor", event.target.checked)} type="checkbox" />
            </label>
            <SettingsRow icon={<DeviceIcon />} label="Login sessions" value="Current device" />
          </div>
          {securityOpen ? (
            <div className="settings-password-panel" aria-label="Change password">
              <label className="field">
                <span>Current password</span>
                <input
                  autoComplete="current-password"
                  disabled={!user || passwordSaving}
                  type="password"
                  value={passwordDraft.currentPassword}
                  onChange={(event) => updatePasswordDraft("currentPassword", event.target.value)}
                />
              </label>
              <label className="field">
                <span>New password</span>
                <input
                  autoComplete="new-password"
                  disabled={!user || passwordSaving}
                  type="password"
                  value={passwordDraft.newPassword}
                  onChange={(event) => updatePasswordDraft("newPassword", event.target.value)}
                />
              </label>
              <label className="field">
                <span>Confirm new password</span>
                <input
                  autoComplete="new-password"
                  disabled={!user || passwordSaving}
                  type="password"
                  value={passwordDraft.confirmPassword}
                  onChange={(event) => updatePasswordDraft("confirmPassword", event.target.value)}
                />
              </label>
              {passwordNotice ? <p className="settings-password-notice">{passwordNotice}</p> : null}
              <button className="submit-link compact" disabled={!user || passwordSaving} onClick={() => void handlePasswordUpdate()} type="button">
                <LockIcon /> {passwordSaving ? "Updating..." : "Update password"}
              </button>
            </div>
          ) : null}
        </article>

        <article className="settings-section-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><BellIcon /></span>
            <div>
              <h2>Notifications & privacy</h2>
              <p>Choose what updates you want to receive.</p>
            </div>
          </div>
          <div className="settings-toggle-list">
            <SettingsToggle checked={draft.emailAlerts} label="Email tournament updates" hint="New tournaments, changes, and results." onChange={(value) => updateDraft("emailAlerts", value)} />
            <SettingsToggle checked={draft.submissionAlerts} label="Submission alerts" hint="Get notified about your catch submissions." onChange={(value) => updateDraft("submissionAlerts", value)} />
            <SettingsToggle checked={draft.reviewReminders} label="Review reminders" hint="Reminders for pending submissions." onChange={(value) => updateDraft("reviewReminders", value)} />
            <SettingsToggle checked={draft.gpsDefault} label="Ask for GPS permission" hint="Always ask before capturing GPS location." onChange={(value) => updateDraft("gpsDefault", value)} />
          </div>
        </article>

        <article className="settings-section-card tournament-defaults-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><TargetIcon /></span>
            <div>
              <h2>Tournament defaults</h2>
              <p>Set your preferences for creating and joining tournaments.</p>
            </div>
          </div>
          <div className="settings-control-grid">
            <label className="field">
              <span>Measurement system</span>
              <select value={draft.measurement} onChange={(event) => updateDraft("measurement", event.target.value)}>
                <option value="METRIC">Metric - cm / kg</option>
                <option value="IMPERIAL">Imperial - in / lb</option>
              </select>
            </label>
            <label className="field">
              <span>Default country</span>
              <select value={draft.countryPreference} onChange={(event) => updateDraft("countryPreference", event.target.value)}>
                {countryOptions([draft.countryPreference]).map((country) => <option key={country} value={country}>{country}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Default date format</span>
              <select value={draft.dateFormat} onChange={(event) => updateDraft("dateFormat", event.target.value)}>
                <option value="european">29.05.2026</option>
                <option value="medium">May 26, 2026</option>
                <option value="short">05/26/26</option>
                <option value="iso">2026-05-26</option>
              </select>
            </label>
            <label className="field wide-field">
              <span>Catch submission preferences</span>
              <select value={draft.catchGpsPreference} onChange={(event) => updateDraft("catchGpsPreference", event.target.value)}>
                <option value="ask">Ask for GPS when submitting catches</option>
                <option value="always">Always include GPS when available</option>
                <option value="manual">Let me enter location manually</option>
              </select>
            </label>
            <p className="settings-inline-hint"><InfoIcon /> These defaults will be applied when you create new tournaments and submit catches.</p>
          </div>
        </article>

        <article className="settings-section-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><PaletteIcon /></span>
            <div>
              <h2>Appearance & preferences</h2>
            </div>
          </div>
          <div className="settings-control-grid appearance-grid">
            <label className="field">
              <span>Theme</span>
              <select value={normalizeThemePreference(draft.theme)} onChange={(event) => applyThemePreference(event.target.value)}>
                <option value="system">System default</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
            <label className="field">
              <span>Language</span>
              <select value={draft.language} onChange={(event) => updateDraft("language", event.target.value)}>
                <option value="English">English</option>
                <option value="Latvian">Latvian</option>
                <option value="Spanish">Spanish</option>
              </select>
            </label>
            <label className="field">
              <span>Time zone</span>
              <select value={draft.timeZone} onChange={(event) => updateDraft("timeZone", event.target.value)}>
                <option value="America/New_York">(UTC-05:00) Eastern Time (US & Canada)</option>
                <option value="Europe/Riga">(UTC+02:00) Riga</option>
                <option value="Europe/London">(UTC+00:00) London</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
        </article>

        <article className="settings-section-card account-session-card">
          <div className="settings-card-head compact-head">
            <span className="settings-card-icon"><LockIcon /></span>
            <div>
              <h2>Session</h2>
              <p>Sign out from this device when you are finished.</p>
            </div>
          </div>
          <button className="danger-button settings-logout-button" disabled={!user} onClick={onLogout} type="button">
            <ArrowRightIcon /> Log out
          </button>
        </article>
      </section>

      <section className="settings-save-bar">
        <span><ShieldIcon /> {hasChanges ? "You have unsaved changes" : "Your changes are saved securely"}</span>
        <div>
          <button className="outline-action" disabled={!hasChanges || saving} type="button" onClick={() => {
            setDraft(savedDraft);
            applyThemePreference(savedDraft.theme);
            setNotice("Changes reverted.");
          }}>
            Cancel
          </button>
          <button className="submit-link" disabled={!user || saving || !hasChanges} type="submit">
            <CheckIcon /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </section>
    </form>
  );
}

function SettingsRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="settings-info-row">
      <span>{icon}</span>
      <b>{label}</b>
      <em>{value}</em>
    </div>
  );
}

function SettingsToggle({ checked, hint, label, onChange }: { checked: boolean; hint: string; label: string; onChange: (checked: boolean) => void }) {
  return (
    <label className="settings-check-row">
      <input checked={checked} onChange={(event) => onChange(event.target.checked)} type="checkbox" />
      <span>
        <b>{label}</b>
        <small>{hint}</small>
      </span>
    </label>
  );
}

function TournamentDashboardView({
  initialTab,
  tournament,
  registrations,
  user,
  onBack,
  onAnnouncementsReload,
  onDelete,
  onEdit,
  onJoin,
  onSubmitCatch,
}: {
  initialTab: "overview" | "leaderboard" | "announcements" | "questions";
  tournament: Tournament | null;
  registrations: RegistrationItem[];
  user: AuthUser | null;
  onBack: () => void;
  onAnnouncementsReload: () => Promise<void>;
  onDelete: (tournament: Tournament) => void;
  onEdit: (tournament: Tournament) => void;
  onJoin: (tournament: Tournament) => void;
  onSubmitCatch: () => void;
}) {
  const [dashboard, setDashboard] = useState<TournamentDashboardData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [tab, setTab] = useState<"overview" | "leaderboard" | "announcements" | "questions">(initialTab);
  const [tournamentAnnouncements, setTournamentAnnouncements] = useState<AnnouncementItem[]>([]);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab, tournament?.id]);

  useEffect(() => {
    if (!tournament) return;
    const tournamentId = tournament.id;
    let cancelled = false;
    async function loadDashboard() {
      setLoadingDashboard(true);
      try {
        const data = await getTournamentDashboard(tournamentId);
        if (!cancelled) setDashboard(data);
      } catch {
        if (!cancelled) setDashboard(null);
      } finally {
        if (!cancelled) setLoadingDashboard(false);
      }
    }
    void loadDashboard();
    const timer = window.setInterval(loadDashboard, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [tournament]);

  useEffect(() => {
    if (!tournament || !user) return;
    const tournamentId = tournament.id;
    let cancelled = false;
    async function loadAnnouncements() {
      try {
        const data = await listAnnouncements(tournamentId);
        if (!cancelled) setTournamentAnnouncements(data.items ?? []);
      } catch {
        if (!cancelled) setTournamentAnnouncements([]);
      }
    }
    void loadAnnouncements();
    const timer = window.setInterval(loadAnnouncements, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [tournament, user]);

  if (!tournament) {
    return <EmptyCard title="Tournament not selected" body="Open a tournament from My Tournaments to view its dashboard." />;
  }

  const registration = registrations.find((item) => item.tournamentId === tournament.id);
  const canManage = canManageTournament(tournament, user);
  const canEditOrDelete = canManage && (getTournamentStatus(tournament) === "DRAFT" || getTournamentStatus(tournament) === "UPCOMING");
  const approvedStatus = registration?.status === "APPROVED" || canManage ? "Approved" : registration?.status || "Not joined";

  return (
    <section className="tournament-detail-page">
      <button className="detail-back-button" type="button" onClick={onBack}>Back to tournaments</button>
      <section className="tournament-detail-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(3, 16, 28, 0.98) 0%, rgba(3, 20, 34, 0.78) 43%, rgba(3, 20, 34, 0.28) 100%), url('${tournamentImage(tournament)}')` }}>
        <div className="detail-hero-copy">
          <span className={`tournament-status-badge ${getTournamentStatus(tournament).toLowerCase()}`}>{tournamentBadge(tournament)}</span>
          <h1>{tournamentName(tournament)}</h1>
          <p><MapPinIcon /> {tournamentLocation(tournament)}</p>
          <p><BellIcon /> {formatRange(tournament.startAt, tournament.endAt)}</p>
        </div>
        <div className="detail-hero-stats">
          <DetailHeroStat icon={<FishLineIcon />} label="Status" value={statusLabel(tournament)} />
          <DetailHeroStat icon={<ClipboardIcon />} label="My status" value={approvedStatus} />
          <DetailHeroStat icon={<TrophyIcon />} label="Participants" value={`${dashboard?.participants.length ?? tournament.registrationCount} registered`} />
          <DetailHeroStat icon={<BellIcon />} label="Ends in" value={timeLeftLabel(tournament.endAt)} hint={formatDateTime(tournament.endAt)} />
          <DetailHeroStat icon={<FishIcon />} label="Species & proof" value={tournament.allowAnySpecies ? "Any species" : tournament.species.join(", ") || "Not configured"} hint={[tournament.photoRequired ? "Photo" : "", tournament.videoRequired ? "Video" : "", tournament.gpsRequired ? "GPS" : "", mediaRuleLabel(tournament.mediaSourceRule)].filter(Boolean).join(" or ")} />
        </div>
        {!canManage ? (
          <div className="detail-hero-actions">
            <TournamentJoinActions onJoin={() => onJoin(tournament)} onSubmitCatch={onSubmitCatch} registration={registration} tournament={tournament} user={user} />
          </div>
        ) : null}
        {canEditOrDelete ? (
          <div className="detail-hero-actions">
            <button className="submit-link compact" type="button" onClick={() => onEdit(tournament)}>Edit tournament</button>
            <button className="danger-button compact" type="button" onClick={() => onDelete(tournament)}>Delete tournament</button>
          </div>
        ) : null}
        <div className="detail-hero-actions report-actions">
          <button className="clear-button compact" type="button" onClick={() => openTournamentPdfReport(tournament, dashboard, resolvedThemePreference(storedThemePreference()))}>Download results PDF</button>
        </div>
      </section>

      <div className="detail-tab-bar" aria-label="Tournament detail tabs">
        <button className={tab === "overview" ? "selected" : ""} onClick={() => setTab("overview")} type="button">Overview</button>
        <button className={tab === "leaderboard" ? "selected" : ""} onClick={() => setTab("leaderboard")} type="button">Leaderboard</button>
        <button className={tab === "announcements" ? "selected" : ""} onClick={() => setTab("announcements")} type="button">Announcements</button>
        <button className={tab === "questions" ? "selected" : ""} onClick={() => setTab("questions")} type="button">Questions</button>
      </div>

      {tab === "overview" ? <TournamentOverviewDetail tournament={tournament} dashboard={dashboard} loading={loadingDashboard} registration={registration} /> : null}
      {tab === "leaderboard" ? <TournamentLeaderboardDetail dashboard={dashboard} loading={loadingDashboard} tournament={tournament} /> : null}
      {tab === "announcements" ? (
        <TournamentAnnouncementsDetail
          announcements={tournamentAnnouncements}
          canManage={canManage}
          onGlobalReload={onAnnouncementsReload}
          onReload={async () => {
            const data = await listAnnouncements(tournament.id);
            setTournamentAnnouncements(data.items ?? []);
          }}
          tournament={tournament}
        />
      ) : null}
      {tab === "questions" ? <TournamentQuestionsDetail canManage={canManage} tournamentId={tournament.id} /> : null}
    </section>
  );

  /*
  return (
    <>
      <section className="page-heading">
        <button className="clear-button compact" type="button" onClick={onBack}>Back to tournaments</button>
        <h1>{tournamentName(tournament)}</h1>
        <p>{tournamentLocation(tournament)} - {formatRange(tournament.startAt, tournament.endAt)}</p>
        {!canManage ? (
          <TournamentJoinActions
            onJoin={() => onJoin(tournament)}
            onSubmitCatch={onSubmitCatch}
            registration={registration}
            tournament={tournament}
            user={user}
          />
        ) : null}
        {canEditOrDelete ? (
          <div className="card-actions dashboard-actions">
            <button className="clear-button compact" type="button" onClick={() => onEdit(tournament)}>Edit tournament</button>
            <button className="danger-button compact" type="button" onClick={() => onDelete(tournament)}>Delete tournament</button>
          </div>
        ) : null}
      </section>
      <section className="dashboard-grid">
        <article className="empty-card">
          <span className="eyebrow">Status</span>
          <h2>{tournamentBadge(tournament)}</h2>
          <p>{stripHtml(tournament.description) || "No description yet."}</p>
          <div className="meta-row details-meta">
            <span>{tournament.registrationCount} registered</span>
            <span>{tournament.participantLimit > 0 ? `${tournament.participantLimit} max` : "No participant limit"}</span>
          </div>
        </article>
        <article className="empty-card">
          <span className="eyebrow">My status</span>
          <h2>{registration ? registration.status : "Not joined"}</h2>
          <p>{registration?.paymentStatus === "REQUIRES_PAYMENT" ? "Payment is pending. Follow the organizer instructions below." : registration?.status === "APPROVED" ? "Approved - you can submit catches while the event is live." : "Join this tournament to submit catches."}</p>
        </article>
        <article className="empty-card">
          <span className="eyebrow">Rules</span>
          <h2>{scoringLabel(tournament.scoringMetric)}</h2>
          <p>{tournament.rules || "Organizer has not added detailed rules yet."}</p>
        </article>
        <article className="empty-card">
          <span className="eyebrow">Species & proof</span>
          <h2>{tournament.allowAnySpecies ? "Any species" : tournament.species.join(", ") || "Not configured"}</h2>
          <p>{[tournament.photoRequired ? "Photo proof" : "", tournament.videoRequired ? "Release video" : "", tournament.gpsRequired ? "GPS required" : "", mediaRuleLabel(tournament.mediaSourceRule)].filter(Boolean).join(" / ")}</p>
        </article>
      </section>
      {tournament.paymentRequired ? (
        <section className="empty-card">
          <span className="eyebrow">Payment instructions</span>
          <h2>{(tournament.entryFeeCents / 100).toFixed(2)} {tournament.currency}</h2>
          <div className="settings-list payment-list">
            <span><small>Method</small>{tournament.paymentMethod}</span>
            <span><small>Account holder</small>{tournament.bankAccountHolder || "-"}</span>
            <span><small>Bank</small>{tournament.bankName || "-"}</span>
            <span><small>IBAN</small>{tournament.iban || "-"}</span>
            <span><small>SWIFT/BIC</small>{tournament.swiftBic || "-"}</span>
            <span><small>Account number</small>{tournament.accountNumber || "-"}</span>
            <span><small>Routing / sort / branch</small>{[tournament.routingNumber, tournament.sortCode, tournament.branchCode].filter(Boolean).join(" / ") || "-"}</span>
          </div>
          <p>{tournament.paymentInstructions || tournament.otherPaymentDetails || "Organizer has not added payment notes yet."}</p>
        </section>
      ) : null}
      <div className="segmented detail-tabs" aria-label="Tournament detail tabs">
        <button className={tab === "overview" ? "selected" : ""} onClick={() => setTab("overview")} type="button">Overview</button>
        <button className={tab === "leaderboard" ? "selected" : ""} onClick={() => setTab("leaderboard")} type="button">Leaderboard</button>
        <button className={tab === "announcements" ? "selected" : ""} onClick={() => setTab("announcements")} type="button">Announcements</button>
        <button className={tab === "questions" ? "selected" : ""} onClick={() => setTab("questions")} type="button">Questions</button>
      </div>
      {(tab === "overview" || tab === "leaderboard") ? <section className="dashboard-grid">
        <article className="empty-card">
          <span className="eyebrow">Participant rankings</span>
          <h2>{loadingDashboard ? "Loading..." : `${dashboard?.rankings.length ?? 0} ranked`}</h2>
          <div className="ranking-list">
            {(dashboard?.rankings ?? []).slice(0, 8).map((item) => (
              <div key={`${item.participantEmail}-${item.rank}`} className="leader-row">
                <span>{item.rank}</span>
                <b>{item.participantName}</b>
                <em>{item.score} {tournament.scoringMetric === "WEIGHT" ? "kg" : "cm"}</em>
              </div>
            ))}
            {!loadingDashboard && !dashboard?.rankings.length ? <p>No approved catches yet.</p> : null}
          </div>
        </article>
        <article className="empty-card">
          <span className="eyebrow">Participants</span>
          <h2>{loadingDashboard ? "Loading..." : `${dashboard?.participants.length ?? tournament.registrationCount} registered`}</h2>
          <div className="ranking-list">
            {(dashboard?.participants ?? []).slice(0, 10).map((item) => (
              <div key={item.id} className="leader-row">
                <span>{item.status === "APPROVED" ? "✓" : "…"}</span>
                <b>{item.name}</b>
                <em>{item.paymentStatus === "PAID" ? "Paid" : item.paymentStatus === "REQUIRES_PAYMENT" ? "Unpaid" : item.status}</em>
              </div>
            ))}
            {!loadingDashboard && !dashboard?.participants.length ? <p>No participants yet.</p> : null}
          </div>
        </article>
        <article className="empty-card">
          <span className="eyebrow">Top catches</span>
          <h2>Overall</h2>
          <div className="catch-mini-list">
            {(dashboard?.topCatches ?? []).slice(0, 6).map((item) => {
              const photoUrl = safeMediaUrl(item.photoUrl);
              return (
                <div key={item.id}>
                  {photoUrl ? <img src={photoUrl} alt={`${item.species} catch`} /> : <FishIcon />}
                  <span><strong>{item.species}</strong><small>{item.participantName} - {item.length || "-"} cm / {item.weight || "-"} kg</small></span>
                </div>
              );
            })}
            {!loadingDashboard && !dashboard?.topCatches.length ? <p>No approved catches yet.</p> : null}
          </div>
        </article>
        <article className="empty-card">
          <span className="eyebrow">Top by species</span>
          <h2>Best catch per species</h2>
          <div className="catch-mini-list">
            {(dashboard?.topBySpecies ?? []).slice(0, 8).map((item) => {
              const photoUrl = safeMediaUrl(item.photoUrl);
              return (
                <div key={`${item.species}-${item.id}`}>
                  {photoUrl ? <img src={photoUrl} alt={`${item.species} catch`} /> : <FishIcon />}
                  <span><strong>{item.species}</strong><small>{item.participantName} - {item.length || "-"} cm / {item.weight || "-"} kg</small></span>
                </div>
              );
            })}
            {!loadingDashboard && !dashboard?.topBySpecies.length ? <p>No approved catches yet.</p> : null}
          </div>
        </article>
      </section> : null}
      {tab === "announcements" ? (
        <TournamentAnnouncementsPanel
          announcements={tournamentAnnouncements}
          canManage={canManage}
          onGlobalReload={onAnnouncementsReload}
          onReload={async () => {
            const data = await listAnnouncements(tournament.id);
            setTournamentAnnouncements(data.items ?? []);
          }}
          tournament={tournament}
        />
      ) : null}
      {tab === "questions" ? <PlaceholderView title="Questions" body="Tournament discussion can be added here for joined participants and organizers without opening generic participant-to-participant messaging." /> : null}
    </>
  );
  */
}

function DetailHeroStat({ hint, icon, label, value }: { hint?: string; icon: ReactNode; label: string; value: string }) {
  return (
    <article className="detail-hero-stat">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        {hint ? <em>{hint}</em> : null}
      </div>
    </article>
  );
}

function TournamentOverviewDetail({ dashboard, loading, registration, tournament }: { dashboard: TournamentDashboardData | null; loading: boolean; registration?: RegistrationItem; tournament: Tournament }) {
  return (
    <div className="detail-content-grid">
      <section className="detail-panel">
        <h2>Tournament Overview</h2>
        <p>{stripHtml(tournament.description) || "No description has been added yet."}</p>
        <div className="detail-info-grid">
          <span><small>Scoring</small>{scoringLabel(tournament.scoringMetric)}</span>
          <span><small>Registration</small>{registration?.status || "Not joined"}</span>
          <span><small>Capacity</small>{tournament.participantLimit > 0 ? `${tournament.registrationCount}/${tournament.participantLimit}` : `${tournament.registrationCount} registered`}</span>
          <span><small>Visibility</small>{tournament.visibility}</span>
        </div>
      </section>
      <section className="detail-panel">
        <PanelTitle title="Participants" action={loading ? "Loading" : `${dashboard?.participants.length ?? tournament.registrationCount} registered`} />
        <div className="detail-compact-list">
          {(dashboard?.participants ?? []).slice(0, 6).map((item) => <span key={item.id}><b>{item.name}</b><em>{item.status}</em></span>)}
          {!loading && !dashboard?.participants.length ? <p className="design-empty">No participants yet.</p> : null}
        </div>
      </section>
      <section className="detail-panel">
        <h2>Rules</h2>
        <p>{tournament.rules || "Organizer has not added detailed rules yet."}</p>
      </section>
      <section className="detail-panel">
        <h2>Proof Requirements</h2>
        <div className="detail-check-list">
          <span>{tournament.photoRequired ? "Photo proof required" : "Photo proof optional"}</span>
          <span>{tournament.videoRequired ? "Release video required" : "Release video optional"}</span>
          <span>{tournament.gpsRequired ? "GPS required" : "GPS optional"}</span>
          <span>{mediaRuleLabel(tournament.mediaSourceRule)}</span>
        </div>
      </section>
    </div>
  );
}

function TournamentLeaderboardDetail({ dashboard, loading, tournament }: { dashboard: TournamentDashboardData | null; loading: boolean; tournament: Tournament }) {
  type RankingRowWithTeam = TournamentDashboardData["rankings"][number] & {
    team?: string;
    teamName?: string;
  };
  const [view, setView] = useState<"participants" | "teams">("participants");
  const [showAllCatches, setShowAllCatches] = useState(false);
  const rankings = dashboard?.rankings ?? [];
  const catches = dashboard?.topCatches ?? [];
  const bySpecies = dashboard?.topBySpecies ?? [];
  const visibleCatches = showAllCatches ? catches : catches.slice(0, 6);
  const canToggleCatches = catches.length > 6;
  const teamRows = Array.from(rankings.reduce((teams, item) => {
    const row = item as RankingRowWithTeam;
    const teamName = row.teamName || row.team;
    if (!teamName) return teams;
    const existing = teams.get(teamName) ?? { bestFish: 0, catches: 0, members: 0, score: 0, teamName };
    existing.bestFish = Math.max(existing.bestFish, item.bestLength || item.bestWeight || 0);
    existing.catches += item.catches;
    existing.members += 1;
    existing.score += item.score;
    teams.set(teamName, existing);
    return teams;
  }, new Map<string, { bestFish: number; catches: number; members: number; score: number; teamName: string }>()).values())
    .sort((a, b) => b.score - a.score);
  return (
    <div className="leaderboard-detail-grid">
      <section className="detail-panel leaderboard-table-panel">
        <div className="leaderboard-toolbar">
          <div className="detail-segmented" aria-label="Leaderboard view">
            <button className={view === "participants" ? "selected" : ""} onClick={() => setView("participants")} type="button">Participants</button>
            <button className={view === "teams" ? "selected" : ""} onClick={() => setView("teams")} type="button">Teams</button>
          </div>
          <label>Scoring by <select defaultValue="total"><option value="total">Total {scoringLabel(tournament.scoringMetric)}</option></select></label>
        </div>
        <div className="detail-leaderboard-table">
          <div className="detail-leaderboard-head">
            <span>Rank</span>
            <span>{view === "teams" ? "Team" : "Participant"}</span>
            <span>Total {scoreUnit(tournament.scoringMetric)}</span>
            <span>{view === "teams" ? "Members" : "# Catches"}</span>
            <span>Biggest Fish</span>
            <span>Trend</span>
          </div>
          {view === "participants" ? rankings.slice(0, 8).map((item, index) => (
            <article key={`${item.participantEmail}-${item.rank}`}>
              <span className={index < 3 ? `rank-medal rank-${index + 1}` : ""}>{item.rank}</span>
              <strong>{item.participantName}<small>{item.participantEmail}</small></strong>
              <b>{item.score} {scoreUnit(tournament.scoringMetric)}</b>
              <span>{item.catches}</span>
              <span>{item.bestLength || item.bestWeight || "-"} {item.bestLength ? "cm" : item.bestWeight ? "kg" : ""}</span>
              <em>{index % 2 ? "-" : "+ 1"}</em>
            </article>
          )) : teamRows.slice(0, 8).map((item, index) => (
            <article key={item.teamName}>
              <span className={index < 3 ? `rank-medal rank-${index + 1}` : ""}>{index + 1}</span>
              <strong>{item.teamName}<small>{item.catches} approved catches</small></strong>
              <b>{item.score} {scoreUnit(tournament.scoringMetric)}</b>
              <span>{item.members}</span>
              <span>{item.bestFish || "-"} {item.bestFish ? scoreUnit(tournament.scoringMetric) : ""}</span>
              <em>{index % 2 ? "-" : "+ 1"}</em>
            </article>
          ))}
          {!loading && view === "participants" && !rankings.length ? <p className="design-empty">No approved catches yet.</p> : null}
          {!loading && view === "teams" && !teamRows.length ? <p className="design-empty">No team entries yet.</p> : null}
        </div>
      </section>
      <aside className="detail-side-stack">
        <section className="detail-panel">
          <h2>Leaderboard Info</h2>
          <div className="detail-info-columns">
            <div><strong>Scoring</strong><p>Scoring is based on {scoringLabel(tournament.scoringMetric).toLowerCase()}. Ties are broken by the biggest single fish.</p></div>
            <div><strong>Rules</strong><div className="detail-check-list"><span>Fish must be caught live</span><span>Measurements must be approved</span><span>Catches must be submitted before the event ends</span><span>Respect local fishing regulations</span></div></div>
          </div>
        </section>
        <section className="detail-panel">
          <PanelTitle title="Top Catches by Species" />
          <div className="species-card-grid">
            {bySpecies.slice(0, 4).map((item, index) => <SpeciesCatchCard key={`${item.species}-${item.id}`} item={item} index={index} />)}
            {!loading && !bySpecies.length ? <p className="design-empty">No species leaders yet.</p> : null}
          </div>
        </section>
        <section className="detail-panel">
          <h2>Live Leaderboard Snapshot</h2>
          <LeaderboardSparkline rankings={rankings} />
        </section>
      </aside>
      <section className="detail-panel recent-catches-panel">
        <PanelTitle
          title="Recent Approved Catches"
          action={canToggleCatches ? (showAllCatches ? "Show fewer" : "View all updates") : undefined}
          onAction={canToggleCatches ? () => setShowAllCatches((current) => !current) : undefined}
        />
        <div className="approved-catch-list">
          {visibleCatches.map((item) => <span key={item.id}><b>{item.participantName}</b><em>caught a {item.length || item.weight || "-"} {item.length ? "cm" : "kg"} {item.species}</em><strong>{formatDateTime(item.createdAt)}</strong></span>)}
          {!loading && !catches.length ? <p className="design-empty">No approved catches yet.</p> : null}
        </div>
      </section>
    </div>
  );
}

function SpeciesCatchCard({ index, item }: { index: number; item: SubmissionItem }) {
  const photoUrl = safeMediaUrl(item.photoUrl);
  return (
    <article className="species-catch-card">
      {photoUrl ? <img src={photoUrl} alt="" /> : <img src={fallbackLakeImages[index % fallbackLakeImages.length]} alt="" />}
      <strong>{item.species || "Catch"}</strong>
      <b>{item.length || item.weight || "-"} {item.length ? "cm" : "kg"}</b>
      <small>{item.participantName}</small>
    </article>
  );
}

function LeaderboardSparkline({ rankings }: { rankings: TournamentDashboardData["rankings"] }) {
  return (
    <div className="leaderboard-sparkline">
      <svg viewBox="0 0 520 170" role="img" aria-label="Live leaderboard trend">
        {[40, 80, 120, 160].map((y) => <line key={y} x1="20" x2="500" y1={y} y2={y} stroke="rgba(255,255,255,.08)" />)}
        {rankings.slice(0, 5).map((item, index) => {
          const y = 135 - Math.min(95, item.score / Math.max(rankings[0]?.score || 1, 1) * 95) + index * 7;
          const color = ["#22e5d1", "#38bdf8", "#facc15", "#a78bfa", "#fb923c"][index];
          return <path key={item.participantEmail} d={`M20 145 C110 ${y + 35}, 160 ${y}, 245 ${y} S390 ${y - 10}, 500 ${y - 16}`} fill="none" stroke={color} strokeWidth="4" />;
        })}
      </svg>
      <div>{rankings.slice(0, 5).map((item) => <span key={item.participantEmail}>{item.participantName}<strong>{item.score}</strong></span>)}</div>
    </div>
  );
}

function TournamentAnnouncementsDetail({ announcements, canManage, onGlobalReload, onReload, tournament }: { announcements: AnnouncementItem[]; canManage: boolean; onGlobalReload: () => Promise<void>; onReload: () => Promise<void>; tournament: Tournament }) {
  const pinned = announcements[0];
  return (
    <div className="announcements-detail-grid">
      <section className="detail-panel announcement-timeline-panel">
        {pinned ? <article className="pinned-announcement"><span className="tournament-status-badge upcoming">Important</span><h2>{pinned.title}</h2><div className="rich-content" dangerouslySetInnerHTML={{ __html: pinned.content }} /><small>Tournament Organizer - {formatDateTime(pinned.createdAt)}</small></article> : null}
        <PanelTitle title="All announcements" action={`${announcements.length}`} />
        {canManage ? <TournamentAnnouncementsPanel announcements={announcements} canManage onGlobalReload={onGlobalReload} onReload={onReload} showList={false} tournament={tournament} /> : null}
        <div className="announcement-timeline">
          {announcements.map((announcement) => <article key={announcement.id}><span /><div><strong>{announcement.title}</strong><div className="rich-content" dangerouslySetInnerHTML={{ __html: announcement.content }} /></div><time>{formatDateTime(announcement.createdAt)}</time></article>)}
          {!announcements.length ? <p className="design-empty">No announcements yet.</p> : null}
        </div>
      </section>
      <aside className="detail-side-stack">
        <section className="detail-panel"><h2>Announcements</h2><div className="announcement-category-list"><span>All <b>{announcements.length}</b></span><span>Unread <b>{announcements.filter((item) => !item.readAt).length}</b></span><span>Rules <b>1</b></span><span>Weather <b>1</b></span><span>Results <b>1</b></span></div></section>
        <section className="detail-panel"><h2>Event schedule</h2><div className="detail-compact-list"><span><b>Lines in</b><em>{formatDateTime(tournament.startAt)}</em></span><span><b>Lines out</b><em>{formatDateTime(tournament.endAt)}</em></span><span><b>Final weigh-in</b><em>{formatDateTime(tournament.endAt)}</em></span></div></section>
      </aside>
    </div>
  );
}

function TournamentQuestionsDetail({ canManage, tournamentId }: { canManage: boolean; tournamentId: string }) {
  type QuestionFilter = "all" | "open" | "answered";
  type TournamentQuestion = {
    answer: string;
    body: string;
    name: string;
    state: "Answered" | "Open";
    tag: string;
    title: string;
  };

  const seedQuestions: TournamentQuestion[] = [
    { answer: "Official measuring boards are preferred. Digital boards are accepted when the whole fish and measurement marks are visible.", body: "Can we use a bump board or digital measuring device instead?", name: "Brandon Walker", state: "Answered", tag: "Rules", title: "Do we need to measure fish on the official measuring board?" },
    { answer: "Lines in begins at the start time shown in the tournament schedule.", body: "Just want to confirm the official start time for Saturday.", name: "Justin Morgan", state: "Answered", tag: "Launch", title: "What time can we start fishing on Day 1?" },
    { answer: "", body: "What's the tiebreaker if two anglers have the same total length?", name: "Sarah Lopez", state: "Open", tag: "Scoring", title: "How are ties broken?" },
  ];
  const faqItems = ["What time is check-in?", "What is the weigh-in time?", "What types of photos are accepted?", "Can I edit or retake my submission?"];
  const questionsStorageKey = `reelmaster:tournament-questions:${tournamentId}`;
  const [questions, setQuestions] = useState<TournamentQuestion[]>(() => {
    try {
      const stored = readLocalStorage(questionsStorageKey);
      return stored ? JSON.parse(stored) as TournamentQuestion[] : seedQuestions;
    } catch {
      return seedQuestions;
    }
  });
  const [draft, setDraft] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<QuestionFilter>("all");
  const [sort, setSort] = useState("recent");
  const [expandedFaq, setExpandedFaq] = useState("");
  const filteredQuestions = questions
    .filter((question) => filter === "all" || question.state.toLowerCase() === filter)
    .sort((a, b) => sort === "open-first" ? (a.state === b.state ? 0 : a.state === "Open" ? -1 : 1) : 0);

  useEffect(() => {
    writeLocalStorage(questionsStorageKey, JSON.stringify(questions));
  }, [questions, questionsStorageKey]);

  function askQuestion() {
    const title = draft.trim();
    if (!title) return;
    setQuestions((current) => [
      { answer: "", body: "Waiting for organizer response.", name: "You", state: "Open", tag: "Question", title },
      ...current,
    ]);
    setDraft("");
    setFilter("all");
  }

  function answerQuestion(title: string) {
    const answer = (answerDrafts[title] || "").trim();
    if (!answer) return;
    setQuestions((current) => current.map((question) => question.title === title ? { ...question, answer, state: "Answered" } : question));
    setAnswerDrafts((current) => {
      const next = { ...current };
      delete next[title];
      return next;
    });
    setFilter("all");
  }

  return (
    <div className="questions-detail-grid">
      <section className="detail-panel questions-main-panel">
        <h2>Ask a question</h2>
        <div className="question-compose">
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                askQuestion();
              }
            }}
            placeholder="What would you like to know about this tournament?"
          />
          <button className="submit-link compact" disabled={!draft.trim()} onClick={askQuestion} type="button">Ask Question</button>
        </div>
        <div className="questions-toolbar">
          <strong>{filter === "all" ? "All Questions" : filter === "open" ? "Open Questions" : "Answered Questions"} <span>{filteredQuestions.length}</span></strong>
          <div className="detail-segmented question-filter-tabs" aria-label="Question filter">
            <button className={filter === "all" ? "selected" : ""} onClick={() => setFilter("all")} type="button">All</button>
            <button className={filter === "open" ? "selected" : ""} onClick={() => setFilter("open")} type="button">Open</button>
            <button className={filter === "answered" ? "selected" : ""} onClick={() => setFilter("answered")} type="button">Answered</button>
          </div>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="recent">Most Recent</option>
            <option value="open-first">Open first</option>
          </select>
        </div>
        <div className="question-list">
          {filteredQuestions.map((question) => (
            <article key={question.title}>
              <div className="question-avatar">{question.name.split(" ").map((part) => part[0]).join("")}</div>
              <div>
                <div className="question-title-line"><span className="question-tag">{question.tag}</span><strong>{question.title}</strong></div>
                <p>{question.body}</p>
                <div className="organizer-answer">
                  <b>Tournament Organizer</b><small>Verified Organizer</small>
                  <p>{question.state === "Answered" ? question.answer : "Waiting for organizer response."}</p>
                  {canManage && question.state === "Open" ? (
                    <div className="question-answer-form">
                      <textarea
                        value={answerDrafts[question.title] || ""}
                        onChange={(event) => setAnswerDrafts((current) => ({ ...current, [question.title]: event.target.value }))}
                        placeholder="Write the organizer answer..."
                      />
                      <button
                        className="submit-link compact"
                        disabled={!(answerDrafts[question.title] || "").trim()}
                        onClick={() => answerQuestion(question.title)}
                        type="button"
                      >
                        Post answer
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              <em className={question.state === "Open" ? "open" : ""}>{question.state}</em>
            </article>
          ))}
          {!filteredQuestions.length ? <p className="design-empty">No questions match this filter.</p> : null}
        </div>
      </section>
      <aside className="detail-side-stack">
        <section className="detail-panel faq-panel">
          <h2>Frequently Asked Questions</h2>
          {faqItems.map((item) => (
            <div className="faq-item" key={item}>
              <button onClick={() => setExpandedFaq((current) => current === item ? "" : item)} type="button">{item}<span>{expandedFaq === item ? "-" : ">"}</span></button>
              {expandedFaq === item ? <p>{item.includes("photos") ? "Submit clear media that shows the full fish, measurement, and tournament context." : "The organizer will publish official timing and instructions in announcements."}</p> : null}
            </div>
          ))}
        </section>
        <section className="detail-panel guidelines-panel"><h2>Response Guidelines</h2><span>Search first</span><span>Be specific</span><span>Stay respectful</span><span>Organizer answered</span></section>
      </aside>
    </div>
  );
}

function PreviewCard({ body, icon, title }: { body: string; icon: ReactNode; title: string }) {
  return (
    <article className="preview-card">
      <span>{icon}</span>
      <h2>{title}</h2>
      <p>{body}</p>
    </article>
  );
}

function splitDateTime(value: string) {
  const [date = "", time = ""] = value.split("T");
  return { date, time: time.slice(0, 5) };
}

function combineDateTime(date: string, time: string) {
  return date ? `${date}T${time || "09:00"}` : "";
}

function isValidIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function dateTimeIsValid(value: string) {
  const { date, time } = splitDateTime(value);
  return Boolean(date && isValidIsoDate(date) && (!time || /^\d{2}:\d{2}$/.test(time)));
}

function parseDateInput(value: string) {
  const text = value.trim();
  if (!text) return "";

  const iso = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (iso) {
    const [, rawYear, rawMonth, rawDay] = iso;
    const normalized = `${rawYear}-${rawMonth.padStart(2, "0")}-${rawDay.padStart(2, "0")}`;
    return isValidIsoDate(normalized) ? normalized : "";
  }

  const local = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/);
  if (!local) return "";

  const first = Number(local[1]);
  const second = Number(local[2]);
  const year = local[3].length === 2 ? Number(`20${local[3]}`) : Number(local[3]);
  const month = first > 12 ? second : first;
  const day = first > 12 ? first : second;
  const normalized = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return isValidIsoDate(normalized) ? normalized : "";
}

function formatDateInput(value: string) {
  if (!value) return "";
  const [, year, month, day] = value.match(/^(\d{4})-(\d{2})-(\d{2})$/) ?? [];
  return year ? `${month}/${day}/${year}` : value;
}

function dateOffset(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function openDatePicker(input: HTMLInputElement | null) {
  if (!input) return;
  try {
    if ("showPicker" in input && typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
  } catch {
    input.focus();
  }
}

function DateEntryField({
  defaultTime,
  error,
  label,
  onChange,
  value,
}: {
  defaultTime: string;
  error?: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  const pickerRef = useRef<HTMLInputElement>(null);
  const { date, time } = splitDateTime(value);
  const [draft, setDraft] = useState(formatDateInput(date));
  const [touched, setTouched] = useState(false);
  const parsedDraft = parseDateInput(draft);
  const invalid = touched && Boolean(draft.trim()) && !parsedDraft;

  useEffect(() => {
    setDraft(formatDateInput(date));
  }, [date]);

  function commitDate(raw: string) {
    const parsed = parseDateInput(raw);
    if (!parsed) return false;
    onChange(combineDateTime(parsed, time || defaultTime));
    setDraft(formatDateInput(parsed));
    return true;
  }

  return (
    <label className="field date-entry-field">
      <span>{label}</span>
      <div className="date-entry-shell">
        <input
          aria-invalid={invalid || Boolean(error)}
          aria-label={label}
          inputMode="numeric"
          onBlur={() => {
            setTouched(true);
            if (draft.trim()) commitDate(draft);
          }}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            if (parseDateInput(next)) commitDate(next);
          }}
          onFocus={() => openDatePicker(pickerRef.current)}
          placeholder="MM/DD/YYYY or YYYY-MM-DD"
          value={draft}
        />
        <input
          aria-hidden="true"
          className="native-date-input"
          ref={pickerRef}
          tabIndex={-1}
          type="date"
          value={date}
          onChange={(event) => onChange(combineDateTime(event.target.value, time || defaultTime))}
        />
        <button aria-label={`Open ${label} picker`} className="date-picker-button" onClick={() => openDatePicker(pickerRef.current)} type="button">
          <CalendarIcon />
        </button>
      </div>
      {invalid ? <span className="field-error">Enter a valid date, such as 05/29/2026 or 2026-05-29.</span> : null}
      {!invalid && error ? <span className="field-error">{error}</span> : null}
    </label>
  );
}

function DateTimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { date, time } = splitDateTime(value);
  const timeOptions = ["06:00", "07:00", "08:00", "09:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00", "22:00"];

  return (
    <div className="field date-time-field">
      <span>{label}</span>
      <div className="date-time-grid">
        <input aria-label={`${label} date`} required type="date" value={date} onChange={(event) => onChange(combineDateTime(event.target.value, time))} />
        <select aria-label={`${label} time`} value={time || "09:00"} onChange={(event) => onChange(combineDateTime(date || dateOffset(0), event.target.value))}>
          {timeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </div>
      <div className="quick-date-row">
        <button type="button" onClick={() => onChange(combineDateTime(dateOffset(0), time || "09:00"))}>Today</button>
        <button type="button" onClick={() => onChange(combineDateTime(dateOffset(1), time || "09:00"))}>Tomorrow</button>
        <button type="button" onClick={() => onChange(combineDateTime(dateOffset(7), time || "09:00"))}>Next week</button>
      </div>
    </div>
  );
}

function DateFilterField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const invalid = Boolean(value && Number.isNaN(new Date(`${value}T00:00:00`).getTime()));
  return (
    <>
      <input
        aria-invalid={invalid}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type="date"
      />
      {invalid ? <span className="field-error">Choose a valid date.</span> : null}
    </>
  );
}

function OptionInputField({
  className = "",
  dataListId,
  emptyOptionLabel,
  error,
  invalid,
  label,
  onChange,
  options,
  placeholder,
  required,
  restrictToOptions,
  value,
}: {
  className?: string;
  dataListId: string;
  emptyOptionLabel?: string;
  error?: string | null;
  invalid?: boolean;
  label: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
  restrictToOptions?: boolean;
  value: string;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const queryText = value.trim().toLowerCase();
  const exactSelected = Boolean(queryText && options.some((option) => option.toLowerCase() === queryText));
  const visibleOptions = open && exactSelected && !typing
    ? options
    : queryText
    ? options.filter((option) => option.toLowerCase().startsWith(queryText))
    : options;
  const listId = `${dataListId}-list`;
  const labelId = `${dataListId}-label`;

  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setTyping(false);
      }
    }
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  function completeCurrentValue() {
    const completed = completeOptionValue(value, options);
    if (completed !== value) {
      onChange(completed);
      return;
    }
    if (restrictToOptions && value.trim() && !optionMatches(value, options)) {
      onChange("");
    }
  }

  function selectOption(option: string) {
    onChange(option);
    setTyping(false);
    setOpen(false);
    window.setTimeout(() => {
      inputRef.current?.focus();
      if (option) inputRef.current?.select();
    }, 0);
  }

  function prepareFreshSearch(input: HTMLInputElement) {
    if (!value.trim()) return;
    window.setTimeout(() => input.select(), 0);
  }

  return (
    <div className={`field option-input-field ${className}`.trim()} ref={wrapperRef}>
      <span id={labelId}>{label}</span>
      <div className="option-input-shell">
        <input
          aria-controls={open ? listId : undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalid}
          aria-labelledby={labelId}
          autoComplete="off"
          placeholder={placeholder}
          ref={inputRef}
          required={required}
          value={value}
          onBlur={() => window.setTimeout(() => {
            if (!wrapperRef.current?.contains(document.activeElement)) {
              completeCurrentValue();
              setOpen(false);
              setTyping(false);
            }
          }, 80)}
          onChange={(event) => {
            setTyping(true);
            setOpen(true);
            onChange(event.target.value);
          }}
          onClick={(event) => {
            setTyping(false);
            setOpen(true);
            prepareFreshSearch(event.currentTarget);
          }}
          onFocus={(event) => {
            setTyping(false);
            setOpen(true);
            prepareFreshSearch(event.currentTarget);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              setTyping(false);
            }
            if (event.key === "Enter" && open && visibleOptions.length === 1) {
              event.preventDefault();
              selectOption(visibleOptions[0]);
            }
            if (event.key === "Escape") {
              setOpen(false);
              setTyping(false);
            }
          }}
        />
        <button
          aria-label={`Show ${label.toLowerCase()} options`}
          className="option-input-toggle"
          type="button"
          onClick={() => {
            setTyping(false);
            setOpen((current) => !current);
            inputRef.current?.focus();
            if (inputRef.current) prepareFreshSearch(inputRef.current);
          }}
        />
      </div>
      {open ? (
        <div className="option-dropdown" id={listId} role="listbox">
          {emptyOptionLabel ? (
            <button
              aria-selected={!queryText}
              className="option-clear-choice"
              role="option"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption("")}
            >
              {emptyOptionLabel}
            </button>
          ) : null}
          {visibleOptions.length ? visibleOptions.slice(0, 120).map((option) => (
            <button
              aria-selected={option.toLowerCase() === queryText}
              key={option}
              role="option"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(option)}
            >
              {option}
            </button>
          )) : <span className="option-empty">No matching options</span>}
        </div>
      ) : null}
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
}

function CreateTournamentView({
  busy,
  editing,
  form,
  isPro,
  onChange,
  onSubmit,
}: {
  busy: boolean;
  editing: boolean;
  form: CreateTournamentForm;
  isPro: boolean;
  onChange: (form: CreateTournamentForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const [step, setStep] = useState(0);
  const [speciesInput, setSpeciesInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [draftNotice, setDraftNotice] = useState("");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState("");
  const coverPreviewUrl = useObjectUrl(coverFile);
  const coverPreview = coverPreviewUrl || safeMediaUrl(form.imageUrl);
  const latestFormRef = useRef(form);
  const steps = ["Basics", "Schedule", "Rules", "Review & confirm"];
  const selectedSpecies = speciesList(form.species);
  const selectedTags = speciesList(form.tags);
  const createCountryOptions = countryOptions();
  const createRegionOptions = regionOptions(form.country);
  const countryIsValid = optionMatches(form.country, createCountryOptions);
  const regionIsValid = !form.region.trim() || optionMatches(form.region, createRegionOptions);
  const basicsOk = Boolean(form.name.trim() && countryIsValid && regionIsValid);
  const paymentOk = !form.paymentRequired || (
    Boolean(form.entryFee && Number(form.entryFee) > 0 && form.currency && form.paymentMethod) &&
    (form.paymentMethod !== "BANK_TRANSFER" || Boolean(form.bankAccountHolder && form.bankName)) &&
    Boolean(form.paymentInstructions || form.otherPaymentDetails || form.iban || form.accountNumber)
  );
  const speciesOk = form.allowAnySpecies || selectedSpecies.length > 0;
  const startOk = dateTimeIsValid(form.startAt);
  const endOk = dateTimeIsValid(form.endAt);
  const chronologicalOk = startOk && endOk && new Date(form.endAt).getTime() > new Date(form.startAt).getTime();
  const scheduleOk = startOk && endOk && chronologicalOk;
  const participantLimit = Number(form.participantLimit || 0);
  const planOk = isPro || (participantLimit >= 1 && participantLimit <= 5);
  const canContinue =
    step === 0 ? basicsOk :
    step === 1 ? Boolean(scheduleOk && paymentOk && planOk) :
    step === 2 ? Boolean(form.scoringMethod && form.scoringMetric && speciesOk) :
    Boolean(basicsOk && scheduleOk && paymentOk && planOk && speciesOk);
  const stepComplete = [
    basicsOk,
    Boolean(scheduleOk && paymentOk && planOk),
    Boolean(form.scoringMethod && form.scoringMetric && speciesOk),
    Boolean(basicsOk && scheduleOk && paymentOk && planOk && speciesOk),
  ];
  const selectedScoringMode = form.scoringMetric === "WEIGHT" ? "weight" : "length";
  const rankedBy = form.scoringMethod.includes("TOTAL") ? "total" : "single";
  const start = splitDateTime(form.startAt);
  const end = splitDateTime(form.endAt);
  const joinApprovalRequired = form.paymentRequired || form.joinApprovalRequired;

  useEffect(() => {
    latestFormRef.current = form;
  }, [form]);

  useEffect(() => {
    if (editing || form.name || form.region || form.startAt) return;
    try {
      const draft = readLocalStorage(CREATE_TOURNAMENT_DRAFT_KEY);
      if (draft) {
        onChange({ ...createTournamentDefaults(null), ...JSON.parse(draft) });
      } else {
        onChange({ ...form, country: form.country || defaultCountryPreference(), participantLimit: form.participantLimit || (isPro ? "" : "5") });
      }
    } catch {
      // Ignore malformed local drafts.
    }
  }, []);

  useEffect(() => {
    if (editing) return;
    writeLocalStorage(CREATE_TOURNAMENT_DRAFT_KEY, JSON.stringify(form));
  }, [editing, form]);

  function addSpecies() {
    const next = speciesInput.trim();
    if (!next) return;
    const exists = selectedSpecies.some((item) => item.toLowerCase() === next.toLowerCase());
    if (exists) {
      setSpeciesInput("");
      return;
    }
    onChange({ ...form, allowAnySpecies: false, species: [...selectedSpecies, next].join(", ") });
    setSpeciesInput("");
  }

  function removeSpecies(species: string) {
    onChange({ ...form, species: selectedSpecies.filter((item) => item !== species).join(", ") });
  }

  function addTag() {
    const nextTags = speciesList(tagInput);
    if (!nextTags.length) return;
    const normalized = new Set(selectedTags.map((item) => item.toLowerCase()));
    const merged = [...selectedTags];
    nextTags.forEach((tag) => {
      if (!normalized.has(tag.toLowerCase())) {
        normalized.add(tag.toLowerCase());
        merged.push(tag);
      }
    });
    onChange({ ...form, tags: merged.join(", ") });
    setTagInput("");
  }

  function removeTag(tag: string) {
    onChange({ ...form, tags: selectedTags.filter((item) => item !== tag).join(", ") });
  }

  function updateTournamentCountry(nextCountry: string) {
    const previousRegionOptions = regionOptions(form.country);
    const nextRegionOptions = regionOptions(nextCountry);
    const selectedKnownPreviousRegion = previousRegionOptions.some((region) => region.toLowerCase() === form.region.trim().toLowerCase());
    const regionStillFits = nextRegionOptions.some((region) => region.toLowerCase() === form.region.trim().toLowerCase());
    onChange({
      ...form,
      country: nextCountry,
      region: selectedKnownPreviousRegion && !regionStillFits ? "" : form.region,
    });
  }

  function nextStep() {
    if (!canContinue) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setStep((current) => Math.min(current + 1, steps.length - 1));
  }

  function previousStep() {
    setStep((current) => Math.max(current - 1, 0));
  }

  function saveDraft() {
    writeLocalStorage(CREATE_TOURNAMENT_DRAFT_KEY, JSON.stringify(form));
    setDraftNotice("Draft saved locally");
    window.setTimeout(() => setDraftNotice(""), 2200);
  }

  async function uploadCoverImage(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setCoverError("Choose an image file for the tournament cover.");
      return;
    }
    setCoverFile(file);
    setCoverError("");
    setCoverUploading(true);
    try {
      const uploaded = await uploadMedia(file, "cover");
      const imageUrl = safeMediaUrl(uploaded.url) || uploaded.url;
      onChange({ ...latestFormRef.current, imageUrl });
      setDraftNotice("Cover image uploaded");
      window.setTimeout(() => setDraftNotice(""), 2200);
    } catch (error) {
      setCoverError((error as Error).message || "Could not upload cover image.");
    } finally {
      setCoverUploading(false);
    }
  }

  function removeCoverImage() {
    setCoverFile(null);
    setCoverError("");
    onChange({ ...latestFormRef.current, imageUrl: "" });
  }

  function updateScoringMode(mode: "length" | "weight") {
    const total = rankedBy === "total";
    onChange({
      ...form,
      scoringMetric: mode === "length" ? "LENGTH" : "WEIGHT",
      scoringMethod: mode === "length" ? (total ? "TOTAL_LENGTH" : "LENGTH") : (total ? "TOTAL_WEIGHT" : "WEIGHT"),
    });
  }

  function updateRankedBy(value: "total" | "single") {
    const length = selectedScoringMode === "length";
    onChange({
      ...form,
      scoringMethod: length ? (value === "total" ? "TOTAL_LENGTH" : "LENGTH") : (value === "total" ? "TOTAL_WEIGHT" : "WEIGHT"),
    });
  }

  function toggleMetric(metric: "length" | "weight", checked: boolean) {
    const hasLength = metric === "length" ? checked : form.scoringMetric !== "WEIGHT";
    const hasWeight = metric === "weight" ? checked : form.scoringMetric !== "LENGTH";
    const scoringMetric = hasLength && hasWeight ? "LENGTH_AND_WEIGHT" : hasWeight ? "WEIGHT" : "LENGTH";
    onChange({ ...form, scoringMetric });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    if (step < steps.length - 1 || !canContinue) {
      event.preventDefault();
      setShowValidation(true);
      return;
    }
    const action = editing ? "save these tournament changes" : "create and publish this tournament";
    if (!window.confirm(`Ready to ${action}?`)) {
      event.preventDefault();
      return;
    }
    onSubmit(event);
  }

  return (
    <>
      <section className="submit-hero create-hero">
        <div>
          <span className="eyebrow">Tournament wizard</span>
          <h1>{editing ? "Edit" : "Create"} a <span>Tournament</span></h1>
          <p>Set up the foundation for your tournament. You can adjust these details anytime.</p>
        </div>
      </section>
      <form className="form-card create-tournament-shell" onSubmit={handleSubmit}>
        <div className="wizard-progress">
          <span>Step {step + 1} of {steps.length}</span>
          <div className="progress-track"><i style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
        </div>
        <div className="wizard-steps create-steps create-design-steps">
          {steps.map((label, index) => (
            <SubmitStepButton active={index === step} complete={stepComplete[index] && index < step} index={index} key={label} label={label} onClick={() => setStep(index)} />
          ))}
        </div>

        <div className="create-wizard-grid">
          <section className="wizard-card">
            {step === 0 ? (
              <>
                <span className="eyebrow">Step 1</span>
                <h2>Event basics</h2>
                <p>Give your tournament a name and basic details so anglers know what to expect.</p>
                <div className="form-grid">
                  <label className="field">
                    <span>Tournament name *</span>
                    <input aria-invalid={showValidation && !form.name.trim()} aria-label="Tournament name" required value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} placeholder="e.g. Summer Bass Classic 2026" />
                    {showValidation && !form.name.trim() ? <span className="field-error">Tournament name is required.</span> : null}
                  </label>
                  <OptionInputField
                    dataListId="create-country-options"
                    error={showValidation && !countryIsValid ? "Choose a country from the list." : null}
                    invalid={showValidation && !countryIsValid}
                    label="Country *"
                    options={createCountryOptions}
                    placeholder="Choose country"
                    required
                    restrictToOptions
                    value={form.country}
                    onChange={updateTournamentCountry}
                  />
                  <OptionInputField
                    dataListId="create-region-options"
                    emptyOptionLabel="No region"
                    error={showValidation && !regionIsValid ? "Choose a region from the list or leave it blank." : null}
                    invalid={showValidation && !regionIsValid}
                    label="Region / state / area"
                    options={createRegionOptions}
                    placeholder={regionPlaceholder(form.country)}
                    restrictToOptions
                    value={form.region}
                    onChange={(value) => onChange({ ...form, region: value })}
                  />
                  <div className="field tag-builder-field">
                    <span>Tournament tags</span>
                    <div className="species-builder tag-builder">
                      <div className="inline-add">
                        <input aria-label="Tournament tag" value={tagInput} onChange={(event) => setTagInput(event.target.value)} onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addTag();
                          }
                        }} placeholder="e.g. bass" />
                        <button className="clear-button compact" onClick={addTag} type="button">Add tag</button>
                      </div>
                      <div className="chip-row">
                        {selectedTags.map((tag) => (
                          <button key={tag} type="button" onClick={() => removeTag(tag)}>{tag} x</button>
                        ))}
                        {!selectedTags.length ? <span className="helper-text">Add tags like bass, summer, shore, or teams.</span> : null}
                      </div>
                    </div>
                  </div>
                </div>
                <label className="field">
                  <span>Description *</span>
                  <textarea aria-label="Tournament description" maxLength={500} rows={4} value={form.description} onChange={(event) => onChange({ ...form, description: event.target.value })} placeholder="Describe your tournament, location highlights, and what makes it special..." />
                  <small>{form.description.length} / 500</small>
                </label>
                <div className="create-cover-uploader">
                  <div className="create-cover-preview">
                    {coverPreview ? <img src={coverPreview} alt="" /> : <CameraIcon />}
                  </div>
                  <div>
                    <strong>Cover image</strong>
                    <small>Add a clear tournament photo that appears on cards and detail pages.</small>
                    {coverError ? <span className="field-error">{coverError}</span> : null}
                    <div className="create-cover-actions">
                      <label className="submit-link compact">
                        <UploadIcon /> {coverUploading ? "Uploading..." : coverPreview ? "Change cover" : "Upload cover"}
                        <input hidden accept="image/*" disabled={coverUploading} type="file" onChange={(event) => void uploadCoverImage(event.target.files?.[0] ?? null)} />
                      </label>
                      {coverPreview ? <button className="clear-button compact" disabled={coverUploading} onClick={removeCoverImage} type="button">Remove</button> : null}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            {step === 1 ? (
              <>
                <span className="eyebrow">Step 2</span>
                <h2>Schedule & access</h2>
                <p>Choose when your tournament runs and who can join.</p>
                <div className="form-grid">
                  <DateEntryField
                    defaultTime="06:00"
                    error={showValidation && !startOk ? "Start date is required." : undefined}
                    label="Start date"
                    value={form.startAt}
                    onChange={(value) => onChange({ ...form, startAt: value })}
                  />
                  <label className="field">
                    <span>Start time</span>
                    <input required type="time" value={start.time || "06:00"} onChange={(event) => onChange({ ...form, startAt: combineDateTime(start.date || dateOffset(1), event.target.value) })} />
                  </label>
                  <DateEntryField
                    defaultTime="15:00"
                    error={showValidation && !endOk ? "End date is required." : showValidation && startOk && endOk && !chronologicalOk ? "End date must be after the start date." : undefined}
                    label="End date"
                    value={form.endAt}
                    onChange={(value) => onChange({ ...form, endAt: value })}
                  />
                  <label className="field">
                    <span>End time</span>
                    <input required type="time" value={end.time || "15:00"} onChange={(event) => onChange({ ...form, endAt: combineDateTime(end.date || start.date || dateOffset(2), event.target.value) })} />
                  </label>
                  <label className="field">
                    <span>Max participants</span>
                    <input aria-invalid={showValidation && !planOk} aria-label="Maximum participants" inputMode="numeric" value={form.participantLimit} onChange={(event) => onChange({ ...form, participantLimit: event.target.value })} placeholder={isPro ? "100" : "5"} />
                    {!isPro ? <small>Free plan supports tournaments with 1-5 participants.</small> : null}
                    {showValidation && !planOk ? <span className="field-error">Set max participants from 1 to 5, or upgrade to Pro for larger events.</span> : null}
                  </label>
                  <label className="field">
                    <span>Visibility</span>
                    <select value={form.visibility} onChange={(event) => onChange({ ...form, visibility: event.target.value })}>
                      <option value="PUBLIC">Public</option>
                      <option value="PRIVATE">Private</option>
                    </select>
                  </label>
                </div>
                <label className="create-toggle-panel">
                  <span><InfoIcon /></span>
                  <div>
                    <strong>Entry fee (optional)</strong>
                    <small>Charge a fee to participate in this tournament.</small>
                  </div>
                  <input checked={form.paymentRequired} onChange={(event) => onChange({ ...form, paymentRequired: event.target.checked, joinApprovalRequired: event.target.checked ? true : form.joinApprovalRequired })} type="checkbox" />
                </label>
                {form.paymentRequired ? (
                  <div className="create-payment-grid">
                    <label className="field">
                      <span>Entry fee amount *</span>
                      <input aria-label="Entry fee" inputMode="decimal" value={form.entryFee} onChange={(event) => onChange({ ...form, entryFee: event.target.value })} placeholder="25.00" />
                    </label>
                    <label className="field">
                      <span>Currency</span>
                      <select value={form.currency} onChange={(event) => onChange({ ...form, currency: event.target.value })}>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                      </select>
                    </label>
                    <label className="field">
                      <span>Payment method</span>
                      <select value={form.paymentMethod} onChange={(event) => onChange({ ...form, paymentMethod: event.target.value })}>
                        <option value="BANK_TRANSFER">Bank transfer</option>
                        <option value="CASH">Cash on site</option>
                        <option value="OTHER">Other / manual</option>
                      </select>
                    </label>
                    {form.paymentMethod === "BANK_TRANSFER" ? (
                      <>
                        <label className="field"><span>Account holder *</span><input value={form.bankAccountHolder} onChange={(event) => onChange({ ...form, bankAccountHolder: event.target.value })} placeholder="Account holder name" /></label>
                        <label className="field"><span>Bank *</span><input value={form.bankName} onChange={(event) => onChange({ ...form, bankName: event.target.value })} placeholder="Bank name" /></label>
                        <label className="field"><span>IBAN or account number *</span><input value={form.iban || form.accountNumber} onChange={(event) => onChange({ ...form, iban: event.target.value, accountNumber: event.target.value })} placeholder="IBAN or local account" /></label>
                      </>
                    ) : null}
                  <label className="field field-wide">
                    <span>Payment instructions *</span>
                    <textarea rows={2} value={form.paymentInstructions} onChange={(event) => onChange({ ...form, paymentInstructions: event.target.value })} placeholder="Use tournament name + participant name as payment reference." />
                    {showValidation && form.paymentRequired && !paymentOk ? <span className="field-error">Complete the fee amount, payment method, and payment instructions before continuing.</span> : null}
                  </label>
                </div>
              ) : null}
                <div className="create-choice-row">
                  <button className={form.visibility === "PUBLIC" ? "selected" : ""} onClick={() => onChange({ ...form, visibility: "PUBLIC" })} type="button"><GlobeIcon /> <span><strong>Public listing</strong><small>Anyone can find and register.</small></span></button>
                  <button className={form.visibility === "PRIVATE" ? "selected" : ""} onClick={() => onChange({ ...form, visibility: "PRIVATE" })} type="button"><ShieldIcon /> <span><strong>Invite only</strong><small>Only invited people can register.</small></span></button>
                </div>
                <div className="create-choice-row">
                  <button className={!joinApprovalRequired ? "selected" : ""} disabled={form.paymentRequired} onClick={() => onChange({ ...form, joinApprovalRequired: false })} type="button"><TeamIcon /> <span><strong>Instant join</strong><small>Free public entrants are approved automatically.</small></span></button>
                  <button className={joinApprovalRequired ? "selected" : ""} onClick={() => onChange({ ...form, joinApprovalRequired: true })} type="button"><ClipboardIcon /> <span><strong>Review join requests</strong><small>{form.paymentRequired ? "Required when entry fee is enabled." : "Approve or decline participants before they compete."}</small></span></button>
                </div>
              </>
            ) : null}
            {step === 2 ? (
              <>
                <span className="eyebrow">Step 3</span>
                <h2>Rules & scoring</h2>
                <p>Set the leaderboard logic and the proof requirements participants must follow.</p>
                <div className="create-option-grid">
                  <button className={selectedScoringMode === "length" ? "selected" : ""} onClick={() => updateScoringMode("length")} type="button"><RulerIcon /><span><strong>Length</strong><small>Longest valid length</small></span></button>
                  <button className={selectedScoringMode === "weight" ? "selected" : ""} onClick={() => updateScoringMode("weight")} type="button"><ScaleIcon /><span><strong>Weight</strong><small>Heaviest valid weight</small></span></button>
                  <button className={rankedBy === "total" ? "selected" : ""} onClick={() => updateRankedBy("total")} type="button"><TrophyIcon /><span><strong>Total</strong><small>Best combined total</small></span></button>
                  <button className={rankedBy === "single" ? "selected" : ""} onClick={() => updateRankedBy("single")} type="button"><FishIcon /><span><strong>Single fish</strong><small>Biggest single fish</small></span></button>
                </div>
                <div className="species-builder">
                  <div className="species-builder-top">
                    <label className="toggle-row">
                      <input checked={form.allowAnySpecies} onChange={(event) => onChange({ ...form, allowAnySpecies: event.target.checked })} type="checkbox" />
                      Allow any species
                    </label>
                  </div>
                  {!form.allowAnySpecies ? (
                    <>
                      <div className="inline-add">
                        <input aria-label="Species name" value={speciesInput} onChange={(event) => setSpeciesInput(event.target.value)} onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addSpecies();
                          }
                        }} placeholder="Pike" />
                        <button className="clear-button compact" onClick={addSpecies} type="button">Add species</button>
                      </div>
                      <div className="chip-row">
                        {selectedSpecies.map((species) => (
                          <button key={species} type="button" onClick={() => removeSpecies(species)}>{species} x</button>
                        ))}
                        {!selectedSpecies.length ? <span className="helper-text">Add at least one allowed species.</span> : null}
                      </div>
                    </>
                  ) : <p className="helper-text">Participants can enter any fish species during catch submission.</p>}
                </div>
                <h3 className="subsection-title">Required catch data</h3>
                <div className="create-checkbox-grid">
                  <label><input checked={form.scoringMetric !== "WEIGHT"} onChange={(event) => toggleMetric("length", event.target.checked)} type="checkbox" /> Fish length</label>
                  <label><input checked={form.scoringMetric !== "LENGTH"} onChange={(event) => toggleMetric("weight", event.target.checked)} type="checkbox" /> Fish weight</label>
                  <label><input checked={form.timestampRequired} onChange={(event) => onChange({ ...form, timestampRequired: event.target.checked })} type="checkbox" /> Timestamp</label>
                  <label><input checked={form.gpsRequired} onChange={(event) => onChange({ ...form, gpsRequired: event.target.checked })} type="checkbox" /> GPS location</label>
                </div>
                <h3 className="subsection-title">Proof requirements</h3>
                <div className="create-proof-grid">
                  <label><CameraIcon /><span><strong>Photo proof</strong><small>Clear photo of fish on official measuring device</small></span><input checked={form.photoRequired} onChange={(event) => onChange({ ...form, photoRequired: event.target.checked })} type="checkbox" /></label>
                  <label><VideoIcon /><span><strong>Release video</strong><small>Short video showing fish being released</small></span><input checked={form.videoRequired} onChange={(event) => onChange({ ...form, videoRequired: event.target.checked })} type="checkbox" /></label>
                  <label><MapPinIcon /><span><strong>GPS location</strong><small>Location metadata must be included</small></span><input checked={form.gpsRequired} onChange={(event) => onChange({ ...form, gpsRequired: event.target.checked })} type="checkbox" /></label>
                </div>
                <label className="field">
                  <span>Photo source rule</span>
                  <select value={form.mediaSourceRule} onChange={(event) => onChange({ ...form, mediaSourceRule: event.target.value as CreateTournamentForm["mediaSourceRule"] })}>
                    <option value="ALLOW_UPLOAD_OR_CAMERA">Allow gallery upload or camera</option>
                    <option value="CAMERA_ONLY">Require fresh camera capture</option>
                  </select>
                </label>
                <label className="field">
                  <span>Organizer notes & rules summary</span>
                  <textarea maxLength={2000} rows={4} value={form.rules} onChange={(event) => onChange({ ...form, rules: event.target.value })} placeholder="Enter any special rules, behavior guidelines, and additional details participants should know..." />
                  <small>{form.rules.length} / 2000</small>
                </label>
              </>
            ) : null}
            {step === 3 ? (
              <>
                <span className="eyebrow">Step 4</span>
                <h2>Review & confirm</h2>
                <p>Please review all tournament details before publishing.</p>
                <div className="create-review-grid">
                  <CreateReviewCard icon={<TrophyIcon />} title="Tournament Basics" onEdit={() => setStep(0)} lines={[`Name: ${form.name || "Missing"}`, `Location: ${[form.region, form.country].filter(Boolean).join(", ") || "Missing"}`, `Tags: ${selectedTags.join(", ") || "None"}`]} />
                  <CreateReviewCard icon={<CameraIcon />} title="Cover image" onEdit={() => setStep(0)} lines={[form.imageUrl ? "Cover uploaded" : "No cover image", form.imageUrl ? "Shown on tournament cards" : "Fallback lake image will be used"]} />
                  <CreateReviewCard icon={<CalendarIcon />} title="Schedule" onEdit={() => setStep(1)} lines={[formatRange(form.startAt, form.endAt), form.startAt && form.endAt ? `${Math.max(1, Math.ceil((new Date(form.endAt).getTime() - new Date(form.startAt).getTime()) / 86400000))} days` : "Duration pending"]} />
                  <CreateReviewCard icon={<GlobeIcon />} title="Visibility" onEdit={() => setStep(1)} lines={[`${form.visibility.toLowerCase()} tournament`, form.visibility === "PUBLIC" ? "Anyone can view and register" : "Only invited anglers can register"]} />
                  <CreateReviewCard icon={<ClipboardIcon />} title="Join Access" onEdit={() => setStep(1)} lines={[joinApprovalRequired ? "Creator reviews join requests" : "Approved instantly", form.paymentRequired ? "Entry fee requires approval" : "Free tournament setting"]} />
                  <CreateReviewCard icon={<TeamIcon />} title="Participants" onEdit={() => setStep(1)} lines={[form.participantLimit ? `Max ${form.participantLimit} anglers` : "No participant limit"]} />
                  <CreateReviewCard icon={<InfoIcon />} title="Fee" onEdit={() => setStep(1)} lines={[form.paymentRequired ? `${form.entryFee || "0"} ${form.currency}` : "Free tournament", form.paymentRequired ? form.paymentMethod : "No payment required"]} />
                  <CreateReviewCard icon={<ScaleIcon />} title="Scoring" onEdit={() => setStep(2)} lines={[scoringLabel(form.scoringMetric), form.scoringMethod.replace("_", " ").toLowerCase()]} />
                  <CreateReviewCard icon={<FishLineIcon />} title="Species" onEdit={() => setStep(2)} lines={[form.allowAnySpecies ? "Any species" : selectedSpecies.join(", ") || "Missing"]} />
                  <CreateReviewCard icon={<ShieldIcon />} title="Proof Requirements" onEdit={() => setStep(2)} lines={[[form.photoRequired ? "Photo" : "", form.videoRequired ? "Video" : "", form.gpsRequired ? "GPS" : ""].filter(Boolean).join(", ") || "No extra proof", mediaRuleLabel(form.mediaSourceRule)]} />
                  <article className="create-ready-card"><CheckIcon /><div><strong>Ready to publish?</strong><small>Once created, your tournament will be live and open for registration.</small></div></article>
                  <article className="create-ready-card"><CheckIcon /><div><strong>All required information is complete</strong><small>You can edit details anytime after creation.</small></div></article>
                </div>
              </>
            ) : null}

            <div className="wizard-actions">
              <button className="clear-button compact" disabled={step === 0} onClick={previousStep} type="button"><ArrowLeftIcon /> Back</button>
              <button className="clear-button compact" onClick={saveDraft} type="button"><ClipboardIcon /> Save draft</button>
              {draftNotice ? <span className="helper-text">{draftNotice}</span> : <span className="helper-text">Your progress is saved automatically</span>}
              {step < steps.length - 1 ? (
                <button aria-disabled={!canContinue} className={canContinue ? "submit-link compact" : "submit-link compact is-disabled"} onClick={nextStep} type="button">Continue <ArrowRightIcon /></button>
              ) : (
                <button aria-disabled={!canContinue} className={canContinue ? "submit-link compact" : "submit-link compact is-disabled"} disabled={busy} type="submit">
                  {busy ? "Saving..." : editing ? "Confirm changes" : "Create tournament"} <ArrowRightIcon />
                </button>
              )}
            </div>
          </section>

          <CreateTournamentPreview form={form} selectedSpecies={selectedSpecies} step={step} />
        </div>
      </form>
    </>
  );
}

function CreateReviewCard({ icon, lines, onEdit, title }: { icon: ReactNode; lines: string[]; onEdit: () => void; title: string }) {
  return (
    <article className="create-review-card">
      <span>{icon}</span>
      <div>
        <strong>{title}</strong>
        {lines.map((line) => <small key={line}>{line}</small>)}
      </div>
      <button className="text-action" onClick={onEdit} type="button">Edit <EditIcon /></button>
    </article>
  );
}

function CreateTournamentPreview({ form, selectedSpecies, step }: { form: CreateTournamentForm; selectedSpecies: string[]; step: number }) {
  const host = "Reelmaster";
  const coverUrl = safeMediaUrl(form.imageUrl);
  const proof = [form.photoRequired ? "Photo" : "", form.videoRequired ? "Video" : "", form.gpsRequired ? "GPS" : ""].filter(Boolean).join(", ") || "Gallery / Camera";
  const basics = [
    { icon: <WaterIcon />, label: step === 1 ? "Dates" : "Water", value: step === 1 ? formatRange(form.startAt, form.endAt) : form.region || "-" },
    { icon: <GlobeIcon />, label: step === 1 ? "Location" : "Country", value: step === 1 ? [form.country, form.region].filter(Boolean).join(", ") || "-" : form.country || "-" },
    { icon: <TrophyIcon />, label: step === 1 ? "Visibility" : step === 2 ? "Ranked by" : "Scoring", value: step === 1 ? form.visibility : step === 2 ? (form.scoringMethod.includes("TOTAL") ? "Total" : "Single fish") : scoringLabel(form.scoringMetric) },
    { icon: <RulerIcon />, label: "Catch data", value: scoringLabel(form.scoringMetric) },
    { icon: <FishLineIcon />, label: "Species", value: form.allowAnySpecies ? "Any" : `${selectedSpecies.length || 0} allowed` },
    { icon: <CameraIcon />, label: "Media proof", value: proof },
    { icon: <TeamIcon />, label: "Participants", value: form.participantLimit ? `Max ${form.participantLimit}` : "No limit" },
    { icon: <InfoIcon />, label: "Fee", value: form.paymentRequired ? `${form.entryFee || "0"} ${form.currency}` : "Free" },
    { icon: <ShieldIcon />, label: "Rules", value: form.rules ? "Custom rules" : "Standard" },
  ];

  return (
    <aside className="live-preview-card create-live-preview">
      <div className="submit-preview-head">
        <span>Live preview</span>
        <small><i /> Updates as you type</small>
      </div>
      <div
        className="create-preview-art"
        style={coverUrl ? { backgroundImage: `linear-gradient(180deg, rgba(6, 28, 43, 0.18), rgba(6, 28, 43, 0.74)), url('${coverUrl}')` } : undefined}
      >
        <BadgeIcon />
        <strong>Tournament preview</strong>
      </div>
      <div className="create-preview-body">
        <h2>{form.name || "Tournament Name"}</h2>
        <p>by {host}</p>
        <div className="create-preview-metrics">
          {basics.map((item) => <span key={item.label}>{item.icon}<small>{item.label}</small><strong>{item.value}</strong></span>)}
        </div>
        <div className="submit-help-line"><InfoIcon /> This is how your tournament details will appear to anglers.</div>
      </div>
    </aside>
  );
}

function RegistrationView({
  onLegal,
  onLogin,
  onLoginClick,
}: {
  onLegal: (view: View) => void;
  onLogin: (session: { token: string; user: AuthUser }) => void;
  onLoginClick: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [country, setCountry] = useState(() => readLocalStorage("countryPreference"));
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!acceptedTerms) {
      setError("Please agree to the terms before creating an account.");
      return;
    }
    setBusy(true);
    try {
      if (country) writeLocalStorage("countryPreference", country);
      const session = await registerAccount({ displayName, email, password });
      onLogin(session);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-screen signup-screen">
      <section className="auth-story-panel">
        <span className="auth-pill"><TeamIcon /> Participant sign up</span>
        <h1>Create your account</h1>
        <p>Participant accounts can be created directly. Creator access is granted through organizer permissions.</p>
        <span className="auth-accent-line" />
        <div className="auth-feature-list">
          <AuthFeature icon={<TrophyIcon />} title="Join tournaments" body="Find and join tournaments that match your passion." />
          <AuthFeature icon={<FishLineIcon />} title="Submit your catches" body="Easily submit your catches and track your progress." />
          <AuthFeature icon={<ChartIcon />} title="Track leaderboard rankings" body="See how you rank against other anglers in real time." />
          <AuthFeature icon={<BellIcon />} title="Stay informed" body="Receive important updates from tournament organizers." />
        </div>
      </section>

      <form className="auth-card signup-auth-card" onSubmit={handleSubmit}>
        <h2>Create your account</h2>
        <p>All fields are required unless marked optional.</p>
        {error ? <div className="notice error">{error}</div> : null}
        <div className="auth-form-grid">
          <AuthInput autoComplete="name" icon={<UserIcon />} label="Display name" placeholder="Name shown on leaderboards" required value={displayName} onChange={setDisplayName} />
          <AuthInput autoComplete="email" icon={<MailIcon />} label="Email" placeholder="you@example.com" required type="email" value={email} onChange={setEmail} />
          <AuthInput
            autoComplete="new-password"
            icon={<LockIcon />}
            label="Password"
            minLength={8}
            placeholder="At least 8 characters"
            required
            rightAction={<button aria-label={showPassword ? "Hide password" : "Show password"} type="button" onClick={() => setShowPassword((shown) => !shown)}><EyeIcon /></button>}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={setPassword}
          />
          <AuthInput
            autoComplete="new-password"
            icon={<LockIcon />}
            label="Confirm password"
            minLength={8}
            placeholder="At least 8 characters"
            required
            rightAction={<button aria-label={showConfirmPassword ? "Hide password" : "Show password"} type="button" onClick={() => setShowConfirmPassword((shown) => !shown)}><EyeIcon /></button>}
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={setConfirmPassword}
          />
          <label className="auth-field wide-auth-field">
            <span>Country <small>(optional)</small></span>
            <div className="auth-input-shell">
              <GlobeIcon />
              <select value={country} onChange={(event) => setCountry(event.target.value)}>
                <option value="">Select your country</option>
                <option value="United States">United States</option>
                <option value="Latvia">Latvia</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
              </select>
            </div>
          </label>
          <label className="auth-check-row wide-auth-field">
            <input checked={acceptedTerms} required type="checkbox" onChange={(event) => setAcceptedTerms(event.target.checked)} />
            <span>
              I agree to the <button className="inline-legal-link" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onLegal("terms"); }} type="button">Terms of Service</button> and <button className="inline-legal-link" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onLegal("privacy"); }} type="button">Privacy Policy</button>.
            </span>
          </label>
        </div>
        <button className="submit-link auth-submit" disabled={busy} type="submit">
          {busy ? "Creating account..." : "Create account"} <ArrowRightIcon />
        </button>
        <AuthDivider />
        <button className="auth-switch-link" type="button" onClick={onLoginClick}>Already have an account? <strong>Log in</strong></button>
      </form>
      <p className="auth-secure-note"><LockIcon /> Your data is secure and never shared with third parties.</p>
    </section>
  );
}

function SubmitCatchView({
  busy,
  form,
  onChange,
  onSubmit,
  tournaments,
  user,
  onSettings,
}: {
  busy: boolean;
  form: SubmissionForm;
  onChange: (form: SubmissionForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  tournaments: Tournament[];
  user: AuthUser | null;
  onSettings: () => void;
}) {
  const [step, setStep] = useState(0);
  const [locationBusy, setLocationBusy] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const selectedTournament = tournaments.find((tournament) => tournament.id === form.tournamentId) ?? tournaments[0];
  const metric = selectedTournament?.scoringMetric || "LENGTH";
  const needsLength = metric === "LENGTH" || metric === "LENGTH_AND_WEIGHT";
  const needsWeight = metric === "WEIGHT" || metric === "LENGTH_AND_WEIGHT";
  const needsPhoto = Boolean(selectedTournament?.photoRequired);
  const needsVideo = Boolean(selectedTournament?.videoRequired);
  const needsGps = Boolean(selectedTournament?.gpsRequired);
  const participantName = user?.displayName || user?.email?.split("@")[0] || "";
  const mediaIsCameraOnly = selectedTournament?.mediaSourceRule === "CAMERA_ONLY";
  const steps = ["Tournament", "Catch", "Media", "Location", "Review"];
  const projected = projectedScore(metric, form.length, form.weight);
  const selectedTournamentId = form.tournamentId || selectedTournament?.id || "";
  const mediaCount = [form.photoFile, form.videoFile].filter(Boolean).length;
  const hasLocation = Boolean(form.lat && form.lng);
  const shareExactPoint = Boolean(hasLocation && form.shareExactPoint);
  const reviewReady = Boolean(
    selectedTournamentId
    && participantName
    && form.species
    && (!needsLength || form.length)
    && (!needsWeight || form.weight)
    && (!needsPhoto || form.photoFile)
    && (!needsVideo || form.videoFile)
    && (!needsGps || shareExactPoint)
  );
  const photoPreviewUrl = useObjectUrl(form.photoFile);
  const canContinue =
    step === 0 ? Boolean(selectedTournamentId) :
    step === 1 ? Boolean(participantName && form.species && (!needsLength || form.length) && (!needsWeight || form.weight)) :
    step === 2 ? Boolean((!needsPhoto || form.photoFile) && (!needsVideo || form.videoFile)) :
    step === 3 ? Boolean(!needsGps || shareExactPoint) :
    reviewReady;
  const stepComplete = [
    Boolean(selectedTournamentId),
    Boolean(participantName && form.species && (!needsLength || form.length) && (!needsWeight || form.weight)),
    Boolean((!needsPhoto || form.photoFile) && (!needsVideo || form.videoFile)),
    Boolean(!needsGps || shareExactPoint),
    reviewReady,
  ];

  function captureLocation() {
    setLocationError(null);
    if (!navigator.geolocation) {
      setLocationError("Your browser does not support GPS location capture.");
      return;
    }
    setLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange({
          ...form,
          lat: String(position.coords.latitude),
          lng: String(position.coords.longitude),
          gpsAccuracy: String(Math.round(position.coords.accuracy)),
          gpsCapturedAt: new Date(position.timestamp).toISOString(),
          shareExactPoint: true,
        });
        setLocationBusy(false);
      },
      (error) => {
        setLocationError(error.message || "Could not capture GPS location.");
        setLocationBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  }

  function nextStep() {
    if (!canContinue) return;
    setStep((current) => Math.min(current + 1, steps.length - 1));
  }

  function handleWizardSubmit(event: FormEvent<HTMLFormElement>) {
    if (step < steps.length - 1) {
      event.preventDefault();
      nextStep();
      return;
    }
    onSubmit(event);
  }

  function editStep(index: number) {
    setStep(index);
  }

  async function selectVideoFile(file: File | null) {
    setMediaError(null);
    if (!file) {
      onChange({ ...form, videoFile: null, videoUrl: "" });
      return;
    }
    try {
      const duration = await readVideoDuration(file);
      if (duration > MAX_RELEASE_VIDEO_SECONDS + 0.25) {
        setMediaError("Release videos must be 30 seconds or shorter.");
        onChange({ ...form, videoFile: null, videoUrl: "" });
        return;
      }
      onChange({ ...form, videoFile: file, videoUrl: "" });
    } catch (error) {
      setMediaError(error instanceof Error ? error.message : "Could not read the selected release video.");
      onChange({ ...form, videoFile: null, videoUrl: "" });
    }
  }

  return (
    <>
      <section className="submit-hero">
        <div>
          <span className="eyebrow">Live submission</span>
          <h1>Submit a <span>Catch</span></h1>
          <p>Follow the steps below to submit your catch for official scoring. All submissions are reviewed before being approved.</p>
        </div>
        <article className="score-card">
          <small>Projected score</small>
          <strong>{projected ? `${projected} ${scoreUnit(metric)}` : "-"}</strong>
          <span>Complete all steps to see projection</span>
          <BadgeIcon />
        </article>
      </section>
      <form className="form-card submit-catch-shell" onSubmit={handleWizardSubmit}>
        <div className="wizard-steps submit-catch-steps">
          {steps.map((label, index) => (
            <SubmitStepButton
              active={index === step}
              complete={stepComplete[index] && index < step}
              index={index}
              key={label}
              label={label}
              onClick={() => setStep(index)}
            />
          ))}
        </div>

        <div className="submit-grid">
          <section className="wizard-card">
            {step === 0 ? (
              <>
                <span className="eyebrow">Step 1 of 5</span>
                <h2>Choose the tournament</h2>
                <p>Only tournaments that are open for submissions and match your approved entries are shown.</p>
                <label className="submit-select-card">
                  <span><TrophyIcon /></span>
                  <select disabled={tournaments.length === 0} value={selectedTournamentId} onChange={(event) => onChange({ ...form, tournamentId: event.target.value, species: "", length: "", weight: "", photoFile: null, videoFile: null, photoUrl: "", videoUrl: "" })}>
                    {tournaments.length === 0 ? <option value="">No tournaments available</option> : null}
                    {tournaments.map((tournament) => (
                      <option key={tournament.id} value={tournament.id}>
                        {tournamentName(tournament)}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedTournament ? (
                  <>
                  <div className="rule-summary submit-rule-summary">
                    <span><ScaleIcon /><small>Scoring mode</small><strong>{scoringLabel(metric)}</strong><em>{metric === "WEIGHT" ? "Heaviest valid catch determines score." : "Longest valid length determines score."}</em></span>
                    <span><FishLineIcon /><small>Allowed species</small><strong>{selectedTournament.allowAnySpecies ? "Any species" : selectedTournament.species.join(", ") || "Not configured"}</strong><em>Eligible catches follow organizer species rules.</em></span>
                    <span><CameraIcon /><small>Proof method</small><strong>{[needsPhoto ? "Photo" : "", needsVideo ? "Video" : "", mediaRuleLabel(selectedTournament.mediaSourceRule)].filter(Boolean).join(" / ")}</strong><em>Photo or video proof required for review.</em></span>
                  </div>
                  <div className="submit-info-band">
                    <span><CalendarIcon /></span>
                    <div>
                      <small>Event timing</small>
                      <strong>{formatRange(selectedTournament.startAt, selectedTournament.endAt)}</strong>
                      <em>Submissions close in {timeLeftLabel(selectedTournament.endAt)}</em>
                    </div>
                    <div>
                      <small>Rules summary</small>
                      <p>{selectedTournament.rules || "Follow all tournament rules and ensure proof is clear and shows the entire fish."}</p>
                    </div>
                  </div>
                  </>
                ) : null}
              </>
            ) : null}
            {step === 1 ? (
              <>
                <span className="eyebrow">Step 2 of 5</span>
                <h2>Catch details</h2>
                <p>Enter the details about your catch. Required fields help ensure accurate scoring.</p>
                <div className="profile-summary submit-profile-card">
                  <span>{(participantName || "AD").slice(0, 2).toUpperCase()}</span>
                  <div>
                    <strong>{participantName || "Display name missing"}</strong>
                    <small>{user?.email || "No account email"}</small>
                  </div>
                  <button className="clear-button compact" type="button" onClick={onSettings}><UserIcon /> Update profile</button>
                </div>
                {!user?.displayName ? <p className="helper-text">Add your display name in Account Settings. Reelmaster will use your email prefix until then.</p> : null}
                <div className="form-grid">
                  <label className="field">
                    <span>Species</span>
                    {!selectedTournament?.allowAnySpecies && selectedTournament?.species.length ? (
                      <select required value={form.species} onChange={(event) => onChange({ ...form, species: event.target.value })}>
                        <option value="">Choose allowed species</option>
                        {selectedTournament.species.map((species) => <option key={species} value={species}>{species}</option>)}
                      </select>
                    ) : (
                      <input required value={form.species} onChange={(event) => onChange({ ...form, species: event.target.value })} placeholder="Species" />
                    )}
                  </label>
                  {needsLength ? (
                    <label className="field">
                      <span>Length (cm)</span>
                      <input required inputMode="decimal" value={form.length} onChange={(event) => onChange({ ...form, length: event.target.value })} placeholder="46.0" />
                    </label>
                  ) : null}
                  <label className="field">
                    <span>Weight (kg){needsWeight ? "" : " (optional)"}</span>
                    <input required={needsWeight} inputMode="decimal" value={form.weight} onChange={(event) => onChange({ ...form, weight: event.target.value })} placeholder="3.0" />
                  </label>
                </div>
                <label className="field">
                  <span>Catch date & time</span>
                  <input disabled value={formatDateTime(new Date().toISOString())} />
                </label>
                <label className="field">
                  <span>Notes (optional)</span>
                  <textarea maxLength={250} value={form.notes} onChange={(event) => onChange({ ...form, notes: event.target.value })} placeholder="Nice morning catch on light tackle." rows={4} />
                  <small>{form.notes.length} / 250</small>
                </label>
                <div className="submit-help-line"><InfoIcon /> {needsWeight ? "Length and weight are required for this tournament." : "The longest valid length determines the score. Weight is optional if the tournament does not require it."}</div>
              </>
            ) : null}
            {step === 2 ? (
              <>
                <span className="eyebrow">Step 3 of 5</span>
                <h2>Attach proof</h2>
                <p>Upload clear proof of your catch. Photos and short release videos help ensure accurate scoring.</p>
                <div className="upload-zone submit-upload-zone">
                  <UploadIcon />
                  <strong>Drag and drop files here</strong>
                  <span>JPG, PNG, or video files. Photos are compressed before upload. Release videos must be 30 seconds or shorter.</span>
                  <div className="media-actions">
                    {!mediaIsCameraOnly ? (
                      <label className="submit-link compact">
                        <UploadIcon /> Upload photo
                        <input hidden accept="image/*" type="file" onChange={(event) => {
                          const file = event.target.files?.[0] ?? null;
                          setMediaError(null);
                          onChange({ ...form, photoFile: file, mediaCaptureMode: file ? "gallery" : form.mediaCaptureMode, photoUrl: "" });
                        }} />
                      </label>
                    ) : null}
                    <label className="clear-button compact">
                      <CameraIcon /> Take photo
                      <input hidden accept="image/*" capture="environment" type="file" onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        setMediaError(null);
                        onChange({ ...form, photoFile: file, mediaCaptureMode: file ? "camera" : form.mediaCaptureMode, photoUrl: "" });
                      }} />
                    </label>
                    <label className="clear-button compact">
                      <VideoIcon /> Upload release video
                      <input hidden accept="video/*" type="file" onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        void selectVideoFile(file);
                      }} />
                    </label>
                  </div>
                </div>
                {mediaError ? <div className="notice error media-validation-notice">{mediaError}</div> : null}
                <div className="submit-media-grid">
                  {form.photoFile ? (
                    <SubmitMediaCard
                      image={photoPreviewUrl}
                      label="Main photo"
                      name={form.photoFile.name}
                      onRemove={() => onChange({ ...form, photoFile: null, photoUrl: "", mediaCaptureMode: "" })}
                      size={`${fileSizeLabel(form.photoFile.size)} before compression`}
                    />
                  ) : null}
                  {form.videoFile ? (
                    <SubmitMediaCard
                      image=""
                      label="Release video"
                      name={form.videoFile.name}
                      onRemove={() => onChange({ ...form, videoFile: null, videoUrl: "" })}
                      size={`${fileSizeLabel(form.videoFile.size)} before compression`}
                      type="video"
                    />
                  ) : null}
                </div>
                <div className="submit-help-line"><InfoIcon /> Clear, full-body fish proof is required. URL proof is no longer accepted from this form.</div>
              </>
            ) : null}
            {step === 3 ? (
              <>
                <span className="eyebrow">Step 4 of 5</span>
                <h2>Catch location</h2>
                <p>Save your catch location using your browser GPS or place the pin manually on the map.</p>
                {locationError ? <div className="notice error">{locationError}</div> : null}
                <div className="location-actions">
                  <button className="submit-link compact" disabled={locationBusy} onClick={captureLocation} type="button">
                    <MapPinIcon /> {locationBusy ? "Capturing..." : form.lat ? "Refresh GPS" : "Use current GPS"}
                  </button>
                  {form.gpsAccuracy ? <span>Accuracy: {form.gpsAccuracy} m</span> : null}
                  <label className="exact-point-toggle">
                    <span>
                      <b>Share exact point</b>
                      <small>{needsGps ? "Required by this tournament for review." : "Optional. Turn off to hide exact coordinates."}</small>
                    </span>
                    <input
                      checked={shareExactPoint}
                      disabled={!hasLocation || needsGps}
                      onChange={(event) => onChange({ ...form, shareExactPoint: event.target.checked })}
                      type="checkbox"
                    />
                  </label>
                </div>
                <CatchMapPreview
                  lat={form.lat ? Number(form.lat) : null}
                  lng={form.lng ? Number(form.lng) : null}
                  onMove={(lat, lng) => onChange({ ...form, lat: String(lat), lng: String(lng), shareExactPoint: true })}
                />
                <div className="submit-location-summary">
                  <span><MapPinIcon /><small>Saved coordinates</small><strong>{hasLocation ? `${Number(form.lat).toFixed(5)} N, ${Number(form.lng).toFixed(5)} E` : "Not set"}</strong></span>
                  <span><GlobeIcon /><small>Detected locality</small><strong>{selectedTournament ? tournamentLocation(selectedTournament) : "Tournament location"}</strong></span>
                </div>
                <div className="submit-help-line"><ShieldIcon /> Exact coordinates are used for tournament verification only when this toggle is on.</div>
              </>
            ) : null}
            {step === 4 ? (
              <>
                <span className="eyebrow">Step 5 of 5</span>
                <h2>Review & submit</h2>
                <p>Please review all details before submitting your catch.</p>
                <div className="submit-review-list">
                  <SubmitReviewRow icon={<TrophyIcon />} title="Tournament" onEdit={() => editStep(0)}>
                    <span><small>Tournament</small>{selectedTournament ? tournamentName(selectedTournament) : "No tournament selected"}</span>
                    <span><small>Scoring mode</small>{scoringLabel(metric)}</span>
                    <span><small>Allowed species</small>{selectedTournament?.allowAnySpecies ? "Any species" : selectedTournament?.species.join(", ") || "Not configured"}</span>
                  </SubmitReviewRow>
                  <SubmitReviewRow icon={<FishLineIcon />} title="Catch details" onEdit={() => editStep(1)}>
                    <span><small>Species</small>{form.species || "Missing"}</span>
                    <span><small>Length</small>{form.length || "-"} cm</span>
                    <span><small>Weight</small>{form.weight || "-"} kg</span>
                    <span><small>Note</small>{form.notes || "-"}</span>
                  </SubmitReviewRow>
                  <SubmitReviewRow icon={<CameraIcon />} title="Media proof" onEdit={() => editStep(2)}>
                    <span className="review-media-preview">{photoPreviewUrl ? <img src={photoPreviewUrl} alt="" /> : <CameraIcon />}</span>
                    <span><small>Photo</small>{form.photoFile?.name || (needsPhoto ? "Missing" : "Optional")}</span>
                    <span><small>Release video</small>{form.videoFile?.name || (needsVideo ? "Missing" : "Optional")}</span>
                  </SubmitReviewRow>
                  <SubmitReviewRow icon={<MapPinIcon />} title="Location" onEdit={() => editStep(3)}>
                    <span><small>Saved location</small>{hasLocation ? `${Number(form.lat).toFixed(5)}, ${Number(form.lng).toFixed(5)}` : "Not set"}</span>
                    <span><small>Accuracy</small>{form.gpsAccuracy ? `${form.gpsAccuracy} m` : "-"}</span>
                    <span><small>Exact point</small>{shareExactPoint ? "Shared with organizers" : hasLocation ? "Hidden from submission" : "Not set"}</span>
                  </SubmitReviewRow>
                </div>
                <div className="notice">All submissions are reviewed by tournament admins before approval. You will be notified about the status of your submission.</div>
              </>
            ) : null}

            <div className="wizard-actions">
              <button className="clear-button compact" disabled={step === 0} onClick={() => setStep((current) => Math.max(current - 1, 0))} type="button"><ArrowLeftIcon /> Back</button>
              {step < steps.length - 1 ? (
                <button className="submit-link compact" disabled={!canContinue || tournaments.length === 0} onClick={nextStep} type="button">Continue <ArrowRightIcon /></button>
              ) : (
                <button className="submit-link compact" disabled={!canContinue || tournaments.length === 0 || busy} type="submit">{busy ? "Submitting..." : "Submit catch"} <ArrowRightIcon /></button>
              )}
            </div>
          </section>

          <SubmitLivePreview
            form={form}
            hasLocation={hasLocation}
            mediaCount={mediaCount}
            projected={projected}
            scoreUnit={scoreUnit(metric)}
            selectedTournament={selectedTournament}
            step={step}
          />
        </div>
      </form>
    </>
  );
}

function SubmitStepButton({ active, complete, index, label, onClick }: { active: boolean; complete: boolean; index: number; label: string; onClick: () => void }) {
  return (
    <button className={`${active ? "selected" : ""} ${complete ? "complete" : ""}`} onClick={onClick} type="button">
      <span>{complete ? <CheckIcon /> : index + 1}</span>{label}
    </button>
  );
}

function SubmitLivePreview({
  form,
  hasLocation,
  mediaCount,
  projected,
  scoreUnit,
  selectedTournament,
  step,
}: {
  form: SubmissionForm;
  hasLocation: boolean;
  mediaCount: number;
  projected: number;
  scoreUnit: string;
  selectedTournament?: Tournament;
  step: number;
}) {
  return (
    <aside className="live-preview-card submit-live-preview">
      <div className="submit-preview-head">
        <span className="eyebrow">Live preview</span>
        <small><i /> {step === 4 ? "Ready to submit" : "Auto-saving"}</small>
      </div>
      <div className="submit-preview-event">
        <span><TrophyIcon /></span>
        <div>
          <h2>{selectedTournament ? tournamentName(selectedTournament) : "Choose a tournament"}</h2>
          <p>Hosted by Reelmaster</p>
        </div>
      </div>
      <div className="submit-preview-section">
        <span className="eyebrow">Projection</span>
        <SubmitOverviewRow icon={<ScaleIcon />} label="Projected Score" value={projected ? `${projected} ${scoreUnit}` : "-"} />
      </div>
      <div className="submit-preview-section">
        <span className="eyebrow">Catch overview</span>
        <SubmitOverviewRow icon={<FishLineIcon />} label="Species" value={form.species || "-"} />
        <SubmitOverviewRow icon={<RulerIcon />} label="Length" value={form.length ? `${form.length} cm` : "- cm"} />
        <SubmitOverviewRow icon={<ScaleIcon />} label="Weight" value={form.weight ? `${form.weight} kg` : "- kg"} />
        <SubmitOverviewRow icon={<CameraIcon />} label="Media" value={mediaCount ? `${mediaCount} file${mediaCount === 1 ? "" : "s"}` : "Not added"} />
        <SubmitOverviewRow icon={<MapPinIcon />} label="Location" value={hasLocation ? "Saved" : "Not set"} />
        {step === 4 ? <SubmitOverviewRow icon={<ClipboardIcon />} label="Note" value={form.notes || "-"} /> : null}
      </div>
    </aside>
  );
}

function SubmitOverviewRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="submit-overview-row">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SubmitMediaCard({ image, label, name, onRemove, size, type = "photo" }: { image: string; label: string; name: string; onRemove: () => void; size: string; type?: "photo" | "video" }) {
  return (
    <article className="submit-media-card">
      {image ? <img src={image} alt="" /> : <div className="submit-media-icon">{type === "video" ? <VideoIcon /> : <CameraIcon />}</div>}
      <span>{label}</span>
      <div>
        <strong>{name}</strong>
        <small>{size}</small>
      </div>
      <button aria-label={`Remove ${label}`} onClick={onRemove} type="button">x</button>
    </article>
  );
}

function SubmitReviewRow({ children, icon, onEdit, title }: { children: ReactNode; icon: ReactNode; onEdit?: () => void; title: string }) {
  return (
    <article className="submit-review-row">
      <span>{icon}</span>
      <div>
        <header>
          <small>{title}</small>
          {onEdit ? <button className="text-action" onClick={onEdit} type="button">Edit <EditIcon /></button> : null}
        </header>
        <div className="submit-review-row-body">{children}</div>
      </div>
    </article>
  );
}

function fileSizeLabel(bytes: number) {
  if (!bytes) return "0 MB";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function useObjectUrl(file: File | null) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file) {
      setUrl("");
      return;
    }
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url;
}

function TournamentSelect({
  allowAll = false,
  formTournamentId,
  onChange,
  tournaments,
}: {
  allowAll?: boolean;
  formTournamentId: string;
  onChange: (tournamentId: string) => void;
  tournaments: Tournament[];
}) {
  return (
    <label className="field">
      <span>Tournament</span>
      <select disabled={tournaments.length === 0} value={formTournamentId} onChange={(event) => onChange(event.target.value)}>
        {allowAll ? <option value="">All tournaments</option> : null}
        {tournaments.length === 0 ? <option value="">No tournaments available</option> : null}
        {tournaments.map((tournament) => (
          <option key={tournament.id} value={tournament.id}>
            {tournamentName(tournament)}
          </option>
        ))}
      </select>
    </label>
  );
}

function LoginView({
  onInstallApp,
  onLegal,
  onLogin,
  onSignup,
  showInstallPrompt,
}: {
  onInstallApp: () => void;
  onLegal: (view: View) => void;
  onLogin: (session: { token: string; user: AuthUser }) => void;
  onSignup: () => void;
  showInstallPrompt: boolean;
}) {
  const [email, setEmail] = useState(() => rememberedLoginEmail());
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => Boolean(rememberedLoginEmail()));
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const loginId = email.trim();
    setError(null);
    setInfo(null);
    if (!loginId) {
      setError("Enter your email or username.");
      return;
    }
    if (isCurrentSiteAddress(loginId)) {
      setError("Enter your account email or username, not the website address.");
      return;
    }
    if (!password) {
      setError("Enter your password.");
      return;
    }
    setEmail(loginId);
    setBusy(true);
    try {
      const session = await login(loginId, password);
      setRememberedLoginEmail(loginId, rememberMe);
      onLogin(session);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-screen login-screen">
      <aside className="auth-story-panel login-story-panel">
        <span className="auth-pill"><FishIcon /> Angler portal</span>
        <h1>Cast off into your next tournament.</h1>
        <p>Log in to see your live leaderboards, manage submissions, and dive into your catch archive.</p>
        <span className="auth-accent-line" />
        <div className="auth-feature-list">
          <AuthFeature icon={<ShieldIcon />} title="Secure & connected" body="Your Reelmaster account is securely connected to WordPress." />
          <AuthFeature icon={<TeamIcon />} title="Role-aware access" body="Get the right dashboards and tools for your role." />
          <AuthFeature icon={<ChartIcon />} title="Tournaments & dashboards" body="Manage tournaments, review submissions, and track results all in one place." />
        </div>
      </aside>

      <form className="auth-card login-auth-card" noValidate onSubmit={handleSubmit}>
        <h1>Welcome back</h1>
        <p>Log in to access tournaments, manage submissions, and view your role-aware dashboards.</p>
        {error ? <div className="notice error">{error}</div> : null}
        {info ? <div className="notice success">{info}</div> : null}
        <div className="auth-form-grid single-column">
          <AuthInput
            autoCapitalize="none"
            autoComplete="username"
            icon={<MailIcon />}
            inputMode="email"
            label="Email or username"
            name="username"
            placeholder="you@example.com"
            required
            spellCheck={false}
            type="text"
            value={email}
            onChange={setEmail}
          />
          <AuthInput
            autoComplete="current-password"
            icon={<LockIcon />}
            label="Password"
            name="password"
            placeholder="Password"
            required
            rightAction={<button aria-label={showPassword ? "Hide password" : "Show password"} type="button" onClick={() => setShowPassword((shown) => !shown)}><EyeIcon /></button>}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={setPassword}
          />
          <div className="login-options-row">
            <label className="auth-check-row">
              <input checked={rememberMe} type="checkbox" onChange={(event) => setRememberMe(event.target.checked)} />
              <span>Remember me</span>
            </label>
            <button type="button" onClick={() => setInfo("Password reset is handled by the connected WordPress account. Use your WordPress reset link or contact an organizer.")}>Forgot password?</button>
          </div>
        </div>
        <button className="submit-link auth-submit" disabled={busy} type="submit">
          {busy ? "Logging in..." : "Log in"}
        </button>
        {showInstallPrompt ? (
          <button className="install-inline-card" onClick={onInstallApp} type="button">
            <SmartphoneIcon />
            <span>
              <strong>Install Reelmaster on this phone</strong>
              <small>Add it to your home screen for faster access.</small>
            </span>
            <span className="install-card-chevron" aria-hidden="true"><ArrowRightIcon /></span>
          </button>
        ) : null}
        <AuthDivider />
        <button className="auth-switch-link" type="button" onClick={onSignup}>Don't have an account? <strong>Create participant account</strong></button>
      </form>
      <p className="auth-terms-note login-terms-note">
        <span className="auth-terms-icon" aria-hidden="true"><LockIcon /></span>
        <span>By logging in, you agree to our <button className="inline-legal-link" onClick={() => onLegal("terms")} type="button">Terms of Service</button> and acknowledge our <button className="inline-legal-link" onClick={() => onLegal("privacy")} type="button">Privacy Policy</button>.</span>
      </p>
    </section>
  );
}

function AuthInput({
  autoCapitalize,
  autoComplete,
  icon,
  inputMode,
  label,
  minLength,
  name,
  onChange,
  placeholder,
  required,
  rightAction,
  spellCheck,
  type = "text",
  value,
}: {
  autoCapitalize?: InputHTMLAttributes<HTMLInputElement>["autoCapitalize"];
  autoComplete?: string;
  icon: ReactNode;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  label: string;
  minLength?: number;
  name?: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  rightAction?: ReactNode;
  spellCheck?: boolean;
  type?: string;
  value: string;
}) {
  return (
    <label className="auth-field">
      <span>{label}</span>
      <div className="auth-input-shell">
        {icon}
        <input
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          inputMode={inputMode}
          minLength={minLength}
          name={name}
          placeholder={placeholder}
          required={required}
          spellCheck={spellCheck}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {rightAction}
      </div>
    </label>
  );
}

function AuthFeature({ body, icon, title }: { body: string; icon: ReactNode; title: string }) {
  return (
    <div className="auth-feature">
      <span>{icon}</span>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </div>
  );
}

function AuthDivider() {
  return (
    <div className="auth-divider">
      <span />
      <b>or</b>
      <span />
    </div>
  );
}

function PlaceholderView({ title, body }: { title: string; body: string }) {
  return (
    <article className="empty-card">
      <h2>{title}</h2>
      <p>{body}</p>
    </article>
  );
}

function Skeleton() {
  return (
    <div className="skeleton-card">
      <span />
      <span />
      <span />
    </div>
  );
}

function EmptyCard({ title, body }: { title: string; body: string }) {
  return (
    <article className="empty-card">
      <h2>{title}</h2>
      <p>{body}</p>
    </article>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
      <path d="m4 14 1.6 1 .5 1.2-.4 1.8 2 2 1.8-.4 1.2.5 1 1.6h2.6l1-1.6 1.2-.5 1.8.4 2-2-.4-1.8.5-1.2L22 14v-4l-1.6-1-.5-1.2.4-1.8-2-2-1.8.4-1.2-.5-1-1.6h-2.6l-1 1.6-1.2.5L7.7 4l-2 2 .4 1.8-.5 1.2L4 10v4Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2A19.8 19.8 0 0 1 3 5.2 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1L8 11.4a16 16 0 0 0 4.6 4.6l2.1-2.1a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.5.6A2 2 0 0 1 22 16.9Z" />
    </svg>
  );
}

function SmartphoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="7" y="2.5" width="10" height="19" rx="2.4" />
      <path d="M10.5 5h3" />
      <path d="M11.4 18.2h1.2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function DeviceIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="11" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 16v5" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3" />
      <path d="M12 19v3" />
      <path d="M2 12h3" />
      <path d="M19 12h3" />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3a9 9 0 0 0 0 18h1.5a2 2 0 0 0 1.4-3.4 1.7 1.7 0 0 1 1.2-2.9H17a4 4 0 0 0 4-4C21 6.4 17 3 12 3Z" />
      <path d="M7.5 10h.01" />
      <path d="M10 7h.01" />
      <path d="M14 7h.01" />
      <path d="M16.5 10h.01" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8 16v-4" />
      <path d="M12 16V8" />
      <path d="M16 16v-6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function FishIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 12c4-5 9-5 13-3 1-1 3-2 5-2v3l-2 1c0 .7 0 1.3-.2 2 .7.6 1.2 1.3 1.2 2l-2 1c-.4.9-1 1.7-1.8 2.4l.8 2-3-1c-2.2 1-4.5 1.6-7 1.6-3 0-5-1.5-5-3l1.5-.5C3.6 16 3 14.6 3 12Zm14 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M5 5H3v3a4 4 0 0 0 4 4" />
      <path d="M19 5h2v3a4 4 0 0 1-4 4" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m3 11 9-8 9 8" />
      <path d="M5 10v10h14V10" />
      <path d="M9 20v-6h6v6" />
    </svg>
  );
}

function FishLineIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 12c3.5-4 8.5-4 12 0-3.5 4-8.5 4-12 0Z" />
      <path d="m6 12-3-3v6l3-3Z" />
      <path d="M15 11h.01" />
      <path d="M18 12c1.4-1.2 2.4-3 3-5" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s7-6 7-12a7 7 0 0 0-14 0c0 6 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 15v3a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v-3" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function CreateNavIcon() {
  return (
    <svg className="create-nav-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.25" y="4.25" width="15.5" height="15.5" rx="4.25" />
      <path d="M12 7.75v8.5" />
      <path d="M7.75 12h8.5" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 4v16" />
      <path d="m4 8 4-4 4 4" />
      <path d="M16 20V4" />
      <path d="m12 16 4 4 4-4" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m8 5 11 7-11 7V5Z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 20 6v6c0 5-3.4 8.2-8 9-4.6-.8-8-4-8-9V6l8-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="8" cy="9" r="3" />
      <circle cx="16" cy="9" r="3" />
      <path d="M2.5 20a6 6 0 0 1 11 0" />
      <path d="M10.5 20a6 6 0 0 1 11 0" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="10" cy="8" r="4" />
      <path d="M3 21a7 7 0 0 1 14 0" />
      <path d="M19 8v6" />
      <path d="M16 11h6" />
    </svg>
  );
}

function BadgeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 14.1 6.3 18 5.6l-.7 3.9L21 11l-3.7 1.5.7 3.9-3.9-.7L12 21l-2.1-5.3-3.9.7.7-3.9L3 11l3.7-1.5L6 5.6l3.9.7L12 3Z" />
      <path d="m9.5 12 1.6 1.6 3.4-3.4" />
    </svg>
  );
}

function ScaleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="m6 7-3 6h6L6 7Z" />
      <path d="m18 7-3 6h6l-3-6Z" />
    </svg>
  );
}

function RulerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20 20 4" />
      <path d="m7 17 2 2" />
      <path d="m10 14 2 2" />
      <path d="m13 11 2 2" />
      <path d="m16 8 2 2" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 8h4l2-3h4l2 3h4v11H4V8Z" />
      <circle cx="12" cy="13.5" r="3.2" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M4 10h16" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 12H5" />
      <path d="m12 5-7 7 7 7" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20h4l11-11-4-4L4 16v4Z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}

function WaterIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 8c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
      <path d="M3 13c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
      <path d="M3 18c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1" />
    </svg>
  );
}
